import React, { useState, useMemo, useCallback, useEffect, useRef, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import LZString from "lz-string";
import {
  FaPlay,
  FaPlus,
  FaListUl,
  FaUpload,
  FaDownload,
  FaTimes,
  FaGripVertical,
  FaFileImport,
  FaFastBackward,
  FaFastForward,
  FaStepBackward,
  FaStepForward,
  FaPause,
  FaQuestionCircle,
  FaFileWord,
  FaFilePdf,
  FaMagic,
  FaFileAlt,
  FaTimesCircle,
  FaYoutube,
  FaBook,
  FaImages,
  FaExternalLinkAlt,
  FaGlobe,
  FaWindowMinimize,
  FaWindowRestore,
  FaWindowMaximize,
  FaEdit,
  FaMusic,
  FaVolumeMute,
  FaVolumeUp,
  FaExpand,
  FaCompress,
  FaChevronUp,
  FaChevronDown,
  FaArrowUp,
  FaArrowDown,
  FaTable,
  FaCopy,
  FaClock,
} from "react-icons/fa";
import SEO from "./components/SEO";
import "./Session.css";
import { BIBLE_BOOKS } from "./utils/bibleBooks";
import SessionPresentationContext from "./contexts/SessionPresentationContext";
import PresentationLinkGenerator from "./components/PresentationLinkGenerator";
import useTouchNavigation from "./utils/useTouchNavigation";
import backgroundMusicPlaylists from "./assets/livestreaming/music.json";
import livestreamingPlaylists from "./assets/livestreaming/playlists.json";
import videoMetadata from "./assets/livestreaming/videos.json";
import youtubePlaylistItems from "./assets/livestreaming/playlistItems.json";

import websitesData from "./assets/session/websites.json";

const ORGANIZATIONS_FEED_DOMAIN = "gcc-organizations.blogspot.com";
const DEFAULT_TEMPLATE_CATALOG_DOMAIN = "gcc-catalog.blogspot.com";
const BLOG_FEED_PROXY_PREFIXES = ["https://r.jina.ai/https://", "https://r.jina.ai/http://"];

const DOMAIN_EVENT = "gcc-domain-change";
const GLOBAL_WATERMARK_TEXT = "GlobalChristianCommunity.com";

const SESSION_SHORTCUTS = [
  { keys: "Space", description: "Play/pause auto-advance" },
  { keys: "Arrow Right / Arrow Left", description: "Advance within the current item" },
  { keys: "Arrow Down / Arrow Up", description: "Jump between session items" },
  { keys: "Option (⌥) or O", description: "Show/hide the session console" },
  { keys: "Control (⌃)", description: "Show/hide the navigation controls" },
  { keys: "T", description: "Toggle slide timer" },
  { keys: "?", description: "Show/hide this shortcuts guide" },
];

const SLIDE_TIMER_PARAM_KEY = "timer";
const SLIDE_TIMER_PARAM_TRUE_VALUES = new Set(["1", "true", "on", "yes"]);
const isSlideTimerParamEnabled = (params) => {
  if (!params) return false;
  const value = params.get(SLIDE_TIMER_PARAM_KEY);
  if (value === null) return false;
  const normalized = value.trim().toLowerCase();
  if (!normalized) return true;
  return SLIDE_TIMER_PARAM_TRUE_VALUES.has(normalized);
};

const SLIDE_TIMER_COLORS = ["#34d399", "#facc15", "#fb7185", "#60a5fa", "#a855f7", "#f97316"];
const formatSlideTimerDisplay = (rawSeconds) => {
  const seconds = Math.max(0, Math.floor(rawSeconds));
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${remaining.toString().padStart(2, "0")}`;
};

const ensureBlogspotDomain = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    return DEFAULT_TEMPLATE_CATALOG_DOMAIN;
  }
  const sanitized = trimmed.replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
  if (/\.blogspot\.com$/i.test(sanitized)) {
    return sanitized.toLowerCase();
  }
  const slug = sanitized
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) {
    return DEFAULT_TEMPLATE_CATALOG_DOMAIN;
  }
  return `${slug}.blogspot.com`;
};

const toShortDomainParam = (value) =>
  ensureBlogspotDomain(value).replace(/\.blogspot\.com$/i, "");

const broadcastDomainSelection = (value) => {
  if (typeof window === "undefined") return;
  const shortValue = toShortDomainParam(value);
  window.__GCC_DOMAIN__ = shortValue;
  window.dispatchEvent(new CustomEvent(DOMAIN_EVENT, { detail: shortValue }));
};

const formatDomainLabel = (domain) => {
  const trimmed = (domain || "").trim();
  if (!trimmed) return "Custom Catalog";
  return trimmed.replace(/\.blogspot\.com$/i, "") || trimmed;
};

const prettifyTitle = (value) => {
  const cleaned = (value || "")
    .toString()
    .replace(/[-_/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const templateContext = require.context("./assets/session/templates", false, /\.json$/);

const buildTemplateDisplayName = (filePath, templateData) => {
  const title = (templateData?.title || "").trim();
  if (title) return title;
  const baseName = filePath.replace("./", "").replace(/\.json$/, "");
  return prettifyTitle(baseName) || baseName || "Untitled Template";
};

const LOCAL_TEMPLATE_ENTRIES = templateContext.keys().map((filePath) => {
  const templateData = templateContext(filePath);
  return {
    name: buildTemplateDisplayName(filePath, templateData),
    data: templateData,
  };
});

const createTemplateDictionary = (entries) => {
  const sorted = [...entries].sort((a, b) => a.name.localeCompare(b.name));
  const nameCounts = {};
  return sorted.reduce((acc, { name, data }) => {
    if (nameCounts[name]) {
      nameCounts[name] += 1;
      acc[`${name} (${nameCounts[name]})`] = data;
    } else {
      nameCounts[name] = 1;
      acc[name] = data;
    }
    return acc;
  }, {});
};

const slugify = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const buildSlugLookup = (templateMap) =>
  Object.keys(templateMap).reduce((map, name) => {
    map[slugify(name)] = name;
    return map;
  }, {});

const parseBlogFeedPayload = (text) => {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    const firstBrace = text.indexOf("{");
    const lastBrace = text.lastIndexOf("}");
    if (firstBrace === -1 || lastBrace === -1 || lastBrace <= firstBrace) {
      return null;
    }
    try {
      return JSON.parse(text.slice(firstBrace, lastBrace + 1));
    } catch (innerError) {
      return null;
    }
  }
};

const extractTextContentFromBlogEntry = (rawContent) => {
  if (!rawContent) return "";
  let textContent = rawContent;
  try {
    if (typeof window !== "undefined" && typeof window.DOMParser === "function") {
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(rawContent, "text/html");
      textContent = doc.body.textContent || rawContent;
    }
  } catch (error) {
    textContent = rawContent;
  }
  if (!textContent && typeof rawContent === "string") {
    textContent = rawContent.replace(/<br\s*\/?>/gi, "\n").replace(/<[^>]+>/g, " ");
  }
  return (textContent || "").replace(/\u00a0/g, " ");
};

const extractJsonFromBlogEntry = (rawContent) => {
  const trimmed = (extractTextContentFromBlogEntry(rawContent) || "").trim();
  const startIndex = trimmed.indexOf("{");
  const endIndex = trimmed.lastIndexOf("}");
  if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
    return null;
  }
  const candidate = trimmed.slice(startIndex, endIndex + 1);
  try {
    return JSON.parse(candidate);
  } catch (error) {
    return null;
  }
};

const extractUrlsFromBlogEntry = (rawContent) => {
  const rawText = extractTextContentFromBlogEntry(rawContent);
  if (!rawText) return [];
  const sanitized = rawText
    .replace(/[“”"']+/g, " ")
    .replace(/[<>]/g, " ")
    .replace(/[,;\t]+/g, " ");
  const urlCandidates = sanitized
    .split(/[\s\n\r]+/g)
    .map((candidate) => candidate.trim())
    .filter(Boolean);
  const urlPattern = /^(https?:\/\/|www\.|[a-z0-9.-]+\.[a-z]{2,})(\/\S*)?$/i;
  const seen = new Set();
  return urlCandidates.filter((candidate) => {
    const cleaned = candidate.replace(/[.,;:!?]+$/, "");
    if (!urlPattern.test(cleaned)) return false;
    const key = cleaned.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const PANEL_KEYS = ["catalog", "session", "preview"];

const PANEL_MODE_DEFAULTS = {
  preview: "normal",
  session: "normal",
  catalog: "normal",
};

const PANEL_LABELS = {
  preview: "Live Preview",
  session: "Session Outline",
  catalog: "Content Library",
};

const EDIT_MODAL_TABS = [
  { key: "current", label: "Live JSON", description: "Live view of the current session configuration." },
  { key: "json", label: "Paste JSON", description: "Paste or edit JSON and apply it to the session." },
  { key: "urls", label: "Paste URLs", description: "Paste a list of URLs to auto-build session items." },
  { key: "table", label: "Table", description: "Edit session titles and URLs in a spreadsheet-style view." },
];

const WORSHIP_EMBED_MEDIA_MESSAGE = "gcc-worship-media";
const WORSHIP_EMBED_CONTROL_MESSAGE = "gcc-worship-control";
const SESSION_NAVIGATION_MESSAGE = "gcc-session-navigation";
const SESSION_NAVIGATION_RESPONSE = "gcc-session-navigation-response";
const SESSION_FOCUS_MESSAGE = "gcc-session-focus";
const REMOTE_NAVIGATION_TYPES = new Set([
  "worshipRemote",
  "bibleRemote",
  "presentationRemote",
]);

function TemplateSelector({ templateNames, onSelect }) {
  return (
    <div className="template-selector">
      <select
        id="template-select"
        onChange={(e) => onSelect(e.target.value)}
        defaultValue=""
      >
        <option value="" disabled>
          Load from Template
        </option>
        {templateNames.map((templateName) => (
          <option key={templateName} value={templateName}>
            {templateName}
          </option>
        ))}
      </select>
    </div>
  );
}

function CatalogSelector({ options, value, onChange }) {
  return (
    <div className="template-selector">
      <select
        id="catalog-select"
        onChange={(event) => onChange(event.target.value)}
        value={value}
        aria-label="Template catalog"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}



const DEFAULT_WORSHIP_DOMAIN = "gcc-catalog.blogspot.com";
const REMOTE_FETCH_TIMEOUT_MS = 8000;

const getRootOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  if (typeof document !== "undefined" && document.location?.origin) {
    return document.location.origin;
  }
  return "";
};

const buildUrlFromRoot = (path) => {
  const origin = getRootOrigin();
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (!origin) {
    return normalizedPath;
  }
  try {
    return new URL(normalizedPath, origin).toString();
  } catch (error) {
    return `${origin}${normalizedPath}`;
  }
};

const getBibleCatalogUrl = () =>
  buildUrlFromRoot(
    "/bibleis?book=0&chapter=1&version=KJV&language=en&page=0&lines=3&wpm=170",
  );

const getPresentationCatalogUrl = () => buildUrlFromRoot("/present");

const buildWorshipEmbedUrl = (slug, domain = DEFAULT_WORSHIP_DOMAIN) => {
  const normalizedDomain = ensureBlogspotDomain(domain);
  const params = new URLSearchParams({
    domain: normalizedDomain,
    display: "slide",
    theme: "classic",
    songViewMode: "buttons",
    lockSong: "1",
  });
  if (slug) params.set("song", slug);
  return `/worship?${params.toString()}`;
};

const normalizeWebsiteUrl = (value) => {
  const input = (value || "").trim();
  if (!input) return "";
  if (/^https?:\/\//i.test(input)) {
    return input;
  }
  return `https://${input}`;
};

const buildWebsiteEmbedUrl = (value) => {
  const normalized = normalizeWebsiteUrl(value);
  if (!normalized) return "";
  try {
    const parsed = new URL(normalized);
    parsed.searchParams.set("utm_source", "gcc-session");
    parsed.searchParams.set("utm_medium", "content-library");
    parsed.searchParams.set("utm_campaign", "websites");
    return parsed.toString();
  } catch (error) {
    return normalized;
  }
};

const FALLBACK_BACKGROUND_PLAYLIST_ID = "PLlfpe0DDwzp1IweiwK-PEuoAZxseiYklW";
const BACKGROUND_PLAYLISTS = Array.isArray(backgroundMusicPlaylists)
  ? backgroundMusicPlaylists
      .filter((entry) => entry && typeof entry.id === "string" && entry.id.trim())
      .map((entry) => ({ ...entry, id: entry.id.trim() }))
  : [];

const LIVESTREAM_PLAYLISTS = Array.isArray(livestreamingPlaylists)
  ? livestreamingPlaylists
      .filter((entry) => entry && typeof entry.id === "string" && entry.id.trim())
      .map((entry) => ({
        ...entry,
        id: entry.id.trim(),
        videoIds: Array.isArray(entry.videoIds) ? entry.videoIds : [],
      }))
  : [];

const LIVESTREAM_PLAYLIST_MAP = new Map(
  LIVESTREAM_PLAYLISTS.map((entry) => [entry.id, entry]),
);

const VIDEO_METADATA = videoMetadata && typeof videoMetadata === "object" ? videoMetadata : {};
const YOUTUBE_PLAYLIST_ITEMS =
  youtubePlaylistItems && typeof youtubePlaylistItems === "object"
    ? youtubePlaylistItems
    : {};

const getBestThumbnailUrl = (thumbnails) => {
  if (!thumbnails) return "";
  const order = ["maxres", "standard", "high", "medium", "default"];
  if (Array.isArray(thumbnails)) {
    for (const entry of thumbnails) {
      if (entry && typeof entry.url === "string" && entry.url.trim()) {
        return entry.url.trim();
      }
    }
    return "";
  }
  if (typeof thumbnails === "object") {
    for (const key of order) {
      const value = thumbnails[key];
      if (value && typeof value.url === "string" && value.url.trim()) {
        return value.url.trim();
      }
    }
    for (const value of Object.values(thumbnails)) {
      if (value && typeof value.url === "string" && value.url.trim()) {
        return value.url.trim();
      }
    }
  }
  return "";
};

const getVideoMetadataTitle = (videoId) => {
  const entry = VIDEO_METADATA?.[videoId];
  if (!entry) return "";
  if (typeof entry === "string") return entry.trim();
  if (typeof entry === "object" && entry !== null) {
    const value = entry.title;
    if (typeof value === "string") return value.trim();
  }
  return "";
};

const getVideoMetadataDescription = (videoId) => {
  const entry = VIDEO_METADATA?.[videoId];
  if (entry && typeof entry === "object" && typeof entry.description === "string") {
    return entry.description.trim();
  }
  return "";
};

const getVideoMetadataThumbnail = (videoId) => {
  const entry = VIDEO_METADATA?.[videoId];
  if (entry && typeof entry === "object") {
    return getBestThumbnailUrl(entry.thumbnails);
  }
  return "";
};

const getVideoMetadataPublishedAt = (videoId) => {
  const entry = VIDEO_METADATA?.[videoId];
  if (entry && typeof entry === "object" && typeof entry.publishedAt === "string") {
    return entry.publishedAt;
  }
  return "";
};

const DEFAULT_BACKGROUND_PLAYLIST_ID =
  BACKGROUND_PLAYLISTS[0]?.id?.trim() || FALLBACK_BACKGROUND_PLAYLIST_ID;

const BACKGROUND_PLAYLIST_IDS = new Set(
  BACKGROUND_PLAYLISTS.map((entry) => (entry.id || "").trim()).filter(Boolean),
);

const normalizePlaylistOptions = (playlists) => {
  const map = new Map();
  (playlists || []).forEach((playlist) => {
    const id = (playlist?.id || "").trim();
    if (!id) return;
    if (BACKGROUND_PLAYLIST_IDS.has(id)) return;
    const title = (playlist?.title || "").trim() || prettifyTitle(id) || id;
    if (!map.has(id)) {
      map.set(id, title);
    }
  });
  return Array.from(map.entries())
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title));
};

const sortYoutubeCatalogItems = (items) => {
  return [...(items || [])].sort((a, b) => {
    const aTime = Date.parse(a?.publishedAt || "");
    const bTime = Date.parse(b?.publishedAt || "");
    const aValue = Number.isFinite(aTime) ? aTime : 0;
    const bValue = Number.isFinite(bTime) ? bTime : 0;
    if (bValue !== aValue) {
      return bValue - aValue;
    }
    return (a?.title || "").localeCompare(b?.title || "");
  });
};

const renderMultilineText = (text) => {
  if (!text) return null;
  const lines = String(text).split(/\r?\n/);
  return lines.map((line, index) => (
    <React.Fragment key={`note-line-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapePdfText = (value = "") =>
  String(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const formatMinutes = (minutes) => {
  const safeMinutes = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safeMinutes / 60)
    .toString()
    .padStart(2, "0");
  const mins = (safeMinutes % 60).toString().padStart(2, "0");
  return `${hours}:${mins}`;
};

const buildSimplePdf = (title, lines) => {
  const header = escapePdfText(title || "Session Outline");
  const contentLines = Array.isArray(lines) ? lines : [];
  const textOps = [
    "BT",
    "/F1 20 Tf",
    "1 0 0 1 72 756 Tm",
    "24 TL",
    `(${header}) Tj`,
    "T*",
    "/F1 12 Tf",
    "16 TL",
  ];
  contentLines.forEach((line) => {
    textOps.push(`(${escapePdfText(line)}) Tj`);
    textOps.push("T*");
  });
  textOps.push("ET");

  const contentStream = textOps.join("\n");
  const objects = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >> endobj",
    `4 0 obj << /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream\nendobj`,
    "5 0 obj << /Type /Font /Subtype /Type1 /Name /F1 /BaseFont /Helvetica >> endobj",
  ];

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((obj) => {
    offsets.push(pdf.length);
    pdf += `${obj}\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  for (let i = 1; i <= objects.length; i += 1) {
    pdf += `${offsets[i].toString().padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
};

const isPlaylistEntryPrivate = (entry) => {
  if (!entry) return false;
  const title = (entry.title || "").trim().toLowerCase();
  if (title === "private video") {
    return true;
  }
  const description = (entry.description || "").trim().toLowerCase();
  if (description === "this video is private." || description === "this video is private") {
    return true;
  }
  const privacyStatus =
    (typeof entry.privacyStatus === "string" && entry.privacyStatus) ||
    (typeof entry.status?.privacyStatus === "string" && entry.status.privacyStatus) ||
    "";
  if (privacyStatus.toLowerCase() === "private") {
    return true;
  }
  return false;
};

const isVideoMetadataPrivate = (metadata) => {
  if (!metadata || typeof metadata !== "object") return false;
  const title = (metadata.title || "").trim().toLowerCase();
  if (title === "private video") return true;
  const description = (metadata.description || "").trim().toLowerCase();
  if (description === "this video is private." || description === "this video is private") return true;
  const privacyStatus = (metadata.privacyStatus || metadata.status?.privacyStatus || "").toLowerCase();
  return privacyStatus === "private";
};

const getConfiguredPlaylistTitle = (playlistId) => {
  const trimmed = (playlistId || "").trim();
  if (!trimmed) return "";
  const match = BACKGROUND_PLAYLISTS.find(
    (entry) => entry.id?.trim() === trimmed,
  );
  if (match?.title) return match.title;
  const local = LIVESTREAM_PLAYLIST_MAP.get(trimmed);
  return local?.title || "";
};

const buildLocalPlaylistItems = (playlistId) => {
  const trimmedId = (playlistId || "").trim();
  if (!trimmedId) return [];
  const playlist = LIVESTREAM_PLAYLIST_MAP.get(trimmedId);
  if (!playlist) return [];
  const seen = new Set();
  const videoTitles = Array.isArray(playlist.videoTitles) ? playlist.videoTitles : [];
  return playlist.videoIds
    .map((videoId, index) => {
      const id = (videoId || "").trim();
      const titleFromPlaylist = (videoTitles[index] || "").trim();
      const metadataTitle = getVideoMetadataTitle(id);
      const fallbackTitle = playlist.title
        ? `${playlist.title} • Track ${index + 1}`
        : `Track ${index + 1}`;
      return { id, title: titleFromPlaylist || metadataTitle || fallbackTitle };
    })
    .filter(({ id }) => {
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map(({ id, title }) => ({ videoId: id, title }));
};

const buildYoutubeItemsFromConfig = () => {
  const playlists = [];
  const videoMap = new Map();

  Object.entries(YOUTUBE_PLAYLIST_ITEMS).forEach(([playlistId, entries]) => {
    const trimmedPlaylistId = (playlistId || "").trim();
    if (!trimmedPlaylistId || !Array.isArray(entries)) return;
    if (BACKGROUND_PLAYLIST_IDS.has(trimmedPlaylistId)) return;

    const playlistInfo = LIVESTREAM_PLAYLIST_MAP.get(trimmedPlaylistId);
    const playlistTitle =
      playlistInfo?.title ||
      prettifyTitle(trimmedPlaylistId) ||
      trimmedPlaylistId;
    let playlistHasVideo = false;

    entries.forEach((entry) => {
      const videoId = (entry?.id || "").trim();
      if (!videoId) return;
      if (isPlaylistEntryPrivate(entry)) return;

      const existing = videoMap.get(videoId);
      const titleFromEntry = (entry?.title || "").trim();
      const videoTitle =
        titleFromEntry || getVideoMetadataTitle(videoId) || "YouTube Video";
      const descriptionFromEntry = (entry?.description || "").trim();
      const metadataDescription = getVideoMetadataDescription(videoId);
      const description =
        descriptionFromEntry ||
        metadataDescription ||
        playlistInfo?.description?.trim() ||
        "YouTube video from GCC Worship.";
      const thumbnail =
        getBestThumbnailUrl(entry?.thumbnails) ||
        getVideoMetadataThumbnail(videoId);
      const publishedAt = entry?.publishedAt || getVideoMetadataPublishedAt(videoId) || "";

      if (existing) {
        if (trimmedPlaylistId && !existing.playlistIds.includes(trimmedPlaylistId)) {
          existing.playlistIds = [...existing.playlistIds, trimmedPlaylistId];
        }
        if (playlistTitle && !existing.playlistTitles.includes(playlistTitle)) {
          existing.playlistTitles = [...existing.playlistTitles, playlistTitle];
        }
        if (!existing.title && videoTitle) {
          existing.title = videoTitle;
        }
        if (!existing.description && description) {
          existing.description = description;
        }
        if (!existing.thumbnail && thumbnail) {
          existing.thumbnail = thumbnail;
        }
        if (!existing.publishedAt && publishedAt) {
          existing.publishedAt = publishedAt;
        }
        return;
      }

      videoMap.set(videoId, {
        sourceId: `youtube-song-${videoId}`,
        type: "youtubeVideo",
        title: videoTitle,
        description,
        embedUrl: videoId ? buildYoutubeAutoplayUrl(videoId) : null,
        originalUrl: videoId ? `${YOUTUBE_WATCH_BASE}${videoId}` : "",
        thumbnail,
        videoId,
        playlistId: trimmedPlaylistId,
        playlistIds: trimmedPlaylistId ? [trimmedPlaylistId] : [],
        playlistTitle,
        playlistTitles: playlistTitle ? [playlistTitle] : [],
        publishedAt,
      });
      playlistHasVideo = true;
    });

    if (playlistHasVideo) {
      playlists.push({ id: trimmedPlaylistId, title: playlistTitle });
    }
  });

  const items = Array.from(videoMap.values());
  return { items, playlists };
};

const buildYoutubeItemsFromVideosMetadata = () => {
  const items = Object.entries(VIDEO_METADATA || {}).reduce((accumulator, [videoId, entry]) => {
    const id = (videoId || "").trim();
    if (!id) return accumulator;
    if (isVideoMetadataPrivate(entry)) return accumulator;

    const title = getVideoMetadataTitle(id) || "YouTube Video";
    const description =
      getVideoMetadataDescription(id) || "YouTube video from GCC Worship.";
    const thumbnail = getVideoMetadataThumbnail(id);
    const publishedAt = getVideoMetadataPublishedAt(id) || "";

    accumulator.push({
      sourceId: `youtube-song-${id}`,
      type: "youtubeVideo",
      title,
      description,
      embedUrl: id ? buildYoutubeAutoplayUrl(id) : null,
      originalUrl: id ? `${YOUTUBE_WATCH_BASE}${id}` : "",
      thumbnail,
      videoId: id,
      playlistId: "",
      playlistIds: [],
      playlistTitle: "",
      playlistTitles: [],
      publishedAt,
    });

    return accumulator;
  }, []);

  return { items, playlists: [] };
};

const buildBackgroundPlaylistFeedUrl = (playlistId) => {
  const trimmed = (playlistId || "").trim();
  if (!trimmed) return "";
  return `https://r.jina.ai/https://www.youtube.com/feeds/videos.xml?playlist_id=${trimmed}`;
};

const YOUTUBE_WATCH_BASE = "https://www.youtube.com/watch?v=";

const buildYoutubeAutoplayUrl = (videoId, extraParams = {}) => {
  const trimmed = (videoId || "").trim();
  if (!trimmed) return "";
  const params = new URLSearchParams({
    autoplay: "1",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
    enablejsapi: "1",
    mute: "0",
    ...extraParams,
  });
  if (!params.has("controls")) {
    params.set("controls", "1");
  }
  const origin = getRootOrigin();
  if (origin) {
    params.set("origin", origin);
  }
  return `https://www.youtube.com/embed/${trimmed}?${params.toString()}`;
};

const buildYoutubeEmbedUrl = (videoId) =>
  buildYoutubeAutoplayUrl(videoId, {
    controls: "0",
    showinfo: "0",
    loop: "1",
    playlist: (videoId || "").trim(),
  });

const parseYoutubeVideoId = (value) => {
  const input = (value || "").trim();
  if (!input) return "";
  if (/^[a-zA-Z0-9_-]{11}$/.test(input)) {
    return input;
  }
  try {
    const parsed = new URL(input);
    const host = parsed.hostname.toLowerCase();
    if (host.includes("youtu.be")) {
      return parsed.pathname.split("/").filter(Boolean).pop() || "";
    }
    if (host.includes("youtube.com")) {
      const queryId =
        parsed.searchParams.get("v") ||
        parsed.searchParams.get("vi") ||
        parsed.searchParams.get("video_id");
      if (queryId) return queryId;
      const pathParts = parsed.pathname.split("/").filter(Boolean);
      if (pathParts.length >= 2 && pathParts[0] === "embed") {
        return pathParts[1];
      }
      if (pathParts.length >= 2 && pathParts[0] === "shorts") {
        return pathParts[1];
      }
    }
  } catch (error) {
    return "";
  }
  return "";
};

const createBackgroundMusicFromVideoId = (videoId, options = {}) => {
  const id = (videoId || "").trim();
  if (!id) return null;
  const { mode, title, originalUrl, ...rest } = options;
  return {
    mode: mode || "playlist",
    videoId: id,
    title: title || "",
    embedUrl: buildYoutubeEmbedUrl(id),
    originalUrl: originalUrl || `${YOUTUBE_WATCH_BASE}${id}`,
    ...rest,
  };
};

const createBackgroundMusicFromUrl = (url, options = {}) => {
  const trimmed = (url || "").trim();
  if (!trimmed) {
    throw new Error("Paste a YouTube link for the background music.");
  }
  const videoId = parseYoutubeVideoId(trimmed);
  if (!videoId) {
    throw new Error("Enter a valid YouTube link for the background music.");
  }
  const next = createBackgroundMusicFromVideoId(videoId, {
    ...options,
    mode: "custom",
  });
  if (next) {
    next.originalUrl = trimmed;
  }
  return next;
};

const resolveBackgroundMusicSelection = (selection, playlistItems) => {
  if (!selection || selection.mode === "none") {
    return null;
  }
  if (selection.mode === "playlist") {
    const fallback = playlistItems?.[0]?.videoId || "";
    const videoId = selection.playlistVideoId || fallback;
    if (!videoId) {
      throw new Error("Select a background track from the playlist.");
    }
    const match =
      Array.isArray(playlistItems) &&
      playlistItems.find((entry) => entry.videoId === videoId);
    const playlistTitle =
      selection.playlistTitle || getConfiguredPlaylistTitle(selection.playlistId);
    return createBackgroundMusicFromVideoId(videoId, {
      mode: "playlist",
      title: match?.title || selection.playlistLabel || "",
      originalUrl: `${YOUTUBE_WATCH_BASE}${videoId}`,
      playlistId: selection.playlistId || "",
      playlistTitle,
    });
  }
  if (selection.mode === "custom") {
    const music = createBackgroundMusicFromUrl(selection.customUrl, {
      mode: "custom",
    });
    if (music && selection.customLabel) {
      music.title = selection.customLabel;
    }
    return music;
  }
  return null;
};

const parsePlaylistFeed = (xmlString) => {
  if (typeof DOMParser === "undefined") return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "application/xml");
    const entries = Array.from(doc.getElementsByTagName("entry"));
    return entries
      .map((entry) => {
        const title = entry.getElementsByTagName("title")[0]?.textContent?.trim() || "";
        const idNode =
          entry.getElementsByTagName("yt:videoId")[0] ||
          entry.getElementsByTagName("videoId")[0];
        const videoId = idNode?.textContent?.trim() || "";
        if (!videoId) return null;
        return { videoId, title };
      })
      .filter(Boolean);
  } catch (error) {
    return [];
  }
};

const getBackgroundMusicEmbedUrl = (music) => {
  if (!music) return "";
  const ensureEmbedParams = (value, videoId) => {
    try {
      const url = new URL(value, "https://www.youtube.com");
      url.searchParams.set("autoplay", "1");
      url.searchParams.set("playsinline", "1");
      url.searchParams.set("mute", "0");
      if (!url.searchParams.has("controls")) {
        url.searchParams.set("controls", "0");
      }
      url.searchParams.set("enablejsapi", "1");
      const origin = getRootOrigin();
      if (origin) {
        url.searchParams.set("origin", origin);
      }
      return url.toString();
    } catch (error) {
      return value;
    }
  };
  if (music.embedUrl) {
    return ensureEmbedParams(music.embedUrl, music.videoId);
  }
  if (music.videoId) {
    return buildYoutubeEmbedUrl(music.videoId);
  }
  if (music.originalUrl) {
    try {
      const resolved = createBackgroundMusicFromUrl(music.originalUrl, {
        mode: music.mode || "custom",
      });
      return ensureEmbedParams(resolved.embedUrl, resolved.videoId);
    } catch (error) {
      return "";
    }
  }
  return "";
};

const describeBackgroundMusic = (music) => {
  if (!music) return "";
  if (music.title) return music.title;
  if (music.originalUrl) return music.originalUrl;
  if (music.videoId) return `YouTube video ${music.videoId}`;
  return "Background track";
};

const parseWebsiteEditorValues = (url) => {
  const raw = (url || "").trim();
  if (!raw) return { baseUrl: "", params: "" };
  const tryParse = (value) => {
    try {
      return new URL(value);
    } catch (error) {
      return null;
    }
  };
  const parsed =
    tryParse(raw) ||
    tryParse(
      /^https?:\/\//i.test(raw)
        ? raw
        : `https://${raw.replace(/^\/+/, "")}`
    );
  if (!parsed) {
    return { baseUrl: raw, params: "" };
  }
  const params = parsed.search ? parsed.search.replace(/^\?+/, "") : "";
  return {
    baseUrl: `${parsed.origin}${parsed.pathname}`,
    params,
  };
};

const buildWebsiteUrlFromParts = (baseUrl, params) => {
  const trimmedBase = (baseUrl || "").trim();
  if (!trimmedBase) return "";
  const normalizedBase = normalizeWebsiteUrl(trimmedBase);
  const trimmedParams = (params || "").trim();
  if (!trimmedParams) {
    return normalizedBase;
  }
  const sanitized = trimmedParams.replace(/^\?+/, "");
  return `${normalizedBase}?${sanitized}`;
};

const DEFAULT_PRESENTATION_REMOTE_SETTINGS = {
  mode: "play",
  tiles: "0",
  controls: "0",
  descriptions: "1",
  gifDelay: "2000",
  iconSize: "34",
  master: "#bd5151",
  subView: "card",
  slideRange: "",
  disableLoop: true,
};

const normalizeHexColor = (value, fallback) => {
  const input = (value || "").trim();
  if (!input) return fallback;
  if (input.startsWith("#")) return input;
  return `#${input.replace(/^#+/, "")}`;
};

const parsePresentationRemoteSettings = (urlValue, fallbackBase) => {
  const defaults = {
    ...DEFAULT_PRESENTATION_REMOTE_SETTINGS,
    file: "",
    presentation: "",
    baseUrl: getPresentationCatalogUrl(),
    slideRange: "",
  };
  if (!urlValue && !fallbackBase) return defaults;
  try {
    const base =
      fallbackBase ||
      getRootOrigin() ||
      "https://globalchristiancommunity.com/present";
    const parsedUrl = new URL(urlValue || base, base);
    const params = parsedUrl.searchParams;
    const tilesParam = params.get("tiles");
    const controlsParam = params.get("controls");
    const descriptionsParam = params.get("descriptions");
    const modeParam = params.get("mode");
    const gifDelayParam = params.get("gifDelay") || params.get("gifdelay");
    const iconSizeParam = params.get("iconSize") || params.get("iconsize");
    const masterParam = params.get("master");
    const subViewParam = params.get("subView") || params.get("subview");
    const slideRangeParam =
      params.get("slides") || params.get("slide") || "";

    return {
      mode: modeParam || defaults.mode,
      tiles: tilesParam ?? defaults.tiles,
      controls: controlsParam ?? defaults.controls,
      descriptions: descriptionsParam ?? defaults.descriptions,
      gifDelay: gifDelayParam || defaults.gifDelay,
      iconSize: iconSizeParam || defaults.iconSize,
      master: normalizeHexColor(masterParam, defaults.master),
      subView: subViewParam || defaults.subView,
      file: params.get("file") || "",
      presentation: params.get("presentation") || "",
      baseUrl: `${parsedUrl.origin}${parsedUrl.pathname}`,
      slideRange: slideRangeParam,
      disableLoop: parseBooleanParam(
        params.get("disableLoop") || params.get("loop"),
        defaults.disableLoop,
      ),
    };
  } catch (error) {
    console.error("Failed to parse presentation settings", error);
    return defaults;
  }
};

const buildPresentationRemoteUrl = (urlValue, fallbackBase, updates) => {
  const baseFallback =
    fallbackBase ||
    getRootOrigin() ||
    "https://globalchristiancommunity.com/present";
  let parsedUrl;
  try {
    parsedUrl = new URL(urlValue || baseFallback, baseFallback);
  } catch (error) {
    parsedUrl = new URL(baseFallback);
  }

  let disableLoopValue = null;

  Object.entries(updates || {}).forEach(([key, value]) => {
    let nextValue = value;
    if (typeof nextValue === "string") {
      nextValue = nextValue.trim();
    }
    if (key === "baseUrl" && typeof nextValue === "string" && nextValue) {
      try {
        parsedUrl = new URL(nextValue);
      } catch (error) {
        // ignore invalid baseUrl updates
      }
      return;
    }
    if (key === "disableLoop") {
      disableLoopValue = parseBooleanParam(nextValue, true);
      return;
    }
    const paramKey = key === "slideRange" ? "slides" : key;
    if (nextValue === undefined || nextValue === null || nextValue === "") {
      parsedUrl.searchParams.delete(paramKey);
    } else {
      parsedUrl.searchParams.set(paramKey, nextValue);
    }
  });

  if (disableLoopValue === null) {
    disableLoopValue = parsedUrl.searchParams.has("disableLoop")
      ? parsedUrl.searchParams.get("disableLoop") !== "0"
      : DEFAULT_PRESENTATION_REMOTE_SETTINGS.disableLoop;
  }

  if (disableLoopValue) {
    parsedUrl.searchParams.set("disableLoop", "1");
  } else {
    parsedUrl.searchParams.delete("disableLoop");
  }

  return parsedUrl.toString();
};

const splitPresentationFile = (fileValue) => {
  if (!fileValue) {
    return { topic: "", file: "" };
  }
  const normalized = fileValue.replace(/^[\\/]+/, "").replace(/[\\/]+/g, "/");
  const segments = normalized.split("/");
  if (segments.length <= 1) {
    return { topic: "", file: normalized };
  }
  const [topic, ...rest] = segments;
  return { topic, file: rest.join("/") };
};

const buildPresentationFile = (topic, file) => {
  const trimmedTopic = (topic || "").trim();
  const trimmedFile = (file || "").trim();
  if (trimmedTopic && trimmedFile) {
    return `${trimmedTopic}/${trimmedFile}`.replace(/[\\/]+/g, "/");
  }
  if (trimmedTopic) return trimmedTopic.replace(/[\\/]+/g, "/");
  return trimmedFile.replace(/[\\/]+/g, "/");
};

const parseBooleanParam = (value, fallback = false) => {
  if (value === null || value === undefined) return fallback;
  const normalized = String(value).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
};

const parseWorshipRemoteSettings = (url) => {
  const defaults = {
    domain: DEFAULT_WORSHIP_DOMAIN,
    display: "slide",
    theme: "classic",
    songViewMode: "buttons",
    slug: "",
    lockSong: true,
  };
  if (!url) return defaults;
  try {
    const parsed = new URL(url, getRootOrigin() || "https://globalchristiancommunity.com");
    return {
      domain: parsed.searchParams.get("domain") || defaults.domain,
      display: parsed.searchParams.get("display") || defaults.display,
      theme: parsed.searchParams.get("theme") || defaults.theme,
      songViewMode: parsed.searchParams.get("songViewMode") || defaults.songViewMode,
      slug: parsed.searchParams.get("song") || "",
      lockSong: parsed.searchParams.get("lockSong") !== "0",
    };
  } catch (error) {
    return defaults;
  }
};

const buildWorshipRemoteUrl = (settings = {}) => {
  const params = new URLSearchParams({
    domain: settings.domain || DEFAULT_WORSHIP_DOMAIN,
    display: settings.display || "slide",
    theme: settings.theme || "classic",
    songViewMode: settings.songViewMode || "buttons",
    lockSong: settings.lockSong === false ? "0" : "1",
  });
  if (settings.slug) {
    params.set("song", settings.slug);
  } else {
    params.delete("song");
  }
  return `/worship?${params.toString()}`;
};

const parseBibleRemoteSettings = (url) => {
  const defaults = {
    baseUrl: getBibleCatalogUrl(),
    book: DEFAULT_BIBLE_SELECTION.book,
    chapter: DEFAULT_BIBLE_SELECTION.chapter,
    verse: DEFAULT_BIBLE_SELECTION.verse,
    version: DEFAULT_BIBLE_SELECTION.version,
    language: DEFAULT_BIBLE_SELECTION.language,
    page: DEFAULT_BIBLE_SELECTION.page,
    lines: DEFAULT_BIBLE_SELECTION.lines,
    wpm: DEFAULT_BIBLE_SELECTION.wpm,
  };
  if (!url) return defaults;
  try {
    const parsed = new URL(url, getRootOrigin() || "https://globalchristiancommunity.com");
    const params = parsed.searchParams;
    return {
      baseUrl: `${parsed.origin}${parsed.pathname}`,
      book: params.get("book") ?? defaults.book,
      chapter: params.get("chapter") ?? defaults.chapter,
      verse: params.get("verse") ?? defaults.verse,
      version: params.get("version") ?? defaults.version,
      language: params.get("language") ?? defaults.language,
      page: params.get("page") ?? defaults.page,
      lines: params.get("lines") ?? defaults.lines,
      wpm: params.get("wpm") ?? defaults.wpm,
    };
  } catch (error) {
    return defaults;
  }
};

const buildBibleRemoteUrl = (settings = {}) => {
  const baseUrl = settings.baseUrl || getBibleCatalogUrl();
  const parsedBase = new URL(baseUrl, getRootOrigin() || "https://globalchristiancommunity.com");
  const setParam = (key, value, fallback) => {
    if (value === undefined || value === null || value === "") {
      parsedBase.searchParams.delete(key);
      return;
    }
    const stringValue = String(value);
    if (fallback !== undefined && stringValue === String(fallback)) {
      parsedBase.searchParams.delete(key);
      return;
    }
    parsedBase.searchParams.set(key, stringValue);
  };

  setParam("book", settings.book ?? DEFAULT_BIBLE_SELECTION.book);
  setParam("chapter", settings.chapter ?? DEFAULT_BIBLE_SELECTION.chapter);
  setParam("verse", settings.verse ?? DEFAULT_BIBLE_SELECTION.verse, "");
  setParam("version", settings.version ?? DEFAULT_BIBLE_SELECTION.version, DEFAULT_BIBLE_SELECTION.version);
  setParam("language", settings.language ?? DEFAULT_BIBLE_SELECTION.language, DEFAULT_BIBLE_SELECTION.language);
  setParam("page", settings.page ?? DEFAULT_BIBLE_SELECTION.page, DEFAULT_BIBLE_SELECTION.page);
  setParam("lines", settings.lines ?? DEFAULT_BIBLE_SELECTION.lines, DEFAULT_BIBLE_SELECTION.lines);
  setParam("wpm", settings.wpm ?? DEFAULT_BIBLE_SELECTION.wpm, DEFAULT_BIBLE_SELECTION.wpm);

  return parsedBase.toString();
};

const buildBibleReference = (bookValue, chapter, verse) => {
  const index = parseInt(bookValue, 10);
  const bookName = BIBLE_BOOKS[index]?.name || "Bible";
  const safeChapter = chapter || DEFAULT_BIBLE_SELECTION.chapter;
  if (verse) {
    return `${bookName} ${safeChapter}:${verse}`;
  }
  return `${bookName} ${safeChapter}`;
};

const extractPlainText = (html) => {
  if (!html) return "";
  if (typeof window !== "undefined" && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }
  if (typeof document !== "undefined") {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return (temp.textContent || "").replace(/\s+/g, " ").trim();
  }
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
};

const extractFirstImageSrc = (html) => {
  if (!html) return null;
  if (typeof window !== "undefined" && window.DOMParser) {
    const doc = new window.DOMParser().parseFromString(html, "text/html");
    const img = doc.querySelector("img[src]");
    return img ? img.getAttribute("src") : null;
  }
  if (typeof document !== "undefined") {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    const img = temp.querySelector("img[src]");
    return img ? img.getAttribute("src") : null;
  }
  return null;
};

const safeId = (prefix) => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
};

const sampleSlidesSources = [
  {
    sourceId: "sample-opening-praise",
    type: "slides",
    title: "Opening Praise",
    description: "Joyful opener deck with scripture anchor and song progression.",
    layout: "titleSubtitle",
    slideTheme: "dusk",
    slideAccent: "cobalt",
    slideGradient: "sunrise",
    slides: [
      "Opening Praise",
      "\"Enter His gates with thanksgiving and His courts with praise.\" — Psalm 100:4",
      "Song Flow",
      "Hosanna (Praise Is Rising)",
      "Here I Am To Worship",
      "Great Things",
    ],
    notes: "Pair with a warm sunrise photo or gradient background.",
  },
  {
    sourceId: "sample-reflective-worship",
    type: "slides",
    title: "Reflective Worship",
    description: "Quiet worship space with scripture anchor and prayerful songs.",
    layout: "titleSubtitle",
    slideTheme: "midnight",
    slideAccent: "gold",
    slideGradient: "ocean",
    slides: [
      "Reflective Worship",
      "\"Be still, and know that I am God.\" — Psalm 46:10",
      "Song Flow",
      "What A Beautiful Name",
      "The Heart Of Worship",
      "I Surrender",
    ],
    notes: "Keep lighting low and allow room for reflective prayer.",
  },
  {
    sourceId: "sample-scripture-reading",
    type: "slides",
    title: "Scripture Reading",
    description: "Anchor the gathering in Philippians 4:4–7 with a reflection prompt.",
    layout: "bullet",
    slideTheme: "sandstone",
    slideAccent: "gold",
    slideGradient: "sunrise",
    slides: [
      "Today's Reading",
      "Philippians 4:4–7",
      "\"Rejoice in the Lord always. I will say it again: Rejoice!\"",
      "Reflection: What does this Scripture invite you to focus on today?",
    ],
  },
  {
    sourceId: "sample-vision-welcome",
    type: "slides",
    title: "Vision & Welcome",
    description: "Warm welcome slide with flow, updates, and prayer focus.",
    layout: "twoColumn",
    slideTheme: "sandstone",
    slideAccent: "emerald",
    slideGradient: "ember",
    slides: [
      "Welcome to GCC Worship",
      "Hosts: share today's heartbeat",
      "Today's Flow: Praise -> Word -> Ministry",
      "Family News: Birthdays & celebrations",
      "Family News: Upcoming serve opportunities",
      "Prayer Focus: People and nations to bless",
      "Connect: Newcomer follow-up",
      "Serve: Highlight teams to join",
    ],
  },
  {
    sourceId: "sample-message-highlights",
    type: "slides",
    title: "Message Highlights",
    description: "Support the teaching with scripture, insights, and next steps.",
    layout: "grid",
    slideTheme: "dusk",
    slideAccent: "emerald",
    slideGradient: "vineyard",
    slides: [
      "Scripture Focus — Matthew 5:13–16",
      "Key Insight — We shine by serving.",
      "Application — Bring light this week.",
      "Closing Prayer — Lord, make us Your light.",
    ],
  },
  {
    sourceId: "sample-testimony-spotlight",
    type: "slides",
    title: "Testimony Spotlight",
    description: "Center a story of transformation to raise faith.",
    layout: "quote",
    slideTheme: "midnight",
    slideAccent: "gold",
    slideGradient: "sunrise",
    slides: [
      "\"When I prayed for peace, I found purpose instead — and that changed everything.\"",
      "Sarah K., GCC Member",
      "Testimony Highlight",
    ],
  },
  {
    sourceId: "sample-workshop-breakout",
    type: "slides",
    title: "Workshop Breakout",
    description: "Guide your breakout with clear steps and worship response.",
    layout: "timeline",
    slideTheme: "forest",
    slideAccent: "emerald",
    slideGradient: "vineyard",
    slides: [
      "Workshop Breakout — Let's move from hearing to doing.",
      "Form groups of 4–5 and share quick wins.",
      "Pray for one another's next step.",
      "Song: Way Maker",
      "Song: Raise A Hallelujah",
      "Song: Graves Into Gardens",
    ],
  },
  {
    sourceId: "sample-prayer-ministry",
    type: "slides",
    title: "Prayer & Ministry",
    description: "Lead the response moment with guided prayer points.",
    layout: "bullet",
    slideTheme: "midnight",
    slideAccent: "cobalt",
    slideGradient: "ocean",
    slides: [
      "Call To Prayer",
      "Let's take a moment to respond to God's Word.",
      "Thanksgiving",
      "Repentance",
      "Intercession",
      "Surrender",
    ],
  },
  {
    sourceId: "sample-communion-benediction",
    type: "slides",
    title: "Communion & Benediction",
    description: "Close in unity with communion focus and blessing.",
    layout: "titleSubtitle",
    slideTheme: "sandstone",
    slideAccent: "gold",
    slideGradient: "sunrise",
    slides: [
      "Communion & Benediction",
      "\"Do this in remembrance of Me.\" — Luke 22:19",
      "Communion Reflection",
      "Closing Blessing: \"The Lord bless you and keep you.\" — Numbers 6:24–26",
    ],
  },
  {
    sourceId: "sample-closing-fellowship",
    type: "slides",
    title: "Closing & Fellowship",
    description: "End with gratitude and clear invitation to connect.",
    layout: "cta",
    slideTheme: "forest",
    slideAccent: "emerald",
    slideGradient: "vineyard",
    slides: [
      "Thank You for Worshiping With Us",
      "Go in peace and serve the Lord.",
      "Stay for Fellowship! Coffee and conversations in the lobby.",
      "Prayer Teams Available",
    ],
  },
];

const websiteSources = websitesData.flatMap((group, groupIndex) => {
  const category = group?.category || "Web Resource";
  const categorySlug = slugify(category) || `category-${groupIndex}`;
  return (group?.sites || [])
    .map((site, siteIndex) => {
      const normalizedUrl = normalizeWebsiteUrl(site?.url);
      const embedUrl = buildWebsiteEmbedUrl(site?.url);
      if (!normalizedUrl && !embedUrl) {
        return null;
      }
      const siteSlug = slugify(site?.title) || `site-${siteIndex}`;
      const sourceId = `website-${categorySlug}-${siteSlug}-${siteIndex}`;
      return {
        sourceId,
        type: "externalPage",
        title: site?.title || "Resource",
        description: site?.description || "",
        reference: category,
        embedUrl: embedUrl || normalizedUrl,
        originalUrl: normalizedUrl || embedUrl || "",
      };
    })
    .filter(Boolean);
});

const SLIDE_LAYOUT_OPTIONS = [
  { value: "titleSubtitle", label: "Title & Subtitle" },
  { value: "bullet", label: "Title + Content" },
  { value: "twoColumn", label: "Two Column" },
  { value: "grid", label: "Tiles / Grid" },
  { value: "chart", label: "Chart / Data" },
  { value: "timeline", label: "Timeline / Process" },
  { value: "matrix", label: "Matrix / Framework" },
  { value: "imageCaption", label: "Image + Caption" },
  { value: "quote", label: "Quote" },
  { value: "cta", label: "Summary / Call to Action" },
];

const SLIDE_THEME_OPTIONS = [
  { value: "midnight", label: "Midnight", background: "#030712", text: "#f8fafc", muted: "#cbd5f5" },
  { value: "forest", label: "Evergreen", background: "#052e16", text: "#f0fdf4", muted: "#bbf7d0" },
  { value: "dusk", label: "Dusk", background: "#111826", text: "#e0e7ff", muted: "#c7d2fe" },
  { value: "sandstone", label: "Sandstone", background: "#3f1f0f", text: "#fefce8", muted: "#fde68a" },
];

const SLIDE_ACCENT_OPTIONS = [
  { value: "gold", label: "Radiant Gold", color: "#f6d365" },
  { value: "cobalt", label: "Cobalt Blue", color: "#60a5fa" },
  { value: "coral", label: "Coral", color: "#fb7185" },
  { value: "emerald", label: "Emerald", color: "#34d399" },
];

const SLIDE_GRADIENT_OPTIONS = [
  { value: "", label: "None", gradient: "" },
  { value: "sunrise", label: "Sunrise", gradient: "linear-gradient(135deg, #f6d365 0%, #fda085 100%)" },
  { value: "ocean", label: "Ocean", gradient: "linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)" },
  { value: "vineyard", label: "Vineyard", gradient: "linear-gradient(135deg, #14532d 0%, #22c55e 100%)" },
  { value: "ember", label: "Ember", gradient: "linear-gradient(135deg, #f97316 0%, #dc2626 100%)" },
];

const DEFAULT_SLIDE_LAYOUT = "bullet";
const DEFAULT_SLIDE_THEME = SLIDE_THEME_OPTIONS[0].value;
const DEFAULT_SLIDE_ACCENT = SLIDE_ACCENT_OPTIONS[0].value;
const DEFAULT_SLIDE_GRADIENT = SLIDE_GRADIENT_OPTIONS[0].value;

const SLIDE_THEME_MAP = SLIDE_THEME_OPTIONS.reduce((acc, theme) => {
  acc[theme.value] = theme;
  return acc;
}, {});

const SLIDE_ACCENT_MAP = SLIDE_ACCENT_OPTIONS.reduce((acc, accent) => {
  acc[accent.value] = accent;
  return acc;
}, {});

const SLIDE_GRADIENT_MAP = SLIDE_GRADIENT_OPTIONS.reduce((acc, gradient) => {
  acc[gradient.value] = gradient;
  return acc;
}, {});

const DEFAULT_ITEM_DURATION_MINUTES = 3;

const sanitizeDurationMinutes = (value) => {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed > 0) {
    return Math.min(999, Math.round(parsed));
  }
  return DEFAULT_ITEM_DURATION_MINUTES;
};

const applySlideDefaults = (item) => {
  const base = {
    ...item,
    durationMinutes: sanitizeDurationMinutes(item.durationMinutes),
  };
  if (base.type === "slides") {
    return {
      ...base,
      layout: base.layout || DEFAULT_SLIDE_LAYOUT,
      slideTheme: base.slideTheme || DEFAULT_SLIDE_THEME,
      slideAccent: base.slideAccent || DEFAULT_SLIDE_ACCENT,
      slideGradient:
        typeof base.slideGradient === "string"
          ? base.slideGradient
          : DEFAULT_SLIDE_GRADIENT,
    };
  }
  if (base.type === "presentationRemote") {
    const settings = parsePresentationRemoteSettings(
      base.embedUrl || base.originalUrl || "",
      getPresentationCatalogUrl(),
    );
    const parts = splitPresentationFile(settings.file);
    const fallbackFile = parts.file || settings.presentation || "";
    return {
      ...base,
      presentTopic: base.presentTopic ?? parts.topic,
      presentFile: base.presentFile ?? fallbackFile,
    };
  }
  return base;
};

const splitPastedUrls = (value) =>
  (value || "")
    .split(/[\s,\t\n\r]+/g)
    .map((entry) => entry.trim())
    .filter(Boolean);

const inferTitleFromUrl = (url, fallback) => {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./i, "");
    const primary = host.split(".")[0] || host;
    return prettifyTitle(primary) || fallback;
  } catch (error) {
    return fallback;
  }
};

const serializeSessionParam = (config) => {
  try {
    return LZString.compressToEncodedURIComponent(JSON.stringify(config));
  } catch (error) {
    console.error("Unable to serialize session payload", error);
    return "";
  }
};

const deserializeSessionParam = (encoded) => {
  if (!encoded) return null;
  try {
    const json = LZString.decompressFromEncodedURIComponent(encoded);
    if (!json) return null;
    return JSON.parse(json);
  } catch (error) {
    console.error("Unable to parse session payload", error);
    return null;
  }
};

const buildShareSnapshot = (config) => {
  if (!config) return null;
  return {
    t: config.title || "",
    a: Boolean(config.autoTitle),
    q: (config.sequence || []).map((item) => {
      const payload = item.payload || {};
      const shareItem = {
        i: item.id,
        s: item.sourceId,
        y: item.type,
        T: item.title,
      };
      if (item.notes) shareItem.n = item.notes;
      if (
        typeof item.durationMinutes === "number" &&
        item.durationMinutes !== DEFAULT_ITEM_DURATION_MINUTES
      ) {
        shareItem.d = item.durationMinutes;
      }
      const sharePayload = {};
      if (payload.songs?.length) sharePayload.g = payload.songs;
      if (payload.slides?.length) sharePayload.l = payload.slides;
      if (payload.layout) sharePayload.L = payload.layout;
      if (payload.slideTheme) sharePayload.t = payload.slideTheme;
      if (payload.slideAccent) sharePayload.c = payload.slideAccent;
      if (payload.slideGradient) sharePayload.r = payload.slideGradient;
      if (payload.reference) sharePayload.f = payload.reference;
      if (payload.content) sharePayload.o = payload.content;
      if (payload.embedUrl) sharePayload.e = payload.embedUrl;
      if (payload.description) sharePayload.p = payload.description;
      if (payload.originalUrl) sharePayload.u = payload.originalUrl;
      if (payload.backgroundMusic) sharePayload.b = payload.backgroundMusic;
      if (Object.keys(sharePayload).length) {
        shareItem.p = sharePayload;
      }
      return shareItem;
    }),
  };
};

const expandShareSnapshot = (snapshot) => {
  if (!snapshot) return null;
  return {
    title: snapshot.t || "",
    autoTitle: Boolean(snapshot.a),
    sequence: (snapshot.q || []).map((entry) => {
      const payload = entry.p || {};
      return {
        id: entry.i || safeId("session-item"),
        sourceId: entry.s || "custom",
        type: entry.y || "moment",
        title: entry.T || "Untitled",
        notes: entry.n || "",
        durationMinutes: sanitizeDurationMinutes(entry.d),
        payload: {
          songs: payload.g || [],
          slides: payload.l || [],
          layout: payload.L || null,
          slideTheme: payload.t || null,
          slideAccent: payload.c || null,
          slideGradient: payload.r || null,
          reference: payload.f || null,
          content: payload.o || null,
          embedUrl: payload.e || null,
          description: payload.p || null,
          originalUrl: payload.u || null,
          backgroundMusic: payload.b || null,
        },
      };
    }),
  };
};

const copyTextToClipboard = async (value) => {
  if (!value) return false;
  try {
    if (
      typeof navigator !== "undefined" &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      await navigator.clipboard.writeText(value);
      return true;
    }
    if (typeof document !== "undefined") {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return true;
    }
  } catch (error) {
    console.error("Copy failed", error);
    return false;
  }
  return false;
};

const buildSessionEntryFromUrl = (url, index, overrides = {}) => {
  const raw = (url || "").trim();
  if (!raw) {
    throw new Error("Remove blank values from your URL list.");
  }
  const youtubeId = parseYoutubeVideoId(raw);
  const titleOverride = (overrides.title || "").trim();
  if (youtubeId) {
    const embedUrl = buildYoutubeEmbedUrl(youtubeId);
    const originalUrl = /^[a-z]+:\/\//i.test(raw) ? raw : `${YOUTUBE_WATCH_BASE}${youtubeId}`;
    return {
      id: safeId("session-item"),
      sourceId: `pasted-youtube-${youtubeId}`,
      type: "youtubeVideo",
      title: titleOverride || `YouTube Video ${index + 1}`,
      notes: "",
      durationMinutes: DEFAULT_ITEM_DURATION_MINUTES,
      payload: {
        songs: [],
        slides: [],
        layout: null,
        slideTheme: null,
        slideAccent: null,
        slideGradient: null,
        reference: null,
        content: null,
        embedUrl,
        description: "Imported YouTube video.",
        originalUrl,
        backgroundMusic: null,
      },
    };
  }

  const normalized = normalizeWebsiteUrl(raw);
  let parsedUrl;
  try {
    parsedUrl = new URL(normalized);
  } catch (error) {
    throw new Error(`Enter a valid URL: ${raw}`);
  }
  const embedUrl = buildWebsiteEmbedUrl(parsedUrl.toString());
  const title = titleOverride || inferTitleFromUrl(parsedUrl.toString(), `Link ${index + 1}`);
  return {
    id: safeId("session-item"),
    sourceId: `pasted-link-${index + 1}`,
    type: "externalPage",
    title,
    notes: "",
    durationMinutes: DEFAULT_ITEM_DURATION_MINUTES,
    payload: {
      songs: [],
      slides: [],
      layout: null,
      slideTheme: null,
      slideAccent: null,
      slideGradient: null,
      reference: null,
      content: null,
      embedUrl,
      description: `Imported link from ${parsedUrl.hostname}`,
      originalUrl: parsedUrl.toString(),
      backgroundMusic: null,
    },
  };
};

const buildTemplateFromUrlContent = (rawContent, fallbackTitle) => {
  const urls = extractUrlsFromBlogEntry(rawContent);
  if (!urls.length) return null;
  const sequence = [];
  urls.forEach((entry, index) => {
    try {
      sequence.push(buildSessionEntryFromUrl(entry, index));
    } catch (error) {
      console.warn("Skipped invalid URL in blog entry", error);
    }
  });
  if (!sequence.length) return null;
  return {
    title: (fallbackTitle || "Session Links").trim() || "Session Links",
    autoTitle: false,
    sequence,
  };
};

const buildSessionItemFromConfigEntry = (entry) => {
  const payload = entry?.payload || {};
  return applySlideDefaults({
    instanceId: entry?.id || safeId("session-item"),
    sourceId: entry?.sourceId || "custom",
    type: entry?.type || "moment",
    title: entry?.title || "Untitled",
    notes: entry?.notes || "",
    durationMinutes: entry?.durationMinutes,
    songs: payload.songs || [],
    slides: payload.slides || [],
    reference: payload.reference || null,
    content: payload.content || null,
    embedUrl: payload.embedUrl || null,
    description: payload.description || "",
    originalUrl: payload.originalUrl || null,
    layout: payload.layout,
    slideTheme: payload.slideTheme,
    slideAccent: payload.slideAccent,
    slideGradient: payload.slideGradient,
    backgroundMusic: payload.backgroundMusic || null,
  });
};

const buildSessionConfigEntryFromItem = (item) => ({
  id: item.instanceId || safeId("session-item"),
  sourceId: item.sourceId,
  type: item.type,
  title: item.title,
  notes: item.notes || "",
  durationMinutes: sanitizeDurationMinutes(item.durationMinutes),
  payload: {
    songs: item.songs || [],
    slides: item.slides || [],
    layout: item.layout || (item.type === "slides" ? DEFAULT_SLIDE_LAYOUT : null),
    slideTheme:
      item.slideTheme || (item.type === "slides" ? DEFAULT_SLIDE_THEME : null),
    slideAccent:
      item.slideAccent || (item.type === "slides" ? DEFAULT_SLIDE_ACCENT : null),
    slideGradient:
      typeof item.slideGradient === "string"
        ? item.slideGradient
        : item.type === "slides"
        ? DEFAULT_SLIDE_GRADIENT
        : null,
    reference: item.reference || null,
    content: item.content || null,
    embedUrl: item.embedUrl || null,
    description: item.description || null,
    originalUrl: item.originalUrl || null,
    backgroundMusic: item.backgroundMusic || null,
  },
});

const deriveUrlFromConfig = (entry) =>
  entry?.payload?.originalUrl || entry?.payload?.embedUrl || "";

const createDefaultConfigEntry = (overrides = {}) => {
  const base = {
    id: safeId("session-item"),
    sourceId: "custom",
    type: "moment",
    title: overrides.title || "Untitled",
    notes: "",
    durationMinutes: DEFAULT_ITEM_DURATION_MINUTES,
    payload: {
      songs: [],
      slides: [],
      layout: null,
      slideTheme: null,
      slideAccent: null,
      slideGradient: null,
      reference: null,
      content: null,
      embedUrl: null,
      description: "",
      originalUrl: "",
      backgroundMusic: null,
    },
  };
  return {
    ...base,
    ...overrides,
    payload: {
      ...base.payload,
      ...(overrides.payload || {}),
    },
  };
};

const createTableRowFromConfig = (entry) => {
  const safeEntry = {
    ...entry,
    id: entry.id || safeId("session-item"),
  };
  return {
    id: safeEntry.id,
    title: safeEntry.title || "",
    url: deriveUrlFromConfig(safeEntry),
    config: safeEntry,
    configDraft: JSON.stringify(safeEntry, null, 2),
    configError: "",
    expanded: false,
  };
};

const deriveTableRowsFromSession = (items) =>
  items.map((item) => createTableRowFromConfig(buildSessionConfigEntryFromItem(item)));

const parseTableLines = (text, startingIndex = 0) => {
  const lines = (text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const rows = [];
  lines.forEach((line, offset) => {
    if (!line) return;
    let cells = line.split(/\t+/);
    if (cells.length === 1) {
      cells = line.split(/\s{2,}|,\s*/);
    }
    const firstCell = (cells[0] || "").trim();
    const secondCell = (cells[1] || "").trim();
    if (!firstCell && !secondCell) return;
    const hasUrlCandidate = Boolean(secondCell) || /^https?:/i.test(firstCell);
    const title = hasUrlCandidate ? firstCell : `Item ${startingIndex + rows.length + 1}`;
    const url = secondCell || (!hasUrlCandidate ? "" : firstCell);
    rows.push({
      id: safeId("table-row"),
      title,
      url: url.trim(),
    });
  });
  return rows;
};

const layoutTemplateSources = [
  {
    sourceId: "layout-title-subtitle",
    type: "slides",
    title: "Title & Subtitle",
    description: "Centered opener slide for branding, hosts, and key scripture focus.",
    layout: "titleSubtitle",
    slideTheme: "midnight",
    slideAccent: "gold",
    slideGradient: "sunrise",
    slides: [
      "Awaken Worship Weekend",
      "Glory Community Church",
      "Hosted by Pastor Elena Ruiz",
      "Theme - Psalm 95:1-3",
    ],
  },
  {
    sourceId: "layout-title-content",
    type: "slides",
    title: "Title + Content (Bullet)",
    description: "Simple teaching slide with headline and supporting thoughts.",
    layout: "bullet",
    slideTheme: "dusk",
    slideAccent: "cobalt",
    slideGradient: "ocean",
    slides: [
      "Why We Gather",
      "Call every heart to encounter Jesus",
      "Create room for the Holy Spirit to minister",
      "Encourage the church toward mission",
    ],
  },
  {
    sourceId: "layout-two-column",
    type: "slides",
    title: "Two-Column Comparison",
    description: "Contrast storylines such as Before / After Christ moments.",
    layout: "twoColumn",
    slideTheme: "forest",
    slideAccent: "emerald",
    slideGradient: "vineyard",
    slides: [
      "Column 1 - Old Life: striving, anxiety, isolation",
      "Column 2 - New Life: grace, peace, community",
      "Scripture Anchor: 2 Corinthians 5:17",
    ],
  },
  {
    sourceId: "layout-grid",
    type: "slides",
    title: "Tiles / Grid Layout",
    description: "Highlight multiple ministries or service elements at a glance.",
    layout: "grid",
    slideTheme: "midnight",
    slideAccent: "cobalt",
    slideGradient: "ocean",
    slides: [
      "Prayer Team",
      "Kids & Families",
      "Outreach Crews",
      "Creative Arts",
    ],
  },
  {
    sourceId: "layout-chart",
    type: "slides",
    title: "Chart / Data Visualization",
    description: "Share impact metrics or generosity updates with clarity.",
    layout: "chart",
    slideTheme: "sandstone",
    slideAccent: "gold",
    slideGradient: "sunrise",
    slides: [
      "Kingdom Impact Report",
      "137 care packages delivered",
      "68 families in weekly discipleship",
      "Next Goal: 25 new volunteers",
    ],
  },
  {
    sourceId: "layout-timeline",
    type: "slides",
    title: "Timeline / Process Flow",
    description: "Show steps in a spiritual formation path or project milestone.",
    layout: "timeline",
    slideTheme: "dusk",
    slideAccent: "cobalt",
    slideGradient: "ocean",
    slides: [
      "Step 1 - Pray",
      "Step 2 - Prepare",
      "Step 3 - Go",
      "Step 4 - Celebrate",
    ],
  },
  {
    sourceId: "layout-matrix",
    type: "slides",
    title: "Matrix / Framework",
    description: "2×2 layout for priorities, spiritual rhythms, or ministry options.",
    layout: "matrix",
    slideTheme: "forest",
    slideAccent: "emerald",
    slideGradient: "vineyard",
    slides: [
      "Axis: Pastoral Care <> Outreach",
      "Axis: Gather <> Scatter",
      "High Care / Gather - Small Groups",
      "High Outreach / Scatter - Neighborhood Serve Teams",
    ],
  },
  {
    sourceId: "layout-image-caption",
    type: "slides",
    title: "Image + Caption",
    description: "Visual storytelling slide with short testimony or highlight.",
    layout: "imageCaption",
    slideTheme: "sandstone",
    slideAccent: "coral",
    slideGradient: "ember",
    slides: [
      "Mission Trip Highlight",
      "Caption: Students praying with families in Tijuana",
      "Scripture: Isaiah 61:1",
    ],
  },
  {
    sourceId: "layout-quote",
    type: "slides",
    title: "Quote / Key Message",
    description: "Spotlight a prophetic word, testimony, or Scripture declaration.",
    layout: "quote",
    slideTheme: "midnight",
    slideAccent: "gold",
    slideGradient: "sunrise",
    slides: [
      '"The Lord is doing a new thing among us."',
      "- Lead Pastor Michael Chen",
      "Reference: Isaiah 43:19",
    ],
  },
  {
    sourceId: "layout-summary-cta",
    type: "slides",
    title: "Summary / Call-to-Action",
    description: "Close with next steps or thanks, plus contact or QR prompt.",
    layout: "cta",
    slideTheme: "dusk",
    slideAccent: "emerald",
    slideGradient: "vineyard",
    slides: [
      "Thank you for worshiping with us!",
      "Next Steps: Prayer teams up front",
      "Serve: Join Welcome Collective",
      "Give: text HOPE to 55498",
    ],
  },
];

const BASE_SOURCE_GROUPS = [
  { key: "sample-slides", label: "Sample Slides", data: sampleSlidesSources },
  {
    key: "layout-templates",
    label: "Layout Templates",
    icon: <FaMagic />,
    description: "Reusable layouts with Christian-focused placeholder copy.",
    data: layoutTemplateSources,
  },
  {
    key: "websites-library",
    label: "Websites",
    icon: <FaGlobe />,
    description: "Curated ministry and worship websites ready to embed live.",
    data: websiteSources,
  },
];

const MIN_PANEL_PERCENT = 15;
const COLLAPSED_PANEL_WIDTH_REM = 2.75;
const DEFAULT_PANEL_WIDTHS = [33.34, 33.33, 33.33];
const presenterTokenId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

const WORSHIP_CATALOG_HELPER_URL = buildWorshipEmbedUrl(undefined, DEFAULT_WORSHIP_DOMAIN);

const DEFAULT_BIBLE_SELECTION = {
  book: "0",
  chapter: "1",
  verse: "",
  version: "NKJV",
  language: "en",
  page: "0",
  lines: "3",
  wpm: "170",
};

const BIBLE_VERSION_CHOICES = [
  { value: "NKJV", label: "NKJV (New King James Version)" },
  { value: "KJV", label: "KJV (King James Version)" },
  { value: "NIV", label: "NIV (New International Version)" },
  { value: "ESV", label: "ESV (English Standard Version)" },
  { value: "WEB", label: "WEB (World English Bible)" },
  { value: "RSV", label: "RSV (Revised Standard Version)" },
  { value: "ICB", label: "ICB (International Children's Bible)" },
  { value: "AMP", label: "AMP (Amplified)" },
  { value: "CSB", label: "CSB (Christian Standard Bible)" },
  { value: "NLT", label: "NLT (New Living Translation)" },
];

const normalizeBibleCatalogTitle = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "Bible Verse";
  return trimmed.toLowerCase() === "global christian community - gcc"
    ? "Bible Verse"
    : trimmed;
};

const deriveAutoTitle = (items) => {
  const base = new Date().toLocaleDateString(undefined, {
    month: "short",
    day: "2-digit",
  });
  const highlight = items.length ? items[0].title : "Creative Flow";
  return `Worship Session ${base} — ${highlight}`;
};

const chunkSlides = (slides, chunkCount) => {
  if (!slides.length || chunkCount <= 0) return [];
  const size = Math.ceil(slides.length / chunkCount);
  const chunks = [];
  for (let index = 0; index < slides.length; index += size) {
    chunks.push(slides.slice(index, index + size));
  }
  return chunks;
};

const renderSlideDeckLayout = (layout, slides, item) => {
  const defaultHeading = item.title || "Slide Deck";
  const safeSlides = slides.length ? slides : [defaultHeading];
  switch (layout) {
    case "titleSubtitle": {
      const [hero, subtitle, ...extras] = safeSlides;
      return (
        <div className="slide-hero">
          <p className="slide-stage__eyebrow">{defaultHeading}</p>
          <h1 className="slide-stage__headline">{hero || defaultHeading}</h1>
          {subtitle && <p className="slide-stage__subtitle">{subtitle}</p>}
          {extras.length > 0 && (
            <ul className="slide-stage__list">
              {extras.map((line, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    case "twoColumn": {
      const [left, right] = chunkSlides(safeSlides, 2);
      return (
        <div className="slide-columns">
          {[left, right].map((column, columnIndex) => (
            <div className="slide-column" key={`col-${columnIndex}`}>
              <p className="slide-stage__eyebrow">{columnIndex === 0 ? "Column A" : "Column B"}</p>
              <ul className="slide-stage__list">
                {(column || []).map((line, index) => (
                  <li key={`${line}-${index}`}>{line}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      );
    }
    case "grid": {
      return (
        <div className="slide-grid">
          {safeSlides.map((line, index) => (
            <div className="slide-card" key={`${line}-${index}`}>
              <span className="slide-card__id">{String(index + 1).padStart(2, "0")}</span>
              <p>{line}</p>
            </div>
          ))}
        </div>
      );
    }
    case "chart": {
      const [heading, ...stats] = safeSlides;
      return (
        <div className="slide-chart">
          <div className="slide-chart__heading">
            <p className="slide-stage__eyebrow">{defaultHeading}</p>
            <h1 className="slide-stage__headline">{heading || defaultHeading}</h1>
          </div>
          <div className="slide-chart__stats">
            {(stats.length ? stats : ["Add stat"]).map((line, index) => (
              <div className="slide-stat" key={`${line}-${index}`}>
                <span className="slide-stat__value">{line.split(":")[0] || line}</span>
                {line.includes(":") && (
                  <span className="slide-stat__label">{line.split(":").slice(1).join(":").trim()}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "timeline": {
      return (
        <div className="slide-timeline">
          {safeSlides.map((line, index) => (
            <div className="slide-timeline__step" key={`${line}-${index}`}>
              <span className="slide-timeline__index">{index + 1}</span>
              <p>{line}</p>
            </div>
          ))}
        </div>
      );
    }
    case "matrix": {
      const axisX = safeSlides[0] || "Axis X";
      const axisY = safeSlides[1] || "Axis Y";
      const quadrants = safeSlides.slice(2);
      while (quadrants.length < 4) {
        quadrants.push("Add detail");
      }
      return (
        <div className="slide-matrix">
          <div className="slide-matrix__axes">
            <span>{axisY}</span>
            <span>{axisX}</span>
          </div>
          <div className="slide-matrix__grid">
            {quadrants.slice(0, 4).map((line, index) => (
              <div className="slide-matrix__cell" key={`${line}-${index}`}>
                <span className="slide-matrix__label">Q{index + 1}</span>
                <p>{line}</p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "imageCaption": {
      const [headline, caption, detail] = safeSlides;
      return (
        <div className="slide-image">
          <div className="slide-image__art" aria-hidden="true" />
          <div className="slide-image__copy">
            <p className="slide-stage__eyebrow">{defaultHeading}</p>
            <h1 className="slide-stage__headline">{headline || defaultHeading}</h1>
            {caption && <p>{caption}</p>}
            {detail && <p className="slide-stage__muted">{detail}</p>}
          </div>
        </div>
      );
    }
    case "quote": {
      const [quoteLine, author, reference] = safeSlides;
      return (
        <div className="slide-quote">
          <blockquote>
            <span className="slide-quote__mark">“</span>
            <p>{quoteLine || defaultHeading}</p>
          </blockquote>
          {author && <cite>{author}</cite>}
          {reference && <p className="slide-stage__muted">{reference}</p>}
        </div>
      );
    }
    case "cta": {
      const headline = safeSlides[0] || defaultHeading;
      const remainder = safeSlides.slice(1);
      const highlight = remainder.length ? remainder[remainder.length - 1] : "";
      const listItems = highlight ? remainder.slice(0, -1) : remainder;
      return (
        <div className="slide-cta">
          <h1 className="slide-stage__headline">{headline}</h1>
          {listItems.length > 0 && (
            <ul className="slide-stage__list">
              {listItems.map((line, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
          )}
          {highlight && <div className="slide-cta__highlight">{highlight}</div>}
        </div>
      );
    }
    case "bullet":
    default:
      return (
        <div className="slide-bullets">
          <p className="slide-stage__eyebrow">{defaultHeading}</p>
          <h1 className="slide-stage__headline">{safeSlides[0] || defaultHeading}</h1>
          {safeSlides.length > 1 && (
            <ul className="slide-stage__list">
              {safeSlides.slice(1).map((line, index) => (
                <li key={`${line}-${index}`}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      );
  }
};

const renderSlideDeck = (item, options = {}) => {
  const { stage = false } = options;
  const layout = item.layout || DEFAULT_SLIDE_LAYOUT;
  const slides = Array.isArray(item.slides) ? item.slides : [];
  const theme = SLIDE_THEME_MAP[item.slideTheme] || SLIDE_THEME_MAP[DEFAULT_SLIDE_THEME];
  const accent = SLIDE_ACCENT_MAP[item.slideAccent] || SLIDE_ACCENT_MAP[DEFAULT_SLIDE_ACCENT];
  const gradient = SLIDE_GRADIENT_MAP[item.slideGradient || ""];

  const style = {
    backgroundColor: theme?.background,
    color: theme?.text,
    "--slide-accent-color": accent?.color,
    "--slide-muted-color": theme?.muted,
    "--slide-text-color": theme?.text,
  };

  if (gradient?.gradient) {
    style.backgroundImage = gradient.gradient;
  } else {
    style.backgroundImage = "none";
  }

  const className = [
    "slide-stage",
    stage ? "slide-stage--stage" : "slide-stage--preview",
    `slide-layout-${layout}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className} style={style}>
      <div className="slide-stage__glass" aria-hidden="true" />
      <div className="slide-stage__inner">{renderSlideDeckLayout(layout, slides, item)}</div>
      {!stage && item.notes && <p className="slide-stage__notes">Notes: {item.notes}</p>}
    </div>
  );
};

const renderCollapsibleDescription = (description, stage) => {
  if (!description) return null;
  if (stage) {
    return <p>{renderMultilineText(description)}</p>;
  }
  return (
    <details className="collapsible-description">
      <summary>Description</summary>
      <p>{renderMultilineText(description)}</p>
    </details>
  );
};

const renderItemPreview = (item, options = {}) => {
  const { stage = false, onEmbedRef } = options;
  const assignEmbedRef = typeof onEmbedRef === "function" ? onEmbedRef : undefined;
  const backgroundBadge =
    !stage && item?.backgroundMusic
      ? (
          <p className="background-music-note">
            <FaMusic /> Background music: {describeBackgroundMusic(item.backgroundMusic)}
          </p>
        )
      : null;
  const wrapWithBadge = (content) =>
    backgroundBadge ? (
      <>
        {content}
        {backgroundBadge}
      </>
    ) : (
      content
    );

  switch (item.type) {
    case "worship":
      return wrapWithBadge(
        <div className={`item-preview${stage ? " stage-mode" : ""}`}>
          <h3>{item.title}</h3>
          <p className="item-subtitle">Workshop Set</p>
          <ul>
            {item.songs.map((song) => (
              <li key={song}>{song}</li>
            ))}
          </ul>
          {stage && item.notes && <p className="item-notes">Notes: {item.notes}</p>}
        </div>
      );
    case "studio":
    case "youtube":
    case "youtubeVideo":
      return wrapWithBadge(
        <div className={`item-preview${stage ? " stage-mode" : ""}`}>
          {(!stage || !item.embedUrl) && (
            <>
              <h3>{item.title}</h3>
              <p className="item-subtitle">
                {item.type === "studio"
                  ? "Studio Playlist"
                  : item.type === "youtubeVideo"
                  ? "YouTube Song"
                  : "YouTube Playlist"}
              </p>
              {renderCollapsibleDescription(item.description, stage)}
            </>
          )}
          {item.embedUrl && (
            <div className={`iframe-wrapper${stage ? " stage-mode" : ""}`}>
              <iframe
                ref={assignEmbedRef}
                src={item.embedUrl}
                title={item.title}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          )}
        </div>
      );
    case "bible":
      return wrapWithBadge(
        <div className={`item-preview${stage ? " stage-mode" : ""}`}>
          <h3>{item.title}</h3>
          <p className="item-subtitle">{item.reference}</p>
          <blockquote>{item.content}</blockquote>
          {stage && item.notes && <p className="item-notes">Notes: {item.notes}</p>}
        </div>
      );
    case "slides": {
      const deck = renderSlideDeck(item, { stage });
      return wrapWithBadge(deck);
    }
    case "worshipRemote":
    case "bibleRemote":
    case "presentationRemote":
    case "externalVideo":
    case "externalPage": {
      return wrapWithBadge(
        <div className={`item-preview${stage ? " stage-mode" : ""}`}>
          {(!stage || !item.embedUrl) && (
            <>
              <h3>{item.title}</h3>
              {item.reference && <p className="item-subtitle">{item.reference}</p>}
              {!item.reference &&
                (item.type === "externalVideo"
                  ? <p className="item-subtitle">External Video</p>
                  : item.type === "externalPage"
                  ? <p className="item-subtitle">External Page</p>
                  : null)}
              {renderCollapsibleDescription(item.description, stage)}
            </>
          )}
          {item.embedUrl ? (
            <div className={`iframe-wrapper${stage ? " stage-mode" : ""}`}>
              <iframe
                ref={assignEmbedRef}
                src={item.embedUrl}
                title={item.title}
                allow="autoplay; fullscreen; clipboard-write"
                allowFullScreen
              />
            </div>
          ) : (
            item.content && <blockquote>{item.content}</blockquote>
          )}
        </div>
      );
    }
    default:
      return wrapWithBadge(
        <div className={`item-preview${stage ? " stage-mode" : ""}`}>
          <h3>{item.title}</h3>
          {renderCollapsibleDescription(item.description, stage)}
        </div>
      );
  }
};

const formatTypeLabel = (type) => {
  switch (type) {
    case "worshipRemote":
      return "Remote Worship";
    case "presentationRemote":
      return "Remote Slides";
    case "bibleRemote":
      return "Remote Bible";
    case "externalVideo":
      return "External Video";
    case "externalPage":
      return "External Page";
    default:
      return type;
  }
};

function WorshipBuilder() {
  const [searchParams] = useSearchParams();
  const initialTemplateCatalogDomain = useMemo(() => {
    const catalogParam = searchParams.get("catalog");
    const domainParam = searchParams.get("domain");
    if (typeof window !== "undefined" && !catalogParam && !domainParam) {
      const globalDomain = window.__GCC_DOMAIN__;
      if (globalDomain) {
        return ensureBlogspotDomain(globalDomain);
      }
    }
    return ensureBlogspotDomain(catalogParam || domainParam || "");
  }, [searchParams]);
  const [sessionItems, setSessionItems] = useState([]);
  const [templateCatalogDomain, setTemplateCatalogDomain] = useState(
    initialTemplateCatalogDomain || DEFAULT_TEMPLATE_CATALOG_DOMAIN,
  );
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [autoTitle, setAutoTitle] = useState(true);
  const [sessionTitle, setSessionTitle] = useState(deriveAutoTitle([]));
  const [activeGroup, setActiveGroup] = useState(BASE_SOURCE_GROUPS[0].key);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isPresenting, setIsPresenting] = useState(false);
  const [slideTimerEnabled, setSlideTimerEnabled] = useState(() =>
    isSlideTimerParamEnabled(searchParams),
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideElapsedSeconds, setSlideElapsedSeconds] = useState(0);
  const [showConfigOverlay, setShowConfigOverlay] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dragIndexRef = useRef(null);
  const slideTimerStartRef = useRef(null);
  const slideTimerLastSegmentRef = useRef(0);
  const presentationTimerRef = useRef(null);
  const timerDragOffsetRef = useRef(null);
  const timerDragHandlersRef = useRef({ move: null, up: null });
  const [timerUseLeftPosition, setTimerUseLeftPosition] = useState(false);
  const [timerPosition, setTimerPosition] = useState({ top: 20, left: 20 });
  const [jsonDraft, setJsonDraft] = useState("");
  const [jsonDraftPristine, setJsonDraftPristine] = useState(true);
  const [importError, setImportError] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState("current");
  const [urlImportInput, setUrlImportInput] = useState("");
  const [urlImportError, setUrlImportError] = useState("");
  const [tableRows, setTableRows] = useState([]);
  const [tableError, setTableError] = useState("");
  const [tablePasteValue, setTablePasteValue] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [youtubeChannelItems, setYoutubeChannelItems] = useState([]);
  const [youtubeCatalogPlaylists, setYoutubeCatalogPlaylists] = useState([]);
  const [youtubeCatalogPlaylistFilter, setYoutubeCatalogPlaylistFilter] = useState("all");
  const [catalogControlsExpanded, setCatalogControlsExpanded] = useState(true);
  const [worshipCatalogItems, setWorshipCatalogItems] = useState([]);
  const [bibleCatalogItems, setBibleCatalogItems] = useState([]);
  const [presentCatalogItems, setPresentCatalogItems] = useState([]);
  const [customMediaItems, setCustomMediaItems] = useState([]);
  const [customMediaDraft, setCustomMediaDraft] = useState({
    title: "",
    url: "",
    description: "",
    mode: "auto",
  });
  const [customMediaError, setCustomMediaError] = useState("");
  const [bibleSelection, setBibleSelection] = useState(DEFAULT_BIBLE_SELECTION);
  const [bibleSelectionOptions, setBibleSelectionOptions] = useState({
    versions: [],
    languages: [],
  });
  const [backgroundPlaylistItems, setBackgroundPlaylistItems] = useState([]);
  const [backgroundPlaylistLoading, setBackgroundPlaylistLoading] = useState(false);
  const [backgroundPlaylistError, setBackgroundPlaylistError] = useState("");
  const [backgroundPlaylistId, setBackgroundPlaylistId] = useState(
    DEFAULT_BACKGROUND_PLAYLIST_ID,
  );
  const [backgroundMusicSelection, setBackgroundMusicSelection] = useState({
    mode: "none",
    playlistId: DEFAULT_BACKGROUND_PLAYLIST_ID,
    playlistVideoId: "",
    playlistLabel: "",
    playlistTitle: getConfiguredPlaylistTitle(DEFAULT_BACKGROUND_PLAYLIST_ID),
    customUrl: "",
    customLabel: "",
  });
  const [backgroundMusicFormError, setBackgroundMusicFormError] = useState("");
  const [backgroundMusicEnabled, setBackgroundMusicEnabled] = useState(true);
  const [catalogPreview, setCatalogPreview] = useState(null);
  const [previewAudioMuted, setPreviewAudioMuted] = useState(false);
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(false);
  const [autoAdvanceSeconds, setAutoAdvanceSeconds] = useState(3);
  const [catalogLoading, setCatalogLoading] = useState({
    youtube: false,
    worship: false,
    bible: false,
    present: false,
  });
  const [catalogError, setCatalogError] = useState({
    youtube: "",
    worship: "",
    bible: "",
    present: "",
  });
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  const bibleCatalogUrl = useMemo(() => getBibleCatalogUrl(), []);
  const presentationCatalogUrl = useMemo(
    () => getPresentationCatalogUrl(),
    [],
  );
  const [startFromUrl, setStartFromUrl] = useState(false);
  const presentationStageRef = useRef(null);
  const presentationContentRef = useRef(null);
  const presentationControlsRef = useRef(null);
  const presentationMusicRef = useRef(null);
  const presentationContentEmbedRef = useRef(null);
  const presentationItemKeyRef = useRef(null);
  const presentationHasTrackableMediaRef = useRef(false);
  const previewMusicRef = useRef(null);
  const controlsDragStateRef = useRef(null);
  const [controlsPosition, setControlsPosition] = useState(null);
  const [presentationControlsVisible, setPresentationControlsVisible] = useState(true);
  const [shortcutsOverlayVisible, setShortcutsOverlayVisible] = useState(false);
  const previewStageRef = useRef(null);
  const blogspotFeedCacheRef = useRef({});
  const [panelWidths, setPanelWidths] = useState([...DEFAULT_PANEL_WIDTHS]);
  const [panelModes, setPanelModes] = useState(PANEL_MODE_DEFAULTS);
  const fullscreenRef = useRef(null);
  const builderContentRef = useRef(null);
  const copyStatusTimerRef = useRef(null);
  const resizingStateRef = useRef(null);
  const sessionParamLoadedRef = useRef(false);
  const topicParamAppliedRef = useRef(false);
  const sessionStackRef = useRef(null);
  const outlineResizeStateRef = useRef(null);
  const panelWidthsRef = useRef([...DEFAULT_PANEL_WIDTHS]);
  const previewContentEmbedRef = useRef(null);
  const previewItemKeyRef = useRef(null);
  const previewHasTrackableMediaRef = useRef(false);
  const previewBackgroundKeyRef = useRef(null);
  const previewBackgroundHasTrackableRef = useRef(false);
  const autoAdvanceSyncRef = useRef({ isRemoteUpdate: false });
  const autoAdvanceTimerRef = useRef(null);
  const navigationRequestRef = useRef(null);
  const navigationFallbackTimerRef = useRef(null);
  const focusTargetRef = useRef("stage");
  const [previewMediaState, setPreviewMediaState] = useState({ status: "idle", itemKey: null });
  const [presentationMediaState, setPresentationMediaState] = useState({ status: "idle", itemKey: null });
  const [previewBackgroundState, setPreviewBackgroundState] = useState({
    status: "idle",
    itemKey: null,
  });
  const [outlineRatio, setOutlineRatio] = useState(0.65);
  const [presenterToken, setPresenterToken] = useState(() => searchParams.get("sync") || "");
  const isPresenterView = searchParams.get("presenter") === "1";
  const presenterChannelRef = useRef(null);
  const [presenterConnected, setPresenterConnected] = useState(!isPresenterView);
  const [isMobileLayout, setIsMobileLayout] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 1024px)").matches;
  });
  const [loadedTemplateName, setLoadedTemplateName] = useState("");
  const [remoteTemplateEntries, setRemoteTemplateEntries] = useState([]);
  const templates = useMemo(
    () => createTemplateDictionary([...LOCAL_TEMPLATE_ENTRIES, ...remoteTemplateEntries]),
    [remoteTemplateEntries],
  );
  const templateNames = useMemo(() => Object.keys(templates), [templates]);
  const templateSlugLookup = useMemo(() => buildSlugLookup(templates), [templates]);
  const normalizedTemplateCatalogDomain = useMemo(
    () => ensureBlogspotDomain(templateCatalogDomain),
    [templateCatalogDomain],
  );
  const catalogSelectOptions = useMemo(() => {
    const baseOptions = catalogOptions.length
      ? catalogOptions
      : [
          {
            label: "GCC Catalog",
            value: DEFAULT_TEMPLATE_CATALOG_DOMAIN,
          },
        ];
    const hasCurrentSelection = baseOptions.some(
      (option) => option.value === normalizedTemplateCatalogDomain,
    );
    if (hasCurrentSelection) {
      return baseOptions;
    }
    return [
      ...baseOptions,
      {
        label: normalizedTemplateCatalogDomain,
        value: normalizedTemplateCatalogDomain,
      },
    ];
  }, [catalogOptions, normalizedTemplateCatalogDomain]);
  const resolveTemplateKey = useCallback(
    (value) => {
      if (!value) return "";
      if (templates[value]) return value;
      const slug = slugify(value);
      return templateSlugLookup[slug] || "";
    },
    [templateSlugLookup, templates],
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mediaQuery = window.matchMedia("(max-width: 1024px)");
    const updateLayout = (event) => setIsMobileLayout(event.matches);

    // Ensure initial value stays in sync when component mounts.
    updateLayout(mediaQuery);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateLayout);
      return () => mediaQuery.removeEventListener("change", updateLayout);
    }

    mediaQuery.addListener(updateLayout);
    return () => mediaQuery.removeListener(updateLayout);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fallbackOptions = [
      {
        label: formatDomainLabel(DEFAULT_TEMPLATE_CATALOG_DOMAIN),
        value: DEFAULT_TEMPLATE_CATALOG_DOMAIN,
      },
    ];
    const fetchCatalogOptions = async () => {
      for (const prefix of BLOG_FEED_PROXY_PREFIXES) {
        const source = `${prefix}${ORGANIZATIONS_FEED_DOMAIN}/feeds/posts/default?alt=json`;
        try {
          const response = await fetch(source, { cache: "no-store" });
          if (!response.ok) {
            throw new Error(`Failed to load catalog list (${response.status})`);
          }
          const text = await response.text();
          const payload =
            parseBlogFeedPayload(text) ||
            (typeof JSON !== "undefined" ? parseBlogFeedPayload(text.trim()) : null);
          const entries = payload?.feed?.entry || [];
          const options = entries
            .map((entry) => {
              const title = (entry?.title?.$t || "").trim();
              const domain = ensureBlogspotDomain(title || "");
              const label = formatDomainLabel(domain) || title || domain;
              return domain ? { label, value: domain } : null;
            })
            .filter(Boolean);
          const uniqueOptions = [];
          const seen = new Set();
          options.forEach((option) => {
            if (seen.has(option.value)) return;
            seen.add(option.value);
            uniqueOptions.push(option);
          });
          if (!seen.has(DEFAULT_TEMPLATE_CATALOG_DOMAIN)) {
            uniqueOptions.unshift({
              label: formatDomainLabel(DEFAULT_TEMPLATE_CATALOG_DOMAIN),
              value: DEFAULT_TEMPLATE_CATALOG_DOMAIN,
            });
          }
          uniqueOptions.sort((a, b) => a.label.localeCompare(b.label));
          if (!cancelled) {
            setCatalogOptions(uniqueOptions.length ? uniqueOptions : fallbackOptions);
          }
          return;
        } catch (error) {
          if (!cancelled) {
            console.warn(`Failed to load catalog options from ${source}`, error);
          }
        }
      }
      if (!cancelled) {
        setCatalogOptions(fallbackOptions);
      }
    };
    fetchCatalogOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchRemoteTemplates = async () => {
      const domain = normalizedTemplateCatalogDomain || DEFAULT_TEMPLATE_CATALOG_DOMAIN;
      for (const prefix of BLOG_FEED_PROXY_PREFIXES) {
        const source = `${prefix}${domain}/feeds/posts/default?alt=json`;
        try {
          const response = await fetch(source, { cache: "no-store" });
          if (!response.ok) {
            throw new Error(`Failed to load blog templates (${response.status})`);
          }
          const text = await response.text();
          const payload =
            parseBlogFeedPayload(text) ||
            (typeof JSON !== "undefined" ? parseBlogFeedPayload(text.trim()) : null);
          const entries = payload?.feed?.entry || [];
          const parsedEntries = entries
            .map((entry) => {
              const categories = (entry?.category || [])
                .map((cat) => (typeof cat?.term === "string" ? cat.term.trim().toLowerCase() : ""))
                .filter(Boolean);
              const hasSessionTag = categories.some((category) => category.includes("session"));
              if (!hasSessionTag) {
                return null;
              }
              const rawContent = entry?.content?.$t;
              const fallbackTitle = (entry?.title?.$t || "").trim();
              let parsedTemplate = extractJsonFromBlogEntry(rawContent);
              if (!parsedTemplate) {
                parsedTemplate = buildTemplateFromUrlContent(rawContent, fallbackTitle);
              }
              if (!parsedTemplate) return null;
              const resolvedName =
                (parsedTemplate?.title || fallbackTitle || "Blog Template").trim() || "Blog Template";
              return { name: resolvedName, data: parsedTemplate };
            })
            .filter(Boolean);
          if (!cancelled && parsedEntries.length) {
            setRemoteTemplateEntries(parsedEntries);
            return;
          }
        } catch (error) {
          if (!cancelled) {
            console.warn(`Failed to fetch blog templates from ${source}`, error);
          }
        }
      }
    };
    setRemoteTemplateEntries([]);
    fetchRemoteTemplates();
    return () => {
      cancelled = true;
    };
  }, [normalizedTemplateCatalogDomain]);
  const panelStyles = useMemo(
    () =>
      panelWidths.map((width) => ({
        flexGrow: width,
        flexBasis: 0,
        flexShrink: 1,
        minWidth: 0,
      })),
    [panelWidths],
  );

  useEffect(() => {
    panelWidthsRef.current = panelWidths;
  }, [panelWidths]);

  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const handleClick = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [exportMenuOpen]);

  const activeMaximizedPanel = useMemo(() => {
    return PANEL_KEYS.find((key) => panelModes[key] === "maximized") || null;
  }, [panelModes]);

  const isDefaultPanelLayout = useMemo(
    () => PANEL_KEYS.every((key) => panelModes[key] === "normal"),
    [panelModes],
  );

  const getPanelStyle = useCallback(
    (panelKey, index) => {
      const mode = panelModes[panelKey] || "normal";
      if (mode === "collapsed") {
        if (activeMaximizedPanel && activeMaximizedPanel !== panelKey) {
          return { display: "none" };
        }
        if (isMobileLayout) {
          return { display: "none" };
        }
        const collapsedWidth = `${COLLAPSED_PANEL_WIDTH_REM}rem`;
        return {
          flex: `0 0 ${collapsedWidth}`,
          maxWidth: collapsedWidth,
          minWidth: collapsedWidth,
        };
      }
      if (isMobileLayout) {
        return {
          width: "100%",
        };
      }
      if (mode === "maximized") {
        return {
          flex: "1 1 100%",
          minWidth: 0,
        };
      }
      if (activeMaximizedPanel && activeMaximizedPanel !== panelKey) {
        return { display: "none" };
      }
      if (isDefaultPanelLayout) {
        return panelStyles[index] || { flex: "1 1 0%", minWidth: 0 };
      }
      return { flex: "1 1 0%", minWidth: 0 };
    },
    [activeMaximizedPanel, isDefaultPanelLayout, isMobileLayout, panelModes, panelStyles],
  );

  const isPanelVisible = useCallback(
    (panelKey) => {
      const mode = panelModes[panelKey] || "normal";
      if (mode === "collapsed") return false;
      if (mode === "maximized") return true;
      if (activeMaximizedPanel && activeMaximizedPanel !== panelKey) {
        return false;
      }
      return true;
    },
    [activeMaximizedPanel, panelModes],
  );

  const visiblePanels = useMemo(
    () => PANEL_KEYS.filter((key) => isPanelVisible(key)),
    [isPanelVisible],
  );

  const visiblePanelsCount = visiblePanels.length;

  const sessionTitleDisplay = (sessionTitle || "Session Outline").trim() || "Session Outline";
  const { setSessionPresentation } = useContext(SessionPresentationContext);

  useEffect(() => {
    if (typeof setSessionPresentation !== "function") return;
    setSessionPresentation({
      isPresenting,
      sessionTitle: sessionTitleDisplay,
    });
  }, [isPresenting, sessionTitleDisplay, setSessionPresentation]);

  const builderClassName = useMemo(() => {
    const classes = ["builder-content"];
    if (visiblePanelsCount <= 1) {
      classes.push("single-panel");
    }
    if (isMobileLayout) {
      classes.push("mobile-layout");
    }
    return classes.join(" ");
  }, [isMobileLayout, visiblePanelsCount]);

  const resetPanelModes = useCallback(() => {
    setPanelModes(() => ({ ...PANEL_MODE_DEFAULTS }));
    setPanelWidths([...DEFAULT_PANEL_WIDTHS]);
  }, []);

  const handleToggleCollapse = useCallback((panelKey) => {
    setPanelModes((prev) => {
      const current = prev[panelKey] || "normal";
      if (current === "collapsed") {
        const hasMaximized = Object.values(prev).some((mode) => mode === "maximized");
        if (hasMaximized) {
          setPanelWidths([...DEFAULT_PANEL_WIDTHS]);
          return { ...PANEL_MODE_DEFAULTS };
        }
        setPanelWidths([...DEFAULT_PANEL_WIDTHS]);
        return { ...prev, [panelKey]: "normal" };
      }
      if (current === "maximized") {
        setPanelWidths([...DEFAULT_PANEL_WIDTHS]);
        return { ...PANEL_MODE_DEFAULTS, [panelKey]: "collapsed" };
      }
      return { ...prev, [panelKey]: "collapsed" };
    });
  }, []);

  const handleExpandPanels = useCallback(() => {
    resetPanelModes();
  }, [resetPanelModes]);

  const handleMaximizePanel = useCallback((panelKey) => {
    setPanelModes((prev) => {
      if (prev[panelKey] === "maximized") {
        setPanelWidths([...DEFAULT_PANEL_WIDTHS]);
        return { ...PANEL_MODE_DEFAULTS };
      }
      return PANEL_KEYS.reduce((accumulator, key) => {
        accumulator[key] = key === panelKey ? "maximized" : "collapsed";
        return accumulator;
      }, {});
    });
  }, []);

  const renderCollapsedHandle = useCallback(
    (panelKey) => (
      <button
        type="button"
        className="panel-collapsed-handle"
        onClick={() => handleToggleCollapse(panelKey)}
        aria-label={`Expand ${PANEL_LABELS[panelKey]}`}
      >
        <span>{PANEL_LABELS[panelKey]}</span>
      </button>
    ),
    [handleToggleCollapse],
  );

  const renderPanelControls = useCallback(
    (panelKey) => {
      const mode = panelModes[panelKey] || "normal";
      const isCollapsed = mode === "collapsed";
      const isMaximized = mode === "maximized";
      return (
        <div className="panel-controls">
          <button
            type="button"
            onClick={() => handleToggleCollapse(panelKey)}
            aria-label={
              isCollapsed
                ? `Expand ${PANEL_LABELS[panelKey]}`
                : `Minimize ${PANEL_LABELS[panelKey]}`
            }
            className={isCollapsed ? "active" : ""}
            aria-pressed={isCollapsed}
          >
            <FaWindowMinimize />
          </button>
          <button
            type="button"
            onClick={handleExpandPanels}
            aria-label="Restore panels"
            disabled={isDefaultPanelLayout}
          >
            <FaWindowRestore />
          </button>
          <button
            type="button"
            onClick={() => handleMaximizePanel(panelKey)}
            aria-label={
              isMaximized
                ? `Exit maximize ${PANEL_LABELS[panelKey]}`
                : `Maximize ${PANEL_LABELS[panelKey]}`
            }
            className={isMaximized ? "active" : ""}
            aria-pressed={isMaximized}
          >
            {isMaximized ? <FaWindowRestore /> : <FaWindowMaximize />}
          </button>
        </div>
      );
    },
    [handleExpandPanels, handleMaximizePanel, handleToggleCollapse, isDefaultPanelLayout, panelModes],
  );

  const handlePointerMove = useCallback(
    (event) => {
      const state = resizingStateRef.current;
      if (!state || state.usableWidth <= 0) return;
      const deltaPercent =
        ((event.clientX - state.startX) / state.usableWidth) * 100;
      const minDelta = MIN_PANEL_PERCENT - state.startWidths[state.index];
      const maxDelta = state.startWidths[state.index + 1] - MIN_PANEL_PERCENT;
      const clampedDelta = Math.min(Math.max(deltaPercent, minDelta), maxDelta);
      const updated = [...state.startWidths];
      updated[state.index] = parseFloat(
        (state.startWidths[state.index] + clampedDelta).toFixed(2),
      );
      updated[state.index + 1] = parseFloat(
        (state.startWidths[state.index + 1] - clampedDelta).toFixed(2),
      );
      setPanelWidths(updated);
    },
    [setPanelWidths],
  );

  const stopResize = useCallback(() => {
    const state = resizingStateRef.current;
    if (state?.target && typeof state.target.releasePointerCapture === "function" && typeof state.pointerId === "number") {
      try {
        state.target.releasePointerCapture(state.pointerId);
      } catch {
        // ignore
      }
    }
    resizingStateRef.current = null;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", stopResize);
    if (state) {
      const collapseThreshold = MIN_PANEL_PERCENT + 0.25;
      const leftKey = PANEL_KEYS[state.index];
      const rightKey = PANEL_KEYS[state.index + 1];
      const currentWidths = panelWidthsRef.current || DEFAULT_PANEL_WIDTHS;
      const shouldCollapseLeft =
        typeof currentWidths?.[state.index] === "number" && currentWidths[state.index] <= collapseThreshold;
      const shouldCollapseRight =
        typeof currentWidths?.[state.index + 1] === "number" && currentWidths[state.index + 1] <= collapseThreshold;
      if (shouldCollapseLeft || shouldCollapseRight) {
        setPanelModes((prev) => {
          const next = { ...prev };
          let changed = false;
          if (shouldCollapseLeft && leftKey && next[leftKey] !== "collapsed") {
            next[leftKey] = "collapsed";
            changed = true;
          }
          if (shouldCollapseRight && rightKey && next[rightKey] !== "collapsed") {
            next[rightKey] = "collapsed";
            changed = true;
          }
          return changed ? next : prev;
        });
        setPanelWidths((prev) => {
          const next = [...prev];
          let updated = false;
          if (shouldCollapseLeft && leftKey) {
            if (next[state.index] !== MIN_PANEL_PERCENT) {
              next[state.index] = MIN_PANEL_PERCENT;
              updated = true;
            }
          }
          if (shouldCollapseRight && rightKey) {
            if (next[state.index + 1] !== MIN_PANEL_PERCENT) {
              next[state.index + 1] = MIN_PANEL_PERCENT;
              updated = true;
            }
          }
          return updated ? next : prev;
        });
      }
    }
  }, [handlePointerMove]);

  const startResize = useCallback(
    (index, event) => {
      if (!builderContentRef.current) return;
      const computedStyle = window.getComputedStyle(builderContentRef.current);
      if ((computedStyle.flexDirection || "row") !== "row") return;
      const gapValue =
        parseFloat(
          computedStyle.columnGap ||
            computedStyle.gap ||
            computedStyle.getPropertyValue("column-gap") ||
            "0",
        ) || 0;
      const rect = builderContentRef.current.getBoundingClientRect();
      const usableWidth = rect.width - gapValue * 2;
      if (usableWidth <= 0) return;
      event.preventDefault();
      const nativeEvent = event.nativeEvent || event;
      const pointerId = nativeEvent.pointerId;
      const target = event.currentTarget;
      if (target?.setPointerCapture && typeof pointerId === "number") {
        target.setPointerCapture(pointerId);
      }
      resizingStateRef.current = {
        index,
        startX: nativeEvent.clientX,
        startWidths: panelWidths,
        usableWidth,
        pointerId,
        target,
      };
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", stopResize);
    },
    [panelWidths, handlePointerMove, stopResize],
  );

  useEffect(() => () => stopResize(), [stopResize]);

  const handleToggleFullscreen = useCallback(() => {
    const target = fullscreenRef.current;
    if (!target) return;
    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      return;
    }
    if (target.requestFullscreen) {
      target.requestFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const handleOutlineResize = useCallback(
    (event) => {
      const state = outlineResizeStateRef.current;
      if (!state || state.height <= 0) return;
      const deltaRatio = (event.clientY - state.startY) / state.height;
      const nextRatio = Math.min(0.85, Math.max(0.3, state.startRatio + deltaRatio));
      setOutlineRatio(nextRatio);
    },
    [],
  );

  const stopOutlineResize = useCallback(() => {
    const state = outlineResizeStateRef.current;
    if (state?.target && typeof state.target.releasePointerCapture === "function" && typeof state.pointerId === "number") {
      try {
        state.target.releasePointerCapture(state.pointerId);
      } catch {
        // ignore
      }
    }
    outlineResizeStateRef.current = null;
    window.removeEventListener("pointermove", handleOutlineResize);
    window.removeEventListener("pointerup", stopOutlineResize);
  }, [handleOutlineResize]);

  const startOutlineResize = useCallback(
    (event) => {
      if (!sessionStackRef.current) return;
      const rect = sessionStackRef.current.getBoundingClientRect();
      if (rect.height <= 0) return;
      event.preventDefault();
      const nativeEvent = event.nativeEvent || event;
      const pointerId = nativeEvent.pointerId;
      const target = event.currentTarget;
      if (target?.setPointerCapture && typeof pointerId === "number") {
        target.setPointerCapture(pointerId);
      }
      outlineResizeStateRef.current = {
        startY: nativeEvent.clientY,
        height: rect.height,
        startRatio: outlineRatio,
        pointerId,
        target,
      };
      window.addEventListener("pointermove", handleOutlineResize);
      window.addEventListener("pointerup", stopOutlineResize);
    },
    [outlineRatio, handleOutlineResize, stopOutlineResize],
  );

  useEffect(() => () => stopOutlineResize(), [stopOutlineResize]);

  const postPresenterMessage = useCallback(
    (message, channelOverride) => {
      const channel = channelOverride || presenterChannelRef.current;
      if (!channel) return;
      channel.postMessage({ ...message, timestamp: Date.now() });
    },
    [],
  );

  const broadcastPresenterState = useCallback(
    (channelOverride) => {
      if (!presenterToken || isPresenterView) return;
      const channel = channelOverride || presenterChannelRef.current;
      if (!channel) return;
      postPresenterMessage({
        type: "SESSION_SYNC",
        payload: {
          sessionItems,
          currentIndex,
          sessionTitle,
          autoAdvanceEnabled,
        },
      }, channel);
    },
    [
      presenterToken,
      isPresenterView,
      sessionItems,
      currentIndex,
      sessionTitle,
      autoAdvanceEnabled,
      postPresenterMessage,
    ],
  );

  const handlePresenterInteraction = useCallback(
    (interactionPayload) => {
      if (!isPresenterView || !interactionPayload) return;
      if (interactionPayload.kind !== "pointer") return;
      const targetRoot =
        presentationContentRef.current ||
        presentationStageRef.current ||
        document.body;
      if (!targetRoot) return;
      const rect = targetRoot.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const clamp = (value) => Math.min(Math.max(value ?? 0, 0), 1);
      const clientX = rect.left + clamp(interactionPayload.x) * rect.width;
      const clientY = rect.top + clamp(interactionPayload.y) * rect.height;
      const target = document.elementFromPoint(clientX, clientY);
      if (!target) return;

      const baseInit = {
        bubbles: true,
        cancelable: true,
        clientX,
        clientY,
        buttons:
          interactionPayload.buttons ??
          (interactionPayload.eventType === "pointerdown" ? 1 : 0),
        altKey: Boolean(interactionPayload.altKey),
        ctrlKey: Boolean(interactionPayload.ctrlKey),
        metaKey: Boolean(interactionPayload.metaKey),
        shiftKey: Boolean(interactionPayload.shiftKey),
      };

      const eventType = interactionPayload.eventType || "click";
      if (eventType === "click" || eventType === "dblclick") {
        target.dispatchEvent(new MouseEvent(eventType, baseInit));
        return;
      }

      if (window.PointerEvent && eventType.startsWith("pointer")) {
        target.dispatchEvent(
          new PointerEvent(eventType, {
            ...baseInit,
            pointerType: interactionPayload.pointerType || "mouse",
          }),
        );
        return;
      }

      const fallbackType =
        eventType === "pointerdown"
          ? "mousedown"
          : eventType === "pointerup"
          ? "mouseup"
          : eventType === "pointermove"
          ? "mousemove"
          : eventType;
      target.dispatchEvent(new MouseEvent(fallbackType, baseInit));
    },
    [isPresenterView],
  );

  const shouldMirrorPreviewClicks =
    !isPresenterView && Boolean(presenterToken) && presenterConnected;

  const emitPreviewPointerEvent = useCallback(
    (eventType, event) => {
      if (!shouldMirrorPreviewClicks) return;
      const stageNode = previewStageRef.current;
      if (!stageNode) return;
      const rect = stageNode.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const clamp = (value) => Math.min(Math.max(value, 0), 1);
      const normalizedX = clamp((event.clientX - rect.left) / rect.width);
      const normalizedY = clamp((event.clientY - rect.top) / rect.height);
      const channel = presenterChannelRef.current;
      if (!channel) return;
      channel.postMessage({
        type: "SESSION_INTERACTION",
        payload: {
          kind: "pointer",
          eventType,
          x: normalizedX,
          y: normalizedY,
          buttons: event.buttons,
          pointerType: event.pointerType,
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
        },
        timestamp: Date.now(),
      });
    },
    [shouldMirrorPreviewClicks],
  );

  const handlePreviewPointerDown = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      emitPreviewPointerEvent("pointerdown", event);
    },
    [emitPreviewPointerEvent],
  );

  const handlePreviewPointerUp = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      emitPreviewPointerEvent("pointerup", event);
    },
    [emitPreviewPointerEvent],
  );

  const handlePreviewClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      emitPreviewPointerEvent("click", event);
    },
    [emitPreviewPointerEvent],
  );

  const handlePreviewDoubleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      emitPreviewPointerEvent("dblclick", event);
    },
    [emitPreviewPointerEvent],
  );

  const handlePreviewWheel = useCallback(
    (event) => {
      if (!shouldMirrorPreviewClicks || !previewStageRef.current) return;
      event.preventDefault();
      previewStageRef.current.scrollTop += event.deltaY;
    },
    [shouldMirrorPreviewClicks],
  );

  useEffect(() => {
    if (!presenterToken || typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
      return undefined;
    }
    const channel = new BroadcastChannel(`gcc-session-presenter-${presenterToken}`);
    presenterChannelRef.current = channel;

    if (isPresenterView) {
      channel.onmessage = (event) => {
        const { type, payload } = event.data || {};
        if (type === "SESSION_SYNC" && payload) {
          setPresenterConnected(true);
          const syncedItems = Array.isArray(payload.sessionItems) ? payload.sessionItems : [];
          setSessionItems(syncedItems.map((entry) => applySlideDefaults(entry)));
          const nextIndex = Math.min(
            Math.max(payload.currentIndex ?? 0, 0),
            Math.max(syncedItems.length - 1, 0)
          );
          setCurrentIndex(nextIndex);
          if (payload.sessionTitle) {
            setSessionTitle(payload.sessionTitle);
            setAutoTitle(false);
          }
          if (payload.autoAdvanceEnabled !== undefined) {
            autoAdvanceSyncRef.current.isRemoteUpdate = true;
            setAutoAdvanceEnabled(Boolean(payload.autoAdvanceEnabled));
          }
        } else if (type === "SESSION_INTERACTION" && payload) {
          handlePresenterInteraction(payload);
        } else if (type === "SESSION_CONTROL" && payload) {
          if (payload.action === "AUTO_ADVANCE_SYNC") {
            autoAdvanceSyncRef.current.isRemoteUpdate = true;
            setAutoAdvanceEnabled(Boolean(payload.value));
          }
        }
      };
      channel.postMessage({ type: "PRESENTER_READY" });
    } else {
      channel.onmessage = (event) => {
        const message = event.data;
        if (message?.type === "PRESENTER_READY") {
          setPresenterConnected(true);
          broadcastPresenterState(channel);
        } else if (message?.type === "SESSION_CONTROL" && message.payload) {
          if (message.payload.action === "AUTO_ADVANCE_SYNC") {
            autoAdvanceSyncRef.current.isRemoteUpdate = true;
            setAutoAdvanceEnabled(Boolean(message.payload.value));
          }
        }
      };
    }

    return () => {
      channel.close();
      if (presenterChannelRef.current === channel) {
        presenterChannelRef.current = null;
      }
    };
  }, [
    presenterToken,
    isPresenterView,
    broadcastPresenterState,
    handlePresenterInteraction,
    setAutoTitle,
    setPresenterConnected,
    setSessionItems,
    setCurrentIndex,
    setSessionTitle,
    setAutoAdvanceEnabled,
  ]);

  const importSession = useCallback((sessionData, options = {}) => {
    const { preserveTemplateSelection = false } = options;
    try {
      setImportError("");
      if (!sessionData || !Array.isArray(sessionData.sequence)) {
        throw new Error("Invalid session format");
      }
      setAutoTitle(Boolean(sessionData.autoTitle));
      setSessionTitle(sessionData.title || deriveAutoTitle(sessionData.sequence));
      setSessionItems(
        sessionData.sequence.map((entry) => buildSessionItemFromConfigEntry(entry))
      );
      setCurrentIndex(0);
      if (!preserveTemplateSelection) {
        setLoadedTemplateName("");
      }
    } catch (error) {
      setImportError(error.message);
    }
  }, [setLoadedTemplateName]);

  const loadBlogspotFeed = useCallback((domain, maxResults = 200) => {
    const cacheKey = `${domain}|${maxResults}`;
    const cached = blogspotFeedCacheRef.current[cacheKey];
    if (cached) return Promise.resolve(cached);
    if (typeof window === "undefined" || typeof document === "undefined") {
      return Promise.reject(
        new Error("Blog feed is not available in this environment"),
      );
    }
    return new Promise((resolve, reject) => {
      const callbackName = `gccBlogFeed_${Date.now()}_${Math.random()
        .toString(16)
        .slice(2)}`;
      const script = document.createElement("script");
      const cleanup = () => {
        delete window[callbackName];
        if (script.parentNode) script.parentNode.removeChild(script);
      };
      window[callbackName] = (data) => {
        blogspotFeedCacheRef.current[cacheKey] = data;
        cleanup();
        resolve(data);
      };
      script.onerror = () => {
        cleanup();
        reject(new Error(`Unable to load feed from ${domain}`));
      };
      script.src = `https://${domain}/feeds/posts/default?alt=json-in-script&max-results=${maxResults}&callback=${callbackName}`;
      document.body.appendChild(script);
    });
  }, []);

  const bibleBookOptions = useMemo(
    () =>
      BIBLE_BOOKS.map((book, index) => ({
        value: String(index),
        label: book.name,
        chapters: book.chapters,
      })),
    []
  );

  const selectedBookIndex = useMemo(() => {
    const parsed = parseInt(bibleSelection.book, 10);
    if (Number.isNaN(parsed)) return 0;
    if (parsed < 0) return 0;
    if (parsed >= BIBLE_BOOKS.length) return BIBLE_BOOKS.length - 1;
    return parsed;
  }, [bibleSelection.book]);

  const bibleChapterOptions = useMemo(() => {
    const chapters = BIBLE_BOOKS[selectedBookIndex]?.chapters || 1;
    return Array.from({ length: chapters }, (_, index) => String(index + 1));
  }, [selectedBookIndex]);

  useEffect(() => {
    const maxChapter = BIBLE_BOOKS[selectedBookIndex]?.chapters || 1;
    const parsed = parseInt(bibleSelection.chapter, 10);
    if (Number.isNaN(parsed) || parsed < 1) {
      setBibleSelection((prev) => ({ ...prev, chapter: "1" }));
      return;
    }
    if (parsed > maxChapter) {
      setBibleSelection((prev) => ({ ...prev, chapter: String(maxChapter) }));
    }
  }, [bibleSelection.chapter, selectedBookIndex]);

  const updateBibleSelection = useCallback((key, value) => {
    setBibleSelection((prev) => ({ ...prev, [key]: value }));
  }, []);

  const versionOptionsWithFallback = useMemo(() => {
    const map = new Map();
    BIBLE_VERSION_CHOICES.forEach((option) => {
      if (!option?.value) return;
      map.set(option.value, {
        value: option.value,
        label: option.label || option.value,
      });
    });
    bibleSelectionOptions.versions.forEach((option) => {
      if (!option?.value) return;
      const label = option.label?.trim() || option.value;
      map.set(option.value, { value: option.value, label });
    });
    const combined = Array.from(map.values());
    return combined.length ? combined : BIBLE_VERSION_CHOICES;
  }, [bibleSelectionOptions.versions]);

  const languageOptionsWithFallback = useMemo(() => {
    if (bibleSelectionOptions.languages.length) {
      return bibleSelectionOptions.languages;
    }
    return [
      {
        value: DEFAULT_BIBLE_SELECTION.language,
        label: "English",
        selected: true,
      },
    ];
  }, [bibleSelectionOptions.languages]);

  const addToSession = useCallback((item) => {
    setSessionItems((prev) => {
      const instanceId = safeId("session-item");
      let nextItem = applySlideDefaults({
        ...item,
        instanceId,
        notes: item.notes || "",
      });
      if (nextItem.type === "presentationRemote") {
        const settings = parsePresentationRemoteSettings(
          nextItem.embedUrl || nextItem.originalUrl || "",
          presentationCatalogUrl,
        );
        const fileParts = splitPresentationFile(settings.file);
        const fallbackFile = fileParts.file || settings.presentation || "";
        nextItem = {
          ...nextItem,
          presentTopic: nextItem.presentTopic ?? fileParts.topic,
          presentFile: nextItem.presentFile ?? fallbackFile,
        };
      }
      const updated = [...prev, nextItem];
      setSelectedItemId(instanceId);
      setCurrentIndex(updated.length - 1);
      return updated;
    });
  }, [presentationCatalogUrl]);

  const queueSessionItem = useCallback(
    (item, options = {}) => {
      if (!item) return;
      if (options.skipBackgroundMusic) {
        setBackgroundMusicFormError("");
        addToSession({ ...item });
        return;
      }
      try {
        const music = resolveBackgroundMusicSelection(
          backgroundMusicSelection,
          backgroundPlaylistItems,
        );
        setBackgroundMusicFormError("");
        const nextItem = { ...item };
        if (music) {
          nextItem.backgroundMusic = music;
        }
        addToSession(nextItem);
      } catch (error) {
        setBackgroundMusicFormError(error?.message || "Unable to add background music.");
      }
    },
    [addToSession, backgroundMusicSelection, backgroundPlaylistItems],
  );

  const toggleBackgroundMusic = useCallback(() => {
    setBackgroundMusicEnabled((prev) => !prev);
  }, []);

  const sendYoutubeCommand = useCallback((iframe, command) => {
    if (!iframe || !iframe.contentWindow) return;
    try {
      const message = JSON.stringify({
        event: "command",
        func: command,
        args: [],
      });
      let targetOrigin = "https://www.youtube.com";
      try {
        const parsed = new URL(iframe.src);
        targetOrigin = `${parsed.protocol}//${parsed.host}`;
      } catch (error) {
        // fallback to default origin
      }
      iframe.contentWindow.postMessage(message, targetOrigin);
    } catch (error) {
      // Ignore cross-origin command errors
    }
  }, []);

  const registerYoutubeIframe = useCallback((iframe) => {
    if (!iframe || !iframe.contentWindow) return;
    try {
      const targetOrigin = (() => {
        try {
          const parsed = new URL(iframe.src);
          return `${parsed.protocol}//${parsed.host}`;
        } catch (error) {
          return "https://www.youtube.com";
        }
      })();
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "listening", id: "preview-player" }),
        targetOrigin,
      );
      iframe.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: "addEventListener",
          args: ["onStateChange"],
        }),
        targetOrigin,
      );
      iframe.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: "addEventListener",
          args: ["infoDelivery"],
        }),
        targetOrigin,
      );
      iframe.contentWindow.postMessage(
        JSON.stringify({
          event: "command",
          func: "getPlayerState",
        }),
        targetOrigin,
      );
    } catch {
      // ignore registration errors
    }
  }, []);

  const resolveCustomMedia = useCallback((draft) => {
    const parsed = new URL(draft.url);
    const host = parsed.hostname.toLowerCase();
    let resolvedType = "externalPage";

    if (draft.mode === "video") {
      resolvedType = "externalVideo";
    } else if (draft.mode === "webpage") {
      resolvedType = "externalPage";
    } else if (
      draft.mode === "auto" &&
      (host.includes("youtube.com") || host.includes("youtu.be") || host.includes("vimeo.com"))
    ) {
      resolvedType = "externalVideo";
    }

    let embedUrl = parsed.toString();

    if (resolvedType === "externalVideo") {
      if (host.includes("youtube.com")) {
        const videoId =
          parsed.searchParams.get("v") ||
          parsed.searchParams.get("vi") ||
          parsed.pathname
            .split("/")
            .filter(Boolean)
            .pop();
        if (videoId) {
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        }
      } else if (host.includes("youtu.be")) {
        const videoId = parsed.pathname
          .split("/")
          .filter(Boolean)
          .pop();
        if (videoId) {
          embedUrl = `https://www.youtube.com/embed/${videoId}`;
        }
      } else if (host.includes("vimeo.com")) {
        const match = parsed.pathname.match(/\/(\d+)/);
        if (match?.[1]) {
          embedUrl = `https://player.vimeo.com/video/${match[1]}`;
        }
      }
    }

    return {
      type: resolvedType,
      embedUrl,
      originalUrl: parsed.toString(),
    };
  }, []);

  const handleCreateCustomMedia = useCallback(() => {
    const title = customMediaDraft.title.trim();
    const url = customMediaDraft.url.trim();
    const description = customMediaDraft.description.trim();

    if (!title || !url) {
      setCustomMediaError("Title and URL are required.");
      return;
    }

    try {
      const resolved = resolveCustomMedia({ ...customMediaDraft, url });
      const item = {
        sourceId: safeId("custom-media"),
        type: resolved.type,
        title,
        description,
        embedUrl: resolved.embedUrl,
        originalUrl: resolved.originalUrl,
      };

      setCustomMediaItems((prev) => [item, ...prev]);
      setCustomMediaDraft((prev) => ({
        ...prev,
        title: "",
        url: "",
        description: "",
      }));
      setCustomMediaError("");
      queueSessionItem(item);
    } catch (error) {
      setCustomMediaError("Enter a valid URL.");
    }
  }, [customMediaDraft, queueSessionItem, resolveCustomMedia]);

  const removeCustomMediaItem = useCallback((sourceId) => {
    setCustomMediaItems((prev) => prev.filter((item) => item.sourceId !== sourceId));
  }, []);

  const getCatalogPreviewEmbed = useCallback((item) => {
    if (!item) return "";
    if (item.embedUrl) return item.embedUrl;
    if (item.videoId) return buildYoutubeEmbedUrl(item.videoId);
    if (item.originalUrl) {
      try {
        const resolved = createBackgroundMusicFromUrl(item.originalUrl, { mode: "custom" });
        if (resolved?.embedUrl) {
          return resolved.embedUrl;
        }
      } catch (error) {
        return "";
      }
    }
    return "";
  }, []);

  const handleToggleCatalogPreview = useCallback(
    (item, explicitEmbedUrl, fallbackContent, previewItem) => {
      if (!item) return;
      const embedUrl = explicitEmbedUrl || getCatalogPreviewEmbed(item);
      const hasContent = Boolean(embedUrl) || Boolean(fallbackContent) || Boolean(previewItem);
      if (!hasContent) return;
      setCatalogPreview((prev) => {
        if (prev?.sourceId === item.sourceId) {
          return null;
        }
        return {
          sourceId: item.sourceId,
          title: item.title || "Preview",
          embedUrl: embedUrl || "",
          fallbackContent: fallbackContent || "",
          previewItem: previewItem ? applySlideDefaults(previewItem) : null,
        };
      });
    },
    [getCatalogPreviewEmbed],
  );

  const buildBibleSelectionItem = useCallback(() => {
    const bookIndex = selectedBookIndex;
    const book = BIBLE_BOOKS[bookIndex];
    const chapter = bibleSelection.chapter || DEFAULT_BIBLE_SELECTION.chapter;
    const verse = (bibleSelection.verse || "").trim();
    const version = bibleSelection.version || DEFAULT_BIBLE_SELECTION.version;
    const language = bibleSelection.language || DEFAULT_BIBLE_SELECTION.language;
    const page = bibleSelection.page || DEFAULT_BIBLE_SELECTION.page;
    const lines = bibleSelection.lines || DEFAULT_BIBLE_SELECTION.lines;
    const wpm = bibleSelection.wpm || DEFAULT_BIBLE_SELECTION.wpm;

    const selectionUrl = new URL(bibleCatalogUrl);
    selectionUrl.searchParams.set("book", String(bookIndex));
    selectionUrl.searchParams.set("chapter", chapter);
    selectionUrl.searchParams.set("version", version);
    selectionUrl.searchParams.set("language", language);
    selectionUrl.searchParams.set("page", page);
    selectionUrl.searchParams.set("lines", lines);
    selectionUrl.searchParams.set("wpm", wpm);
    if (verse) {
      selectionUrl.searchParams.set("verses", verse);
      selectionUrl.searchParams.set("verse", verse);
    } else {
      selectionUrl.searchParams.delete("verses");
      selectionUrl.searchParams.delete("verse");
    }

    const versionLabel =
      versionOptionsWithFallback.find((option) => option.value === version)?.label || version;
    const languageLabel =
      languageOptionsWithFallback.find((option) => option.value === language)?.label || language;

    const reference = verse
      ? `${book?.name || "Bible"} ${chapter}:${verse}`
      : `${book?.name || "Bible"} ${chapter}`;

    return {
      sourceId: `bible-custom-${bookIndex}-${chapter}-${verse || "full"}`,
      type: "bibleRemote",
      title: reference,
      description: `${versionLabel} • ${languageLabel}`,
      reference,
      embedUrl: selectionUrl.toString(),
      content: null,
    };
  }, [
    bibleSelection.chapter,
    bibleSelection.language,
    bibleSelection.lines,
    bibleSelection.page,
    bibleSelection.version,
    bibleSelection.verse,
    bibleSelection.wpm,
    bibleCatalogUrl,
    languageOptionsWithFallback,
    selectedBookIndex,
    versionOptionsWithFallback,
  ]);

  const currentBibleSelectionItem = useMemo(() => buildBibleSelectionItem(), [buildBibleSelectionItem]);

  const handleAddBibleSelection = useCallback(() => {
    if (!currentBibleSelectionItem) return;
    queueSessionItem(currentBibleSelectionItem);
  }, [currentBibleSelectionItem, queueSessionItem]);

  const handlePreviewBibleSelection = useCallback(() => {
    if (!currentBibleSelectionItem?.embedUrl) return;
    handleToggleCatalogPreview(currentBibleSelectionItem, currentBibleSelectionItem.embedUrl);
  }, [currentBibleSelectionItem, handleToggleCatalogPreview]);

  const handleOpenBibleSelectionLink = useCallback(() => {
    if (!currentBibleSelectionItem?.embedUrl) return;
    if (typeof window === "undefined") return;
    window.open(currentBibleSelectionItem.embedUrl, "_blank", "noopener,noreferrer");
  }, [currentBibleSelectionItem]);


  const setCatalogLoadingState = useCallback((key, value) => {
    setCatalogLoading((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setCatalogErrorState = useCallback((key, value) => {
    setCatalogError((prev) => ({ ...prev, [key]: value }));
  }, []);

  const fetchYoutubeCatalog = useCallback(async () => {
    setCatalogLoadingState("youtube", true);
    setCatalogErrorState("youtube", "");

    try {
      const configResult = buildYoutubeItemsFromConfig();
      if (configResult.items.length) {
        setYoutubeChannelItems(sortYoutubeCatalogItems(configResult.items));
        setYoutubeCatalogPlaylists(normalizePlaylistOptions(configResult.playlists));
        setYoutubeCatalogPlaylistFilter("all");
        return;
      }

      const metadataResult = buildYoutubeItemsFromVideosMetadata();
      if (metadataResult.items.length) {
        setYoutubeChannelItems(sortYoutubeCatalogItems(metadataResult.items));
        setYoutubeCatalogPlaylists(normalizePlaylistOptions(metadataResult.playlists));
        setYoutubeCatalogPlaylistFilter("all");
        setCatalogErrorState(
          "youtube",
          "Showing videos from local archive; YouTube configuration unavailable.",
        );
      } else {
        setYoutubeChannelItems([]);
        setYoutubeCatalogPlaylists([]);
        setYoutubeCatalogPlaylistFilter("all");
        setCatalogErrorState("youtube", "No YouTube videos available.");
      }
    } catch (error) {
      setYoutubeChannelItems([]);
      setYoutubeCatalogPlaylists([]);
      setYoutubeCatalogPlaylistFilter("all");
      setCatalogErrorState("youtube", error?.message || "Unable to load YouTube songs.");
    } finally {
      setCatalogLoadingState("youtube", false);
    }
  }, [
    setCatalogErrorState,
    setCatalogLoadingState,
    setYoutubeCatalogPlaylistFilter,
    setYoutubeCatalogPlaylists,
    setYoutubeChannelItems,
  ]);

  const parseRemoteHtml = useCallback(async (url) => {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = setTimeout(() => {
      if (controller) {
        controller.abort();
      }
    }, REMOTE_FETCH_TIMEOUT_MS);

    try {
      const options = controller
        ? { mode: "cors", signal: controller.signal }
        : { mode: "cors" };
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error(`Unable to load content from ${url}`);
      }

      const text = await response.text();
      const parser = new DOMParser();
      return parser.parseFromString(text, "text/html");
    } catch (error) {
      if (error?.name === "AbortError") {
        throw new Error(
          `Request to ${url} timed out. Please try again later.`,
        );
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  const fetchWorshipCatalog = useCallback(async () => {
    const domain = normalizedTemplateCatalogDomain || DEFAULT_WORSHIP_DOMAIN;
    setCatalogLoadingState("worship", true);
    setCatalogErrorState("worship", "");

    try {
      const feed = await loadBlogspotFeed(domain, 200);
      const entries = feed?.feed?.entry || [];

      if (!entries.length) {
        throw new Error("No songs detected in the worship catalog feed");
      }

      const seenSlugs = new Set();
      const items = entries
        .map((entry, index) => {
          const title = entry.title?.$t || `Song ${index + 1}`;
          const baseSlug = slugify(title) || `song-${index}`;
          let slug = baseSlug;
          let attempt = 1;
          while (seenSlugs.has(slug)) {
            slug = `${baseSlug}-${attempt++}`;
          }
          seenSlugs.add(slug);
          const contentHtml = entry.content?.$t || "";
          const summary = entry.summary?.$t || "";
          const descriptionSource = extractPlainText(summary || contentHtml);
          const description = descriptionSource
            ? descriptionSource.length > 200
              ? `${descriptionSource.slice(0, 197)}…`
              : descriptionSource
            : "Song from GCC Worship classic slide library.";
          const categories = (entry.category || [])
            .map((cat) => (typeof cat.term === "string" ? cat.term.trim() : ""))
            .filter(Boolean);
          const normalizedCategories = categories.map((cat) => cat.toLowerCase());
          if (normalizedCategories.includes("session")) {
            return null;
          }
          const reference = categories.length ? categories.join(", ") : null;
          const thumbnail = extractFirstImageSrc(contentHtml);
          const alternateLink = (entry.link || []).find((lnk) => lnk.rel === "alternate")?.href || null;

        return {
          sourceId: `worship-catalog-${domain}-${slug}`,
          type: "worshipRemote",
          title,
          description,
          embedUrl: buildWorshipEmbedUrl(slug, domain),
          reference,
          thumbnail: thumbnail || undefined,
          originalUrl: alternateLink || undefined,
        };
      })
        .filter(Boolean);

      setWorshipCatalogItems(items);
    } catch (error) {
      setCatalogErrorState("worship", error.message || "Unable to load worship catalog");
      setWorshipCatalogItems([]);
    } finally {
      setCatalogLoadingState("worship", false);
    }
  }, [
    loadBlogspotFeed,
    normalizedTemplateCatalogDomain,
    setCatalogErrorState,
    setCatalogLoadingState,
  ]);

  const fetchBibleCatalog = useCallback(async () => {
    setCatalogLoadingState("bible", true);
    setCatalogErrorState("bible", "");

    try {
      const document = await parseRemoteHtml(bibleCatalogUrl);
      const base = new URL(bibleCatalogUrl);

      const parseSelectOptions = (element) => {
        if (!element) return [];
        return Array.from(element.querySelectorAll("option")).map((option) => {
          const value = option.value || option.getAttribute("value") || option.textContent.trim();
          const label = (option.textContent || value || "").replace(/\s+/g, " ").trim();
          const selected = option.selected || element.value === value;
          return {
            value,
            label,
            selected,
          };
        }).filter((option) => option.value !== undefined && option.value !== null && option.value !== "");
      };

      const coerceFieldValue = (selector, fallback) => {
        const element = document.querySelector(selector);
        if (!element) return fallback;
        if (element.tagName === "SELECT") {
          const options = parseSelectOptions(element);
          const selectedOption = options.find((entry) => entry.selected) || options[0];
          return selectedOption?.value ?? fallback;
        }
        const value = element.value || element.getAttribute("value");
        return value !== undefined && value !== null && value !== "" ? value : fallback;
      };

      const versionOptions = parseSelectOptions(
        document.querySelector("select[name='version'], select#version, select[data-version]")
      );
      const languageOptions = parseSelectOptions(
        document.querySelector("select[name='language'], select#language, select[data-language]")
      );

      const verseNodes = Array.from(
        new Set([
          ...document.querySelectorAll("[data-verse]"),
          ...document.querySelectorAll("[data-ref]"),
          ...document.querySelectorAll("a[href*='verse=']"),
          ...document.querySelectorAll("button[data-verse]"),
        ])
      );

      const verses = [];

      verseNodes.forEach((node, index) => {
        const datasetVerse = node.getAttribute("data-verse") || node.getAttribute("data-ref");
        const href = node.getAttribute("href");
        let verseKey = datasetVerse || "";
        let contentText = (node.textContent || "").replace(/\s+/g, " ").trim();

        if (!verseKey && href) {
          try {
            const parsedHref = new URL(href, base);
            verseKey =
              parsedHref.searchParams.get("verse") ||
              parsedHref.searchParams.get("reference") ||
              parsedHref.searchParams.get("ref");
          } catch (error) {
            // Ignore invalid URLs
          }
        }

        if (!contentText) {
          const dataText = node.getAttribute("data-text");
          if (dataText) {
            contentText = dataText;
          }
        }

        if (!contentText) return;

        const verseUrl = new URL(bibleCatalogUrl);
        if (verseKey) {
          verseUrl.searchParams.set("reference", verseKey);
        }

        const title = normalizeBibleCatalogTitle(verseKey || `Passage ${index + 1}`);

        verses.push({
          sourceId: `bible-catalog-${verseKey || index}`,
          type: "bibleRemote",
          title,
          description: "Verse from GCC Bible reader.",
          content:
            contentText.length > 480 ? `${contentText.slice(0, 477)}…` : contentText,
          reference: verseKey || undefined,
          embedUrl: verseUrl.toString(),
        });
      });

      const uniqueVerses = [];
      const seen = new Set();

      verses.forEach((verse) => {
        const key = verse.reference || verse.content.slice(0, 60);
        if (seen.has(key)) return;
        seen.add(key);
        uniqueVerses.push(verse);
      });

      if (!uniqueVerses.length) {
        const defaultContent = (document.querySelector("main") || document.body).textContent || "";
        const trimmed = defaultContent.replace(/\s+/g, " ").trim();
        uniqueVerses.push({
          sourceId: "bible-catalog-default",
          type: "bibleRemote",
          title: normalizeBibleCatalogTitle(document.title || "Bible Passage"),
          description: "Full passage from GCC Bible reader.",
          content: trimmed.slice(0, 500),
          reference: document.title,
          embedUrl: bibleCatalogUrl,
        });
      }

      const pageValue = coerceFieldValue(
        "select[name='page'], input[name='page'], input[data-page]",
        DEFAULT_BIBLE_SELECTION.page
      );
      const linesValue = coerceFieldValue(
        "select[name='lines'], input[name='lines'], input[data-lines]",
        DEFAULT_BIBLE_SELECTION.lines
      );
      const wpmValue = coerceFieldValue(
        "select[name='wpm'], input[name='wpm'], input[data-wpm]",
        DEFAULT_BIBLE_SELECTION.wpm
      );

      setBibleSelectionOptions({
        versions: versionOptions,
        languages: languageOptions,
      });

      setBibleSelection((prev) => {
        const ensureValue = (options, current, fallback) => {
          if (!options.length) return current || fallback;
          if (options.some((option) => option.value === current)) return current;
          const selectedOption = options.find((option) => option.selected);
          return selectedOption?.value || options[0].value || fallback;
        };

        return {
          ...prev,
          version: ensureValue(versionOptions, prev.version, DEFAULT_BIBLE_SELECTION.version),
          language: ensureValue(languageOptions, prev.language, DEFAULT_BIBLE_SELECTION.language),
          page: pageValue || DEFAULT_BIBLE_SELECTION.page,
          lines: linesValue || DEFAULT_BIBLE_SELECTION.lines,
          wpm: wpmValue || DEFAULT_BIBLE_SELECTION.wpm,
        };
      });

      setBibleCatalogItems(uniqueVerses);
    } catch (error) {
      setCatalogErrorState("bible", error.message || "Unable to load Bible catalog");
      setBibleCatalogItems([]);
    } finally {
      setCatalogLoadingState("bible", false);
    }
  }, [
    bibleCatalogUrl,
    parseRemoteHtml,
    setCatalogErrorState,
    setCatalogLoadingState,
  ]);

  const fetchPresentCatalog = useCallback(async () => {
    setCatalogLoadingState("present", true);
    setCatalogErrorState("present", "");

    try {
      const document = await parseRemoteHtml(presentationCatalogUrl);
      const base = new URL(presentationCatalogUrl);
      const deckNodes = Array.from(
        new Set([
          ...document.querySelectorAll("[data-presentation]"),
          ...document.querySelectorAll("a[href*='presentation=']"),
          ...document.querySelectorAll("button[data-presentation]"),
          ...document.querySelectorAll("article"),
        ])
      );

      const decks = [];
      const seenKeys = new Set();
      const registerDeck = (deck, keyHint) => {
        const key = keyHint || deck.sourceId;
        if (!deck || !deck.title || !deck.embedUrl) return;
        if (key && seenKeys.has(key)) return;
        if (key) seenKeys.add(key);
        decks.push(deck);
      };

      deckNodes.forEach((node, index) => {
        const datasetDeck = node.getAttribute("data-presentation");
        const href = node.getAttribute("href");
        let deckId = datasetDeck || "";

        if (!deckId && href) {
          try {
            const parsed = new URL(href, base);
            deckId =
              parsed.searchParams.get("presentation") ||
              parsed.searchParams.get("deck") ||
              parsed.searchParams.get("id");
          } catch (error) {
            // Ignore invalid URLs
          }
        }

        const rawTitle =
          (node.querySelector("h1, h2, h3")?.textContent || node.textContent || "")
            .replace(/\s+/g, " ")
            .trim() ||
          deckId ||
          `Deck ${index + 1}`;

        if (!rawTitle) return;

        const normalizedSlug = slugify(deckId || rawTitle);
        const embedUrl = new URL(presentationCatalogUrl);
        if (deckId) {
          embedUrl.searchParams.set("presentation", deckId);
        }
        embedUrl.searchParams.set("mode", "play");
        embedUrl.searchParams.set("tiles", "0");
        embedUrl.searchParams.set("controls", "0");
        embedUrl.searchParams.set("disableLoop", "1");

        registerDeck(
          {
            sourceId: `present-catalog-${normalizedSlug || index}`,
            type: "presentationRemote",
            title: prettifyTitle(rawTitle) || rawTitle,
            description: "Deck from GCC Present library.",
            embedUrl: embedUrl.toString(),
            originalUrl: embedUrl.toString(),
            presentTopic: "",
            presentFile: deckId || normalizedSlug || rawTitle,
            disableLoop: true,
          },
          normalizedSlug || deckId || rawTitle
        );
      });

      const tileNodes = Array.from(document.querySelectorAll(".tile-item"));
      tileNodes.forEach((tile, tileIndex) => {
        const filePath = tile.getAttribute("data-file-path") || "";
        const fileBase = tile.getAttribute("data-file-base") || "";
        const folder = tile.getAttribute("data-folder") || "";
        const baseText = (fileBase || tile.textContent || "").replace(/\s+/g, " ").trim();
        if (!baseText) return;
        const querySlug =
          tile.getAttribute("data-file-slug") || slugify(baseText);
        const pathSlug = slugify(filePath || `${folder}-${baseText}`);
        const title = prettifyTitle(baseText) || baseText;
        const embedUrl = new URL(presentationCatalogUrl);
        if (querySlug) {
          embedUrl.searchParams.set("file", querySlug);
        }
        embedUrl.searchParams.set("mode", "play");
        embedUrl.searchParams.set("tiles", "0");
        embedUrl.searchParams.set("controls", "0");
        embedUrl.searchParams.set("disableLoop", "1");
        const description = folder
          ? `Topic deck from ${prettifyTitle(folder)} collection.`
          : "Topic deck from Present library.";

        registerDeck(
          {
            sourceId: `present-catalog-${pathSlug || querySlug || safeId(
              "present-tile",
            )}`,
            type: "presentationRemote",
            title,
            description,
            embedUrl: embedUrl.toString(),
            originalUrl: embedUrl.toString(),
            presentTopic: folder,
            presentFile: filePath || querySlug || pathSlug || "",
            disableLoop: true,
          },
          pathSlug || querySlug || `${baseText}-${tileIndex}`,
        );
      });

      setPresentCatalogItems(decks);
    } catch (error) {
      setCatalogErrorState("present", error.message || "Unable to load Present library");
      setPresentCatalogItems([]);
    } finally {
      setCatalogLoadingState("present", false);
    }
  }, [
    parseRemoteHtml,
    presentationCatalogUrl,
    setCatalogErrorState,
    setCatalogLoadingState,
  ]);

  const customSourceGroup = useMemo(
    () => ({
      key: "custom-external",
      label: "Custom Media",
      data: customMediaItems,
      icon: <FaExternalLinkAlt />,
      description: "Embed external webpages or videos into your session.",
    }),
    [customMediaItems]
  );

  const dynamicSourceGroups = useMemo(
    () => [
      {
        key: "youtube-dynamic",
        label: "YouTube Songs",
        data: youtubeChannelItems,
        playlists: youtubeCatalogPlaylists,
        icon: <FaYoutube />,
        description: "Curated GCC Worship playlists and videos.",
        catalogKey: "youtube",
      },
      {
        key: "worship-catalog",
        label: "Worship Catalog",
        data: worshipCatalogItems,
        icon: <FaPlay />,
        description: "Classic GCC Worship slides and medleys.",
        catalogKey: "worship",
      },
      {
        key: "bible-catalog",
        label: "Bible Library",
        data: bibleCatalogItems,
        icon: <FaBook />,
        description: "Curated verses from GCC Bible reader.",
        catalogKey: "bible",
      },
      {
        key: "present-catalog",
        label: "Present Decks",
        data: presentCatalogItems,
        icon: <FaImages />,
        description: "Pull decks from GCC Present slideshow.",
        catalogKey: "present",
      },
    ],
    [
      bibleCatalogItems,
      presentCatalogItems,
      worshipCatalogItems,
      youtubeCatalogPlaylists,
      youtubeChannelItems,
    ]
  );

  const sourceGroups = useMemo(
    () => [customSourceGroup, ...BASE_SOURCE_GROUPS, ...dynamicSourceGroups],
    [customSourceGroup, dynamicSourceGroups]
  );

  const catalogMeta = useMemo(
    () => ({
      youtube: {
        loading: catalogLoading.youtube,
        error: catalogError.youtube,
        refresh: fetchYoutubeCatalog,
        helperUrl: "https://www.youtube.com/@gcc-worship",
      },
      worship: {
        loading: catalogLoading.worship,
        error: catalogError.worship,
        refresh: fetchWorshipCatalog,
        helperUrl: WORSHIP_CATALOG_HELPER_URL,
      },
      bible: {
        loading: catalogLoading.bible,
        error: catalogError.bible,
        refresh: fetchBibleCatalog,
        helperUrl: bibleCatalogUrl,
      },
      present: {
        loading: catalogLoading.present,
        error: catalogError.present,
        refresh: fetchPresentCatalog,
        helperUrl: presentationCatalogUrl,
      },
    }),
    [
      bibleCatalogUrl,
      catalogError.bible,
      catalogError.present,
      catalogError.worship,
      catalogError.youtube,
      catalogLoading.bible,
      catalogLoading.present,
      catalogLoading.worship,
      catalogLoading.youtube,
      fetchBibleCatalog,
      fetchPresentCatalog,
      fetchWorshipCatalog,
      fetchYoutubeCatalog,
      presentationCatalogUrl,
    ]
  );

  const sessionConfig = useMemo(
    () => ({
      title: sessionTitle,
      autoTitle,
      sequence: sessionItems.map((item) => buildSessionConfigEntryFromItem(item)),
    }),
    [autoTitle, sessionItems, sessionTitle]
  );

  const liveSessionJson = useMemo(
    () => JSON.stringify(sessionConfig, null, 2),
    [sessionConfig],
  );

  const sessionOutlineEntries = useMemo(
    () =>
      sessionItems.map((item, index) => ({
        heading: `${index + 1}. ${item.title || "Untitled Item"}`,
        type: formatTypeLabel(item.type),
        description: item.description ? String(item.description) : "",
        reference: item.reference ? String(item.reference) : "",
        content: item.content ? String(item.content) : "",
        notes: item.notes ? String(item.notes) : "",
        songs: Array.isArray(item.songs) ? item.songs : [],
        slides: Array.isArray(item.slides) ? item.slides : [],
        background: item.backgroundMusic ? describeBackgroundMusic(item.backgroundMusic) : "",
        originalUrl: item.originalUrl || "",
        embedUrl: item.embedUrl || "",
        durationMinutes: sanitizeDurationMinutes(item.durationMinutes),
      })),
    [sessionItems],
  );

  const backgroundPlaylistMap = useMemo(() => {
    const map = new Map();
    backgroundPlaylistItems.forEach((entry) => {
      if (entry.videoId) {
        map.set(entry.videoId, entry.title || entry.videoId);
      }
    });
    return map;
  }, [backgroundPlaylistItems]);

  const autoGeneratedTitle = useMemo(
    () => deriveAutoTitle(sessionItems),
    [sessionItems]
  );

  const activeGroupConfig = useMemo(() => {
    const group = sourceGroups.find((entry) => entry.key === activeGroup) || null;
    const meta = group?.catalogKey ? catalogMeta[group.catalogKey] : null;
    return { group, meta };
  }, [activeGroup, catalogMeta, sourceGroups]);

  const [catalogSearchTerms, setCatalogSearchTerms] = useState({});
  const activeGroupData = activeGroupConfig.group;
  const activeCatalogKey = activeGroupData?.catalogKey || activeGroupData?.key || "";
  const isBibleLibrary = activeGroupData?.key === "bible-catalog";
  const isPresentLibrary = activeGroupData?.key === "present-catalog";
  const describePresentTopic = useCallback((topic) => {
    if (!topic || topic === "featured") {
      return "Featured";
    }
    return prettifyTitle(topic);
  }, []);

  const extractPresentDeckSettings = useCallback(
    (embedUrl) => {
      const defaults = {
        mode: "Play",
        tiles: "Hidden",
        controls: "Hidden",
        descriptions: "Off",
      };
      if (!embedUrl) return defaults;
      try {
        const parsed = new URL(embedUrl, presentationCatalogUrl);
        const params = parsed.searchParams;
        const normalizeToggle = (value, onLabel = "On", offLabel = "Off") =>
          value === "1" ? onLabel : offLabel;
        return {
          mode: prettifyTitle(params.get("mode") || defaults.mode),
          tiles: normalizeToggle(params.get("tiles"), "Visible", "Hidden"),
          controls: normalizeToggle(params.get("controls"), "Visible", "Hidden"),
          descriptions: normalizeToggle(params.get("descriptions"), "On", "Off"),
        };
      } catch (error) {
        return defaults;
      }
    },
    [presentationCatalogUrl],
  );
  const activeCatalogSearch = activeCatalogKey
    ? catalogSearchTerms[activeCatalogKey] || ""
    : "";
  const activeGroupItems = activeGroupData?.data || [];
  useEffect(() => {
    const handleDomainEvent = (event) => {
      const incoming = ensureBlogspotDomain(event?.detail || "");
      if (!incoming) return;
      setTemplateCatalogDomain((prev) => (prev === incoming ? prev : incoming));
    };
    window.addEventListener(DOMAIN_EVENT, handleDomainEvent);
    return () => window.removeEventListener(DOMAIN_EVENT, handleDomainEvent);
  }, []);
  const handleCatalogDomainChange = useCallback(
    (value) => {
      const normalized = ensureBlogspotDomain(value);
      setTemplateCatalogDomain(normalized);
      broadcastDomainSelection(normalized);
    },
    [],
  );

  const filteredActiveGroupItems = useMemo(() => {
    if (
      activeCatalogKey === "youtube" &&
      youtubeCatalogPlaylistFilter &&
      youtubeCatalogPlaylistFilter !== "all"
    ) {
      const selectedPlaylistId = youtubeCatalogPlaylistFilter;
      return activeGroupItems.filter((item) => {
        const playlistIds = Array.isArray(item.playlistIds)
          ? item.playlistIds
          : item.playlistId
          ? [item.playlistId]
          : [];
        return playlistIds.includes(selectedPlaylistId);
      });
    }
    return activeGroupItems;
  }, [activeCatalogKey, activeGroupItems, youtubeCatalogPlaylistFilter]);

  const currentCatalogItems = useMemo(() => {
    const items = filteredActiveGroupItems;
    const query = (activeCatalogSearch || "").trim().toLowerCase();
    if (!query) {
      return items;
    }

    const tokens = query.split(/\s+/).filter(Boolean);
    if (!tokens.length) {
      return items;
    }

    const matchesTokens = (value) => {
      if (!value) return false;
      const text = Array.isArray(value) ? value.join(" ") : String(value);
      const normalized = text.toLowerCase();
      return tokens.every((token) => normalized.includes(token));
    };

    return items.filter((item) => {
      if (matchesTokens(item.title)) return true;
      if (matchesTokens(item.description)) return true;
      if (matchesTokens(item.reference)) return true;
      if (matchesTokens(item.content)) return true;
      if (matchesTokens(item.originalUrl)) return true;
      if (matchesTokens(item.embedUrl)) return true;
      if (matchesTokens(item.songs)) return true;
      if (matchesTokens(item.slides)) return true;
      return false;
    });
  }, [activeCatalogSearch, filteredActiveGroupItems]);

  const updateCatalogSearch = useCallback((key, value) => {
    if (!key) return;
    setCatalogSearchTerms((prev) => {
      const nextValue = value || "";
      if (prev[key] === nextValue) {
        return prev;
      }
      return { ...prev, [key]: nextValue };
    });
  }, []);

  const handleBackgroundPlaylistTrackSelect = useCallback(
    (videoId, entry) => {
      if (!videoId) return;
      setBackgroundMusicSelection((prev) => {
        if (
          prev.mode === "playlist" &&
          prev.playlistVideoId === videoId &&
          prev.playlistId === backgroundPlaylistId
        ) {
          return prev;
        }
        return {
          ...prev,
          mode: "playlist",
          playlistId: backgroundPlaylistId,
          playlistTitle: getConfiguredPlaylistTitle(backgroundPlaylistId),
          playlistVideoId: videoId,
          playlistLabel: entry?.title || entry?.videoId || prev.playlistLabel || "",
        };
      });
      setBackgroundMusicFormError("");
    },
    [backgroundPlaylistId, setBackgroundMusicSelection, setBackgroundMusicFormError]
  );

  const handleBackgroundPlaylistSourceChange = useCallback(
    (nextId) => {
      const trimmed = (nextId || "").trim();
      if (!trimmed) {
        return;
      }
      setBackgroundPlaylistId((prev) => {
        if (prev === trimmed) return prev;
        return trimmed;
      });
      setBackgroundPlaylistError("");
      setBackgroundMusicFormError("");
      setBackgroundMusicSelection((prev) => ({
        ...prev,
        playlistId: trimmed,
        playlistTitle: getConfiguredPlaylistTitle(trimmed),
        playlistVideoId: "",
        playlistLabel: "",
      }));
    },
    [setBackgroundMusicSelection, setBackgroundMusicFormError, setBackgroundPlaylistError]
  );

  const handlePreviewBackgroundSelection = useCallback(() => {
    if (backgroundMusicSelection.mode === "playlist") {
      const videoId = (backgroundMusicSelection.playlistVideoId || "").trim();
      if (!videoId) return;
      const track = backgroundPlaylistItems.find((entry) => entry.videoId === videoId);
      const sourceId = `background-track-${videoId}`;
      const embedUrl = buildYoutubeEmbedUrl(videoId);
      setCatalogPreview((prev) =>
        prev?.sourceId === sourceId
          ? null
          : {
              sourceId,
              title: track?.title || backgroundMusicSelection.playlistLabel || "Background Track",
              embedUrl,
            },
      );
      return;
    }

    if (backgroundMusicSelection.mode === "custom") {
      const url = (backgroundMusicSelection.customUrl || "").trim();
      if (!url) return;
      try {
        const music = createBackgroundMusicFromUrl(url, { mode: "custom" });
        if (!music?.embedUrl) return;
        const identifier = music.videoId || url;
        const sourceId = `background-custom-${identifier}`;
        setCatalogPreview((prev) =>
          prev?.sourceId === sourceId
            ? null
            : {
                sourceId,
                title: backgroundMusicSelection.customLabel || "Custom Music",
                embedUrl: music.embedUrl,
              },
        );
      } catch (error) {
        // ignore invalid custom URLs for preview
      }
    }
  }, [backgroundMusicSelection, backgroundPlaylistItems]);

  const currentBackgroundPreviewSourceId = useMemo(() => {
    if (backgroundMusicSelection.mode === "playlist") {
      const videoId = (backgroundMusicSelection.playlistVideoId || "").trim();
      return videoId ? `background-track-${videoId}` : "";
    }
    if (backgroundMusicSelection.mode === "custom") {
      const url = (backgroundMusicSelection.customUrl || "").trim();
      if (!url) return "";
      try {
        const music = createBackgroundMusicFromUrl(url, { mode: "custom" });
        const identifier = music?.videoId || url;
        return identifier ? `background-custom-${identifier}` : "";
      } catch (error) {
        return "";
      }
    }
    return "";
  }, [backgroundMusicSelection]);

  const isBackgroundPreviewActive =
    Boolean(currentBackgroundPreviewSourceId) &&
    catalogPreview?.sourceId === currentBackgroundPreviewSourceId;
  const playlistPreviewActive =
    backgroundMusicSelection.mode === "playlist" && isBackgroundPreviewActive;
  const customPreviewActive =
    backgroundMusicSelection.mode === "custom" && isBackgroundPreviewActive;

  const isCatalogLoading = Boolean(activeGroupConfig.meta?.loading);
  const activeCatalogError = activeGroupConfig.meta?.error || "";
  const totalCatalogItems = filteredActiveGroupItems.length;
  const catalogSearchEnabled = Boolean(
    activeCatalogKey && (totalCatalogItems > 0 || activeCatalogSearch),
  );

  useEffect(() => {
    if (autoTitle && sessionTitle !== autoGeneratedTitle) {
      setSessionTitle(autoGeneratedTitle);
    }
  }, [autoGeneratedTitle, autoTitle, sessionTitle]);

  useEffect(() => {
    if ((showConfigOverlay || showEditModal) && jsonDraftPristine) {
      setJsonDraft(liveSessionJson);
    }
  }, [jsonDraftPristine, liveSessionJson, showConfigOverlay, showEditModal]);


  useEffect(() => {
    fetchYoutubeCatalog();
  }, [fetchYoutubeCatalog]);

  useEffect(() => {
    setCatalogPreview(null);
  }, [activeGroup]);

  useEffect(() => () => {
    if (copyStatusTimerRef.current) {
      clearTimeout(copyStatusTimerRef.current);
    }
  }, []);

  useEffect(() => {
    setCatalogPreview((prev) => {
      if (!prev) return prev;
      return prev.sourceId?.startsWith("background-") ? null : prev;
    });
  }, [
    backgroundMusicSelection.customUrl,
    backgroundMusicSelection.mode,
    backgroundMusicSelection.playlistVideoId,
  ]);

  useEffect(() => {
    if (!youtubeCatalogPlaylists.length) {
      if (youtubeCatalogPlaylistFilter !== "all") {
        setYoutubeCatalogPlaylistFilter("all");
      }
      return;
    }
    if (
      youtubeCatalogPlaylistFilter !== "all" &&
      !youtubeCatalogPlaylists.some((playlist) => playlist.id === youtubeCatalogPlaylistFilter)
    ) {
      setYoutubeCatalogPlaylistFilter("all");
    }
  }, [youtubeCatalogPlaylistFilter, youtubeCatalogPlaylists]);

  useEffect(() => {
    const playlistId = (backgroundPlaylistId || "").trim();
    const localItems = buildLocalPlaylistItems(playlistId);
    setBackgroundPlaylistItems(localItems);
    setBackgroundPlaylistError("");

    if (!playlistId) {
      setBackgroundPlaylistLoading(false);
      return undefined;
    }

    if (typeof window === "undefined" || typeof fetch === "undefined") {
      setBackgroundPlaylistLoading(false);
      return undefined;
    }

    const feedUrl = buildBackgroundPlaylistFeedUrl(playlistId);
    if (!feedUrl) {
      setBackgroundPlaylistLoading(false);
      if (!localItems.length) {
        setBackgroundPlaylistError("Missing playlist configuration.");
      }
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const loadPlaylistMetadata = async () => {
      setBackgroundPlaylistLoading(true);
      try {
        const response = await fetch(feedUrl, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Unable to load playlist (HTTP ${response.status})`);
        }
        const text = await response.text();
        if (cancelled) return;
        const parsed = parsePlaylistFeed(text);
        if (!parsed.length) {
          if (!localItems.length) {
            setBackgroundPlaylistError("No tracks found in the selected playlist.");
          }
          return;
        }
        const parsedMap = new Map(
          parsed.map((entry) => [entry.videoId, entry.title || entry.videoId]),
        );
        setBackgroundPlaylistItems((prev) => {
          const baseOrder = prev.length ? prev : localItems;
          const seen = new Set();
          const merged = [];

          const pushItem = (videoId, fallbackTitle) => {
            const trimmedId = (videoId || "").trim();
            if (!trimmedId || seen.has(trimmedId)) return;
            seen.add(trimmedId);
            const parsedTitle = parsedMap.get(trimmedId);
            const metadataTitle = getVideoMetadataTitle(trimmedId);
            merged.push({
              videoId: trimmedId,
              title: parsedTitle || metadataTitle || fallbackTitle || trimmedId,
            });
          };

          baseOrder.forEach((item) => {
            pushItem(item.videoId, item.title || item.videoId);
          });

          parsed.forEach((entry) => {
            pushItem(entry.videoId, entry.title || entry.videoId);
          });

          if (!merged.length) {
            parsed.forEach((entry) => {
              pushItem(entry.videoId, entry.title || entry.videoId);
            });
          }

          return merged;
        });
      } catch (error) {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        if (!localItems.length) {
          setBackgroundPlaylistItems([]);
        }
        if (!localItems.length) {
          setBackgroundPlaylistError(
            error?.message || "Unable to load background music playlist.",
          );
        }
      } finally {
        if (!cancelled) {
          setBackgroundPlaylistLoading(false);
        }
      }
    };

    loadPlaylistMetadata();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [backgroundPlaylistId]);

  useEffect(() => {
    if (
      backgroundMusicSelection.mode === "playlist" &&
      !backgroundMusicSelection.playlistVideoId &&
      backgroundPlaylistItems.length
    ) {
      const firstItem = backgroundPlaylistItems[0];
      setBackgroundMusicSelection((prev) => {
        let next = prev;
        let changed = false;
        if (prev.playlistId !== backgroundPlaylistId) {
          next = {
            ...next,
            playlistId: backgroundPlaylistId,
            playlistTitle: getConfiguredPlaylistTitle(backgroundPlaylistId),
          };
          changed = true;
        }
        if (!prev.playlistVideoId && firstItem) {
          if (!changed) {
            next = { ...next };
          }
          next.playlistVideoId = firstItem.videoId;
          next.playlistLabel = firstItem.title || firstItem.videoId || "";
          changed = true;
        }
        return changed ? next : prev;
      });
    }
  }, [
    backgroundMusicSelection.mode,
    backgroundMusicSelection.playlistVideoId,
    backgroundPlaylistId,
    backgroundPlaylistItems,
  ]);

  useEffect(() => {
    if (
      backgroundMusicSelection.mode === "playlist" &&
      !backgroundPlaylistLoading &&
      !backgroundPlaylistItems.length &&
      backgroundPlaylistError &&
      backgroundMusicSelection.playlistId === backgroundPlaylistId
    ) {
      setBackgroundMusicSelection((prev) => ({
        ...prev,
        mode: "none",
        playlistVideoId: "",
        playlistLabel: "",
      }));
    }
  }, [
    backgroundMusicSelection.mode,
    backgroundMusicSelection.playlistId,
    backgroundPlaylistError,
    backgroundPlaylistItems.length,
    backgroundPlaylistLoading,
    backgroundPlaylistId,
    setBackgroundMusicSelection,
  ]);

  useEffect(() => {
    fetchWorshipCatalog();
  }, [fetchWorshipCatalog]);

  useEffect(() => {
    fetchBibleCatalog();
  }, [fetchBibleCatalog]);

  useEffect(() => {
    fetchPresentCatalog();
  }, [fetchPresentCatalog]);

  useEffect(() => {
    if (!sourceGroups.length) return;
    if (!sourceGroups.some((group) => group.key === activeGroup)) {
      setActiveGroup(sourceGroups[0].key);
    }
  }, [activeGroup, setActiveGroup, sourceGroups]);

  useEffect(() => {
    if (!sessionItems.length) {
      setCurrentIndex(0);
      return;
    }
    setCurrentIndex((prev) => {
      if (prev >= sessionItems.length) {
        return sessionItems.length - 1;
      }
      return prev;
    });
  }, [sessionItems]);

  const removeSessionItem = useCallback((instanceId) => {
    setSessionItems((prev) => prev.filter((item) => item.instanceId !== instanceId));
    setSelectedItemId((prev) => (prev === instanceId ? null : prev));
  }, []);

  const updateSessionItem = useCallback((instanceId, updates) => {
    setSessionItems((prev) =>
      prev.map((item) => {
        if (item.instanceId !== instanceId) return item;
        if (typeof updates === "function") {
          return { ...item, ...updates(item) };
        }
        return { ...item, ...updates };
      })
    );
  }, []);

  const updateSessionItemNotes = useCallback(
    (instanceId, notes) => {
      updateSessionItem(instanceId, { notes });
    },
    [updateSessionItem]
  );

  const handleDurationChange = useCallback(
    (instanceId, value) => {
      updateSessionItem(instanceId, {
        durationMinutes: sanitizeDurationMinutes(value),
      });
    },
    [updateSessionItem],
  );

  const handleDragStart = useCallback((event, index) => {
    dragIndexRef.current = index;
    event.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (event, index) => {
      event.preventDefault();
      const fromIndex = dragIndexRef.current;
      if (fromIndex === null || fromIndex === index) return;
      setSessionItems((prev) => {
        const next = [...prev];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(index, 0, moved);
        return next;
      });
      dragIndexRef.current = null;
    },
    []
  );

  const showCopyStatus = useCallback((message) => {
    if (copyStatusTimerRef.current) {
      clearTimeout(copyStatusTimerRef.current);
    }
    setCopyStatus(message);
    if (message) {
      copyStatusTimerRef.current = setTimeout(() => {
        setCopyStatus("");
      }, 2400);
    }
  }, []);

  const stopPresentation = useCallback(() => {
    setIsPresenting(false);
    setShowConfigOverlay(true);
    setAutoAdvanceEnabled(false);
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) =>
      prev + 1 < sessionItems.length ? prev + 1 : prev
    );
  }, [sessionItems.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const handleControlsDragMove = useCallback((event) => {
    const state = controlsDragStateRef.current;
    if (!state) return;
    if (state.pointerId !== null && event.pointerId !== state.pointerId) {
      return;
    }

    const { stageRect, offsetX, offsetY } = state;
    let left = event.clientX - stageRect.left - offsetX;
    let top = event.clientY - stageRect.top - offsetY;
    const controlsEl = presentationControlsRef.current;
    if (controlsEl) {
      const width = controlsEl.offsetWidth;
      const height = controlsEl.offsetHeight;
      left = Math.max(0, Math.min(stageRect.width - width, left));
      top = Math.max(0, Math.min(stageRect.height - height, top));
    }
    setControlsPosition({ left, top });
    event.preventDefault();
  }, []);

  const endControlsDrag = useCallback(
    (event) => {
      const state = controlsDragStateRef.current;
      if (!state) return;
      if (state.pointerId !== null && event.pointerId !== state.pointerId) {
        return;
      }
      const controlsEl = presentationControlsRef.current;
      if (controlsEl && state.pointerId !== null && controlsEl.releasePointerCapture) {
        try {
          controlsEl.releasePointerCapture(state.pointerId);
        } catch {
          /* ignore */
        }
      }
      controlsDragStateRef.current = null;
      window.removeEventListener("pointermove", handleControlsDragMove);
      window.removeEventListener("pointerup", endControlsDrag);
      window.removeEventListener("pointercancel", endControlsDrag);
    },
    [handleControlsDragMove]
  );

  const beginControlsDrag = useCallback(
    (event) => {
      const stage = presentationStageRef.current;
      const controlsEl = presentationControlsRef.current;
      if (!stage || !controlsEl) return;

      const stageRect = stage.getBoundingClientRect();
      const controlRect = controlsEl.getBoundingClientRect();
      controlsDragStateRef.current = {
        pointerId: event.pointerId ?? null,
        offsetX: event.clientX - controlRect.left,
        offsetY: event.clientY - controlRect.top,
        stageRect,
      };

      window.addEventListener("pointermove", handleControlsDragMove, { passive: false });
      window.addEventListener("pointerup", endControlsDrag);
      window.addEventListener("pointercancel", endControlsDrag);

      if (controlsEl.setPointerCapture && event.pointerId !== undefined) {
        try {
          controlsEl.setPointerCapture(event.pointerId);
        } catch {
          /* ignore */
        }
      }

      setControlsPosition({
        left: controlRect.left - stageRect.left,
        top: controlRect.top - stageRect.top,
      });

      event.preventDefault();
    },
    [endControlsDrag, handleControlsDragMove]
  );

  useEffect(
    () => () => {
      window.removeEventListener("pointermove", handleControlsDragMove);
      window.removeEventListener("pointerup", endControlsDrag);
      window.removeEventListener("pointercancel", endControlsDrag);
    },
    [handleControlsDragMove, endControlsDrag]
  );

  useEffect(() => {
    broadcastPresenterState();
  }, [broadcastPresenterState]);

  useEffect(() => {
    if (!presenterToken || !presenterChannelRef.current) {
      autoAdvanceSyncRef.current.isRemoteUpdate = false;
      return undefined;
    }
    if (autoAdvanceSyncRef.current.isRemoteUpdate) {
      autoAdvanceSyncRef.current.isRemoteUpdate = false;
      return undefined;
    }
    postPresenterMessage({
      type: "SESSION_CONTROL",
      payload: { action: "AUTO_ADVANCE_SYNC", value: autoAdvanceEnabled },
    });
    return undefined;
  }, [autoAdvanceEnabled, postPresenterMessage, presenterToken]);

  const currentItem = sessionItems[currentIndex];
  const currentItemKey = useMemo(() => {
    if (!currentItem) return null;
    return (
      currentItem.instanceId ||
      currentItem.sourceId ||
      currentItem.videoId ||
      currentItem.embedUrl ||
      currentItem.title ||
      null
    );
  }, [currentItem]);
  const currentItemHasTrackableMedia = useMemo(() => {
    if (!currentItem) return false;
    if (currentItem.type === "worshipRemote" && currentItem.embedUrl) {
      return true;
    }
    const embedUrl = (currentItem.embedUrl || "").toLowerCase();
    if (embedUrl.includes("youtube.com") || embedUrl.includes("youtu.be")) {
      return true;
    }
    const videoId = (currentItem.videoId || "").toLowerCase();
    return Boolean(videoId);
  }, [currentItem]);
  const currentBackgroundMusicEmbed = useMemo(
    () => getBackgroundMusicEmbedUrl(currentItem?.backgroundMusic),
    [currentItem],
  );
  const previewItem = currentItem || sessionItems[0] || null;
  const previewBackgroundMusicEmbed = useMemo(
    () => getBackgroundMusicEmbedUrl(previewItem?.backgroundMusic),
    [previewItem],
  );
  const previewItemKey = useMemo(() => {
    if (!previewItem) return null;
    return (
      previewItem.instanceId ||
      previewItem.sourceId ||
      previewItem.videoId ||
      previewItem.embedUrl ||
      previewItem.title ||
      null
    );
  }, [previewItem]);
  const previewItemHasTrackableMedia = useMemo(() => {
    if (!previewItem) return false;
    if (previewItem.type === "worshipRemote" && previewItem.embedUrl) {
      return true;
    }
    const embedUrl = (previewItem.embedUrl || "").toLowerCase();
    if (embedUrl.includes("youtube.com") || embedUrl.includes("youtu.be")) {
      return true;
    }
    const videoId = (previewItem.videoId || "").toLowerCase();
    if (videoId) return true;
    return false;
  }, [previewItem]);
  const previewBackgroundItemKey = useMemo(
    () => (previewItemKey ? `${previewItemKey}|bg` : null),
    [previewItemKey],
  );
  const previewBackgroundHasTrackable = useMemo(() => {
    if (!previewBackgroundMusicEmbed) return false;
    const url = previewBackgroundMusicEmbed.toLowerCase();
    return url.includes("youtube.com") || url.includes("youtu.be");
  }, [previewBackgroundMusicEmbed]);
  const getIframeOrigin = useCallback((src) => {
    if (!src) return "*";
    if (typeof window === "undefined") return "*";
    try {
      return new URL(src, window.location.href).origin;
    } catch (error) {
      return "*";
    }
  }, []);
  const focusPresentationControls = useCallback(() => {
    const container = presentationControlsRef.current;
    if (!container) return;
    const button = container.querySelector("button:not([disabled])");
    if (button && typeof button.focus === "function") {
      button.focus();
    }
  }, []);
  const toggleShortcutsOverlay = useCallback(() => {
    setShortcutsOverlayVisible((prev) => !prev);
    setPresentationControlsVisible(true);
  }, [setPresentationControlsVisible]);
  const focusStageEmbed = useCallback(() => {
    const embed = presentationContentEmbedRef.current;
    if (embed && typeof embed.focus === "function") {
      embed.focus();
      return true;
    }
    const stageEl = presentationStageRef.current;
    if (stageEl && typeof stageEl.focus === "function") {
      stageEl.focus();
      return true;
    }
    return false;
  }, []);
  const togglePresentationFocus = useCallback(() => {
    const next = focusTargetRef.current === "controls" ? "stage" : "controls";
    focusTargetRef.current = next;
    if (next === "controls") {
      setPresentationControlsVisible(true);
      focusPresentationControls();
    } else {
      focusStageEmbed();
    }
  }, [focusPresentationControls, focusStageEmbed]);
  const sendActionToRemote = useCallback(
    (action) => {
      if (!isPresenting || !currentItem) return false;
      if (!REMOTE_NAVIGATION_TYPES.has(currentItem.type)) return false;
      const iframe = presentationContentEmbedRef.current;
      if (!iframe || !iframe.contentWindow) return false;
      const origin = getIframeOrigin(iframe.src);
      const requestId = safeId("session-nav");
      navigationRequestRef.current = { id: requestId, action };
      try {
        iframe.contentWindow.postMessage(
          {
            type: SESSION_NAVIGATION_MESSAGE,
            requestId,
            action,
            itemKey: currentItemKey,
          },
          origin,
        );
      } catch (error) {
        navigationRequestRef.current = null;
        return false;
      }
      if (navigationFallbackTimerRef.current) {
        clearTimeout(navigationFallbackTimerRef.current);
      }
      navigationFallbackTimerRef.current = window.setTimeout(() => {
        navigationFallbackTimerRef.current = null;
        if (navigationRequestRef.current?.id !== requestId) return;
        navigationRequestRef.current = null;
        if (action === "next") {
          goToNext();
        } else {
          goToPrevious();
        }
      }, 300);
      return true;
    },
    [currentItem, currentItemKey, getIframeOrigin, goToNext, goToPrevious, isPresenting],
  );
  const navigatePresentation = useCallback(
    (action) => {
      if (sendActionToRemote(action)) {
        return true;
      }
      if (action === "next") {
        goToNext();
      } else if (action === "previous") {
        goToPrevious();
      }
      return false;
    },
    [goToNext, goToPrevious, sendActionToRemote],
  );
  const handleNavigationResponse = useCallback(
    (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object" || data.type !== SESSION_NAVIGATION_RESPONSE) {
        return;
      }
      const payload = data.payload || {};
      const pending = navigationRequestRef.current;
      if (!pending || pending.id !== payload.requestId) return;
      if (navigationFallbackTimerRef.current) {
        clearTimeout(navigationFallbackTimerRef.current);
        navigationFallbackTimerRef.current = null;
      }
      navigationRequestRef.current = null;
      if (payload.handled === false) {
        if (payload.action === "next") {
          goToNext();
        } else if (payload.action === "previous") {
          goToPrevious();
        }
      }
    },
    [goToNext, goToPrevious],
  );
  const handleFocusMessage = useCallback(
    (event) => {
      const data = event?.data;
      if (!data || typeof data !== "object" || data.type !== SESSION_FOCUS_MESSAGE) {
        return;
      }
      const target = data.payload?.target;
      if (target === "controls") {
      setPresentationControlsVisible(true);
      focusPresentationControls();
      focusTargetRef.current = "controls";
    } else if (target === "stage") {
        focusStageEmbed();
        focusTargetRef.current = "stage";
      }
    },
    [focusPresentationControls, focusStageEmbed],
  );
  useEffect(() => {
    window.addEventListener("message", handleNavigationResponse);
    return () => window.removeEventListener("message", handleNavigationResponse);
  }, [handleNavigationResponse]);
  useEffect(() => {
    window.addEventListener("message", handleFocusMessage);
    return () => window.removeEventListener("message", handleFocusMessage);
  }, [handleFocusMessage]);
  useEffect(() => {
    return () => {
      if (navigationFallbackTimerRef.current) {
        clearTimeout(navigationFallbackTimerRef.current);
        navigationFallbackTimerRef.current = null;
      }
    };
  }, []);
  useTouchNavigation(presentationContentRef, {
    onLeft: () => navigatePresentation("previous"),
    onRight: () => navigatePresentation("next"),
  });
  const controlEmbeddedMedia = useCallback(
    (iframe, item, action) => {
      if (!iframe || !item || !iframe.contentWindow) return;
      if (item.type === "worshipRemote") {
        let targetOrigin = "*";
        if (typeof window !== "undefined") {
          try {
            targetOrigin = new URL(iframe.src, window.location.origin).origin;
          } catch (error) {
            targetOrigin = "*";
          }
        }
        try {
          iframe.contentWindow.postMessage(
            { type: WORSHIP_EMBED_CONTROL_MESSAGE, action: action === "play" ? "play" : "pause" },
            targetOrigin,
          );
        } catch (error) {
          // Ignore cross-origin failures
        }
      } else {
        const command = action === "play" ? "playVideo" : "pauseVideo";
        sendYoutubeCommand(iframe, command);
      }
    },
    [sendYoutubeCommand],
  );
  const handlePreviewEmbedRef = useCallback(
    (node) => {
      previewContentEmbedRef.current = node || null;
      if (node && previewItemHasTrackableMedia) {
        registerYoutubeIframe(node);
        if (presenterToken && !isPresenterView && previewItem) {
          controlEmbeddedMedia(node, previewItem, "pause");
        }
      }
    },
    [
      controlEmbeddedMedia,
      isPresenterView,
      presenterToken,
      previewItem,
      previewItemHasTrackableMedia,
      registerYoutubeIframe,
    ],
  );
  const handlePresentationEmbedRef = useCallback(
    (node) => {
      presentationContentEmbedRef.current = node || null;
      if (node && currentItemHasTrackableMedia) {
        registerYoutubeIframe(node);
      }
    },
    [currentItemHasTrackableMedia, registerYoutubeIframe],
  );
  const isBackgroundMusicAvailable = Boolean(currentBackgroundMusicEmbed);
  const backgroundMusicToggleLabel = backgroundMusicEnabled
    ? "Pause background music"
    : "Play background music";
  const syncBackgroundMusicPlayback = useCallback(() => {
    const command = backgroundMusicEnabled ? "playVideo" : "pauseVideo";
    sendYoutubeCommand(presentationMusicRef.current, command);
    sendYoutubeCommand(previewMusicRef.current, command);
  }, [backgroundMusicEnabled, sendYoutubeCommand]);
  const handleMusicIframeLoad = useCallback(
    (event) => {
      const iframe = event?.currentTarget;
      if (!iframe) return;
      registerYoutubeIframe(iframe);
      if (backgroundMusicEnabled) {
        sendYoutubeCommand(iframe, "playVideo");
      } else {
        sendYoutubeCommand(iframe, "pauseVideo");
      }
      if (iframe === previewMusicRef.current) {
        sendYoutubeCommand(iframe, previewAudioMuted ? "mute" : "unMute");
      }
    },
    [backgroundMusicEnabled, previewAudioMuted, registerYoutubeIframe, sendYoutubeCommand],
  );

  useEffect(() => {
    syncBackgroundMusicPlayback();
  }, [syncBackgroundMusicPlayback, currentBackgroundMusicEmbed, previewBackgroundMusicEmbed]);

  useEffect(() => {
    previewItemKeyRef.current = previewItemKey;
    previewHasTrackableMediaRef.current =
      previewItemHasTrackableMedia && panelModes.preview !== "collapsed";
    if (!previewItem) {
      setPreviewMediaState({ status: "idle", itemKey: null });
      previewContentEmbedRef.current = null;
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
      return;
    }
    const nextState = previewItemHasTrackableMedia
      ? { status: "waiting", itemKey: previewItemKey }
      : { status: "ready", itemKey: previewItemKey };
    setPreviewMediaState(nextState);
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, [panelModes.preview, previewItem, previewItemKey, previewItemHasTrackableMedia]);

  useEffect(() => {
    presentationItemKeyRef.current = currentItemKey;
    presentationHasTrackableMediaRef.current = currentItemHasTrackableMedia && isPresenting;
    if (!isPresenting || !currentItem) {
      setPresentationMediaState({ status: "idle", itemKey: null });
      if (!isPresenting) {
        presentationContentEmbedRef.current = null;
      }
      return;
    }
    const next = currentItemHasTrackableMedia
      ? { status: "waiting", itemKey: currentItemKey }
      : { status: "ready", itemKey: currentItemKey };
    setPresentationMediaState(next);
  }, [currentItem, currentItemKey, currentItemHasTrackableMedia, isPresenting]);

  useEffect(() => {
    previewBackgroundKeyRef.current = previewBackgroundItemKey;
    previewBackgroundHasTrackableRef.current =
      previewBackgroundHasTrackable && panelModes.preview !== "collapsed";
    if (!previewItem || !previewBackgroundMusicEmbed || panelModes.preview === "collapsed") {
      setPreviewBackgroundState({ status: "idle", itemKey: null });
      return;
    }
    const next = previewBackgroundHasTrackable
      ? { status: "waiting", itemKey: previewBackgroundItemKey }
      : { status: "ready", itemKey: previewBackgroundItemKey };
    setPreviewBackgroundState(next);
  }, [
    panelModes.preview,
    previewBackgroundHasTrackable,
    previewBackgroundItemKey,
    previewBackgroundMusicEmbed,
    previewItem,
  ]);

  useEffect(() => {
    const handlePlayerMessage = (event) => {
      const contentIframe = previewContentEmbedRef.current;
      const presentationIframe = presentationContentEmbedRef.current;
      const backgroundIframe = previewMusicRef.current;
      const isContentSource =
        contentIframe && event.source === contentIframe.contentWindow;
      const isPresentationSource =
        presentationIframe && event.source === presentationIframe.contentWindow;
      const isBackgroundSource =
        backgroundIframe && event.source === backgroundIframe.contentWindow;
      if (!isContentSource && !isPresentationSource && !isBackgroundSource) return;
      let data = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (!data || typeof data !== "object") return;
      if ((isContentSource || isPresentationSource) && data.type === WORSHIP_EMBED_MEDIA_MESSAGE) {
        const state = data.state;
        if (isContentSource) {
          if (!previewHasTrackableMediaRef.current) return;
          const currentKey = previewItemKeyRef.current;
          if (!currentKey) return;
          if (presenterToken && !isPresenterView && state === "playing") {
            controlEmbeddedMedia(previewContentEmbedRef.current, previewItem, "pause");
          }
          if (state === "ended" || state === "playing" || state === "waiting") {
            setPreviewMediaState((prev) =>
              prev.itemKey === currentKey ? { ...prev, status: state } : prev,
            );
          } else if (state === "paused") {
            setPreviewMediaState((prev) =>
              prev.itemKey === currentKey ? { ...prev, status: "waiting" } : prev,
            );
          }
        } else {
          if (!presentationHasTrackableMediaRef.current) return;
          const currentKey = presentationItemKeyRef.current;
          if (!currentKey) return;
          if (state === "ended" || state === "playing" || state === "waiting") {
            setPresentationMediaState((prev) =>
              prev.itemKey === currentKey ? { ...prev, status: state } : prev,
            );
          } else if (state === "paused") {
            setPresentationMediaState((prev) =>
              prev.itemKey === currentKey ? { ...prev, status: "waiting" } : prev,
            );
          }
        }
        return;
      }
      const stateValue =
        data.event === "onStateChange" && typeof data.info === "number"
          ? data.info
          : data.event === "infoDelivery" && data.info && typeof data.info.playerState === "number"
          ? data.info.playerState
          : null;
      if (stateValue === null) return;
      if (isContentSource) {
        if (!previewHasTrackableMediaRef.current) return;
        const currentKey = previewItemKeyRef.current;
        if (!currentKey) return;
        if (presenterToken && !isPresenterView && stateValue === 1) {
          controlEmbeddedMedia(previewContentEmbedRef.current, previewItem, "pause");
        }
        if (stateValue === 0) {
          setPreviewMediaState((prev) =>
            prev.itemKey === currentKey ? { ...prev, status: "ended" } : prev,
          );
        } else if (stateValue === 1) {
          setPreviewMediaState((prev) =>
            prev.itemKey === currentKey ? { ...prev, status: "playing" } : prev,
          );
        }
      } else if (isPresentationSource) {
        if (!presentationHasTrackableMediaRef.current) return;
        const currentKey = presentationItemKeyRef.current;
        if (!currentKey) return;
        if (stateValue === 0) {
          setPresentationMediaState((prev) =>
            prev.itemKey === currentKey ? { ...prev, status: "ended" } : prev,
          );
        } else if (stateValue === 1) {
          setPresentationMediaState((prev) =>
            prev.itemKey === currentKey ? { ...prev, status: "playing" } : prev,
          );
        }
      } else if (isBackgroundSource) {
        if (!previewBackgroundHasTrackableRef.current) return;
        const currentKey = previewBackgroundKeyRef.current;
        if (!currentKey) return;
        if (stateValue === 0) {
          setPreviewBackgroundState((prev) =>
            prev.itemKey === currentKey ? { ...prev, status: "ended" } : prev,
          );
        } else if (stateValue === 1) {
          setPreviewBackgroundState((prev) =>
            prev.itemKey === currentKey ? { ...prev, status: "playing" } : prev,
          );
        }
      }
    };
    window.addEventListener("message", handlePlayerMessage);
    return () => window.removeEventListener("message", handlePlayerMessage);
  }, [
    controlEmbeddedMedia,
    isPresenterView,
    presenterToken,
    previewItem,
  ]);

  useEffect(() => () => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const iframe = previewMusicRef.current;
    if (!iframe) return;
    sendYoutubeCommand(iframe, previewAudioMuted ? "mute" : "unMute");
  }, [previewAudioMuted, sendYoutubeCommand]);

  useEffect(() => {
    if (!autoAdvanceEnabled && autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
  }, [autoAdvanceEnabled]);

  useEffect(() => {
    const iframe = previewContentEmbedRef.current;
    if (!iframe || !previewItem || !previewItemHasTrackableMedia) return;
    controlEmbeddedMedia(iframe, previewItem, "pause");
  }, [controlEmbeddedMedia, previewItem, previewItemHasTrackableMedia]);

  useEffect(() => {
    const iframe = presentationContentEmbedRef.current;
    if (!iframe || !currentItem || !currentItemHasTrackableMedia) return;
    if (!isPresenting) {
      controlEmbeddedMedia(iframe, currentItem, "pause");
      return;
    }
    if (!autoAdvanceEnabled) {
      controlEmbeddedMedia(iframe, currentItem, "pause");
      return;
    }
    if (presentationMediaState.status !== "waiting" && presentationMediaState.status !== "idle") {
      return;
    }
    controlEmbeddedMedia(iframe, currentItem, "play");
  }, [
    autoAdvanceEnabled,
    controlEmbeddedMedia,
    currentItem,
    currentItemHasTrackableMedia,
    isPresenting,
    presentationMediaState.status,
  ]);

  useEffect(() => {
    if (!autoAdvanceEnabled) return undefined;

    const usePresentationMedia = isPresenting && !isPresenterView;
    const activeItem = usePresentationMedia ? currentItem : previewItem;
    if (!activeItem) return undefined;

    const activeHasTrackableMedia = usePresentationMedia
      ? currentItemHasTrackableMedia
      : previewItemHasTrackableMedia;
    const activeMediaStatus = usePresentationMedia
      ? presentationMediaState.status
      : previewMediaState.status;

    const previewPanelCollapsed = panelModes.preview === "collapsed";
    const shouldWaitForMedia = usePresentationMedia
      ? activeHasTrackableMedia
      : activeHasTrackableMedia && !previewPanelCollapsed;
    if (shouldWaitForMedia && activeMediaStatus !== "ended") {
      return undefined;
    }

    const shouldWaitForBackground = !usePresentationMedia && previewBackgroundHasTrackable && !previewPanelCollapsed;
    if (shouldWaitForBackground && previewBackgroundState.status !== "ended") {
      return undefined;
    }

    const seconds = Number(autoAdvanceSeconds);
    const delayMs = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 30000;
    const timer = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      navigatePresentation("next");
    }, delayMs);
    autoAdvanceTimerRef.current = timer;
    return () => {
      clearTimeout(timer);
      if (autoAdvanceTimerRef.current === timer) {
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [
    autoAdvanceEnabled,
    autoAdvanceSeconds,
    currentItem,
    currentItemHasTrackableMedia,
    navigatePresentation,
    isPresenterView,
    isPresenting,
    panelModes.preview,
    presentationMediaState.status,
    previewBackgroundHasTrackable,
    previewBackgroundState.status,
    previewItem,
    previewItemHasTrackableMedia,
    previewMediaState.status,
  ]);

  useEffect(() => {
    if (!sessionItems.length && autoAdvanceEnabled) {
      setAutoAdvanceEnabled(false);
    }
  }, [autoAdvanceEnabled, sessionItems.length]);

  const handleKeyNavigation = useCallback(
    (event) => {
      // --- These keys should work no matter what is focused ---
      if (event.key === "Control") {
        if (isPresenting) {
          event.preventDefault();
          setPresentationControlsVisible((prev) => !prev);
          setShortcutsOverlayVisible(false);
        }
        return;
      }
      if (event.key === "?") {
        event.preventDefault();
        toggleShortcutsOverlay();
        return;
      }
      if (event.key === "Tab" || event.key === "F6") {
        if (isPresenting) {
          event.preventDefault();
          togglePresentationFocus();
          return;
        }
      }
      if (
        (event.code === "AudioVolumeMute" || event.code === "MediaPlayPause") &&
        currentBackgroundMusicEmbed
      ) {
        event.preventDefault();
        toggleBackgroundMusic();
        return;
      }
      if (!isPresenting) {
        const targetTag = (event.target?.tagName || "").toLowerCase();
        const isEditable =
          event.target?.isContentEditable ||
          ["input", "textarea", "select", "button"].includes(targetTag);
        if (isEditable) return;
        if (event.key === " " && !event.metaKey && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
          setAutoAdvanceEnabled((prev) => !prev);
          return;
        }
        if (["ArrowRight", "ArrowDown", "PageDown"].includes(event.key)) {
          event.preventDefault();
          goToNext();
          return;
        }
        if (["ArrowLeft", "ArrowUp", "PageUp"].includes(event.key)) {
          event.preventDefault();
          goToPrevious();
          return;
        }
        return;
      }

      if (event.key === "Alt" && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        setShowConfigOverlay((prev) => !prev);
        setPresentationControlsVisible(true);
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        goToNext();
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        goToPrevious();
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        stopPresentation();
        return;
      }

      const lowerKey = event.key.toLowerCase();

      if (lowerKey === "o") {
        event.preventDefault();
        setShowConfigOverlay((prev) => !prev);
        setPresentationControlsVisible(true);
        return;
      }

      if (lowerKey === "t") {
        event.preventDefault();
        setSlideTimerEnabled((prev) => !prev);
        return;
      }

      if (lowerKey === "p") {
        event.preventDefault();
        setAutoAdvanceEnabled((prev) => !prev);
        return;
      }

      if (lowerKey === "m" && currentBackgroundMusicEmbed) {
        event.preventDefault();
        toggleBackgroundMusic();
        return;
      }

      // Presenter controls should always own navigation, even when an iframe is focused.
      // This keeps the top-level next/previous controls responsive during presentations.

      // "Next" keys
      if (["ArrowRight", " ", "Enter", "PageDown"].includes(event.key)) {
        event.preventDefault();
        navigatePresentation("next");
      // "Previous" keys
      } else if (["ArrowLeft", "Backspace", "PageUp"].includes(event.key)) {
        event.preventDefault();
        navigatePresentation("previous");
      }
    },
    [
      currentBackgroundMusicEmbed,
      goToNext,
      goToPrevious,
      navigatePresentation,
      isPresenting,
      stopPresentation,
      setAutoAdvanceEnabled,
      setShowConfigOverlay,
      toggleBackgroundMusic,
      togglePresentationFocus,
      toggleShortcutsOverlay,
      setShortcutsOverlayVisible,
    ]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyNavigation);
    return () => window.removeEventListener("keydown", handleKeyNavigation);
  }, [handleKeyNavigation]);

  useEffect(() => {
    if (!isPresenting) return;
    if (typeof document === "undefined") return;

    const element =
      document.documentElement || document.body || presentationStageRef.current;
    if (!element) return;

    const requestFullscreen =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;

    if (!requestFullscreen) return;
    if (document.fullscreenElement) return;

    Promise.resolve()
      .then(() => requestFullscreen.call(element))
      .catch(() => {
        // Silent failure; fullscreen may not be supported
      });
  }, [isPresenting]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (isPresenting && !document.fullscreenElement) {
        setIsPresenting(false);
        setShowConfigOverlay(true);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, [isPresenting]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const className = "session-presenting";
    if (isPresenting) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
    return () => {
      document.body.classList.remove(className);
    };
  }, [isPresenting]);

  useEffect(() => {
    if (!isPresenting) {
      setControlsPosition(null);
      setPresentationControlsVisible(true);
    } else {
      setShowConfigOverlay(false);
    }
  }, [isPresenting, setShowConfigOverlay]);

  const selectedItem = useMemo(
    () => sessionItems.find((item) => item.instanceId === selectedItemId) || null,
    [selectedItemId, sessionItems]
  );

  const selectedItemBackgroundMusic = selectedItem?.backgroundMusic || null;
  const selectedItemBackgroundMode = useMemo(() => {
    if (!selectedItemBackgroundMusic) return "none";
    if (selectedItemBackgroundMusic.mode) return selectedItemBackgroundMusic.mode;
    if (selectedItemBackgroundMusic.originalUrl) return "custom";
    if (selectedItemBackgroundMusic.videoId) return "playlist";
    return "playlist";
  }, [selectedItemBackgroundMusic]);
  const selectedItemBackgroundError = useMemo(() => {
    if (!selectedItemBackgroundMusic) return "";
    if (selectedItemBackgroundMusic.mode === "playlist" && !selectedItemBackgroundMusic.videoId) {
      return "Select a playlist track for this slide.";
    }
    if (
      selectedItemBackgroundMusic.mode === "custom" &&
      selectedItemBackgroundMusic.originalUrl &&
      !selectedItemBackgroundMusic.videoId
    ) {
      return "Enter a valid YouTube link for the background music.";
    }
    return "";
  }, [selectedItemBackgroundMusic]);

  const handleSelectedItemBackgroundModeChange = useCallback(
    (mode) => {
      if (!selectedItem) return;
      if (mode === "none") {
        updateSessionItem(selectedItem.instanceId, { backgroundMusic: null });
        return;
      }
      if (mode === "playlist") {
        const current = selectedItem.backgroundMusic;
        const nextVideoId =
          (current?.mode === "playlist" && current.videoId) ||
          backgroundPlaylistItems[0]?.videoId ||
          "";
        if (!nextVideoId) {
          updateSessionItem(selectedItem.instanceId, {
            backgroundMusic: {
              mode: "playlist",
              videoId: "",
              title: "",
              embedUrl: "",
              originalUrl: "",
            },
          });
          return;
        }
        updateSessionItem(selectedItem.instanceId, {
          backgroundMusic: createBackgroundMusicFromVideoId(nextVideoId, {
            mode: "playlist",
            title: backgroundPlaylistMap.get(nextVideoId) || "",
            originalUrl: `${YOUTUBE_WATCH_BASE}${nextVideoId}`,
            playlistId: backgroundPlaylistId,
            playlistTitle: getConfiguredPlaylistTitle(backgroundPlaylistId),
          }),
        });
        return;
      }
      if (mode === "custom") {
        const current = selectedItem.backgroundMusic;
        updateSessionItem(selectedItem.instanceId, {
          backgroundMusic:
            current?.mode === "custom"
              ? { ...current }
              : {
                  mode: "custom",
                  videoId: "",
                  title: "",
                  embedUrl: "",
                  originalUrl: "",
                },
        });
      }
    },
    [
      selectedItem,
      updateSessionItem,
      backgroundPlaylistId,
      backgroundPlaylistItems,
      backgroundPlaylistMap,
    ],
  );

  const handleSelectedItemBackgroundPlaylistChange = useCallback(
    (videoId) => {
      if (!selectedItem) return;
      updateSessionItem(selectedItem.instanceId, () => {
        const trimmed = (videoId || "").trim();
        if (!trimmed) {
          return {
            backgroundMusic: {
              mode: "playlist",
              videoId: "",
              title: "",
              embedUrl: "",
              originalUrl: "",
              playlistId: backgroundPlaylistId,
              playlistTitle: getConfiguredPlaylistTitle(backgroundPlaylistId),
            },
          };
        }
        return {
          backgroundMusic: createBackgroundMusicFromVideoId(trimmed, {
            mode: "playlist",
            title: backgroundPlaylistMap.get(trimmed) || "",
            originalUrl: `${YOUTUBE_WATCH_BASE}${trimmed}`,
            playlistId: backgroundPlaylistId,
            playlistTitle: getConfiguredPlaylistTitle(backgroundPlaylistId),
          }),
        };
      });
    },
    [backgroundPlaylistId, backgroundPlaylistMap, selectedItem, updateSessionItem],
  );

  const handleSelectedItemBackgroundCustomUrlChange = useCallback(
    (value) => {
      if (!selectedItem) return;
      updateSessionItem(selectedItem.instanceId, (item) => {
        const trimmed = (value || "").trim();
        const current =
          item.backgroundMusic && item.backgroundMusic.mode === "custom"
            ? { ...item.backgroundMusic }
            : {
                mode: "custom",
                videoId: "",
                title: "",
                embedUrl: "",
                originalUrl: "",
              };
        current.originalUrl = trimmed;
        if (!trimmed) {
          current.videoId = "";
          current.embedUrl = "";
        } else {
          try {
            const resolved = createBackgroundMusicFromUrl(trimmed, { mode: "custom" });
            current.videoId = resolved.videoId;
            current.embedUrl = resolved.embedUrl;
          } catch (error) {
            current.videoId = "";
            current.embedUrl = "";
          }
        }
        return { backgroundMusic: current };
      });
    },
    [selectedItem, updateSessionItem],
  );

  const handleSelectedItemBackgroundCustomLabelChange = useCallback(
    (value) => {
      if (!selectedItem) return;
      updateSessionItem(selectedItem.instanceId, (item) => {
        const current =
          item.backgroundMusic && item.backgroundMusic.mode === "custom"
            ? { ...item.backgroundMusic }
            : {
                mode: "custom",
                videoId: "",
                title: "",
                embedUrl: "",
                originalUrl: "",
              };
        current.title = value;
        return { backgroundMusic: current };
      });
    },
    [selectedItem, updateSessionItem],
  );

  const presentationControlsStyle = useMemo(() => {
    if (!controlsPosition) return null;
    return {
      left: `${controlsPosition.left}px`,
      top: `${controlsPosition.top}px`,
      bottom: "auto",
      right: "auto",
      transform: "none",
    };
  }, [controlsPosition]);

  const selectedWebsiteValues = useMemo(() => {
    if (!selectedItem || selectedItem.type !== "externalPage") {
      return { baseUrl: "", params: "" };
    }
    const sourceUrl = selectedItem.embedUrl || selectedItem.originalUrl || "";
    return parseWebsiteEditorValues(sourceUrl);
  }, [selectedItem]);

  const presentationRemoteSettings = useMemo(() => {
    if (!selectedItem || selectedItem.type !== "presentationRemote") {
      return null;
    }
    const sourceUrl = selectedItem.embedUrl || selectedItem.originalUrl || "";
    return parsePresentationRemoteSettings(sourceUrl, presentationCatalogUrl);
  }, [selectedItem, presentationCatalogUrl]);

  const handlePresentationRemoteSettingsChange = useCallback(
    (changes) => {
      if (!selectedItem || selectedItem.type !== "presentationRemote") {
        return;
      }
      const sourceUrl =
        selectedItem.embedUrl || selectedItem.originalUrl || presentationCatalogUrl;
      try {
        const nextUrl = buildPresentationRemoteUrl(
          sourceUrl,
          presentationCatalogUrl,
          changes,
        );
        const updatedSettings = parsePresentationRemoteSettings(nextUrl, presentationCatalogUrl);
        const fileParts = splitPresentationFile(updatedSettings.file);
        updateSessionItem(selectedItem.instanceId, (item) => ({
          ...item,
          embedUrl: nextUrl,
          originalUrl: nextUrl,
          presentTopic: fileParts.topic,
          presentFile: fileParts.file || updatedSettings.presentation || "",
        }));
      } catch (error) {
        console.error("Failed to update presentation settings", error);
      }
    },
    [selectedItem, presentationCatalogUrl, updateSessionItem],
  );

  const selectedWorshipRemoteSettings = useMemo(() => {
    if (!selectedItem || selectedItem.type !== "worshipRemote") {
      return null;
    }
    const sourceUrl = selectedItem.embedUrl || selectedItem.originalUrl || "";
    return parseWorshipRemoteSettings(sourceUrl);
  }, [selectedItem]);

  const handleWorshipRemoteSettingsChange = useCallback(
    (changes) => {
      if (!selectedItem || selectedItem.type !== "worshipRemote") {
        return;
      }
      const current = parseWorshipRemoteSettings(
        selectedItem.embedUrl || selectedItem.originalUrl || buildWorshipEmbedUrl(),
      );
      const next = { ...current, ...changes };
      const nextUrl = buildWorshipRemoteUrl(next);
      updateSessionItem(selectedItem.instanceId, (item) => ({
        ...item,
        embedUrl: nextUrl,
      }));
    },
    [selectedItem, updateSessionItem],
  );

  const selectedBibleRemoteSettings = useMemo(() => {
    if (!selectedItem || selectedItem.type !== "bibleRemote") {
      return null;
    }
    const sourceUrl = selectedItem.embedUrl || selectedItem.originalUrl || getBibleCatalogUrl();
    return parseBibleRemoteSettings(sourceUrl);
  }, [selectedItem]);

  const bibleRemoteChapterOptions = useMemo(() => {
    if (!selectedBibleRemoteSettings) return [];
    const bookIndex = parseInt(selectedBibleRemoteSettings.book, 10);
    const chapters = BIBLE_BOOKS[Number.isNaN(bookIndex) ? 0 : Math.min(Math.max(bookIndex, 0), BIBLE_BOOKS.length - 1)]?.chapters || 1;
    return Array.from({ length: chapters }, (_, index) => String(index + 1));
  }, [selectedBibleRemoteSettings]);

  const handleBibleRemoteSettingsChange = useCallback(
    (changes) => {
      if (!selectedItem || selectedItem.type !== "bibleRemote") {
        return;
      }
      const current = parseBibleRemoteSettings(
        selectedItem.embedUrl || selectedItem.originalUrl || getBibleCatalogUrl(),
      );
      const next = { ...current, ...changes };
      const nextUrl = buildBibleRemoteUrl(next);
      const reference = buildBibleReference(next.book, next.chapter, next.verse);
      const versionLabel =
        versionOptionsWithFallback.find((option) => option.value === next.version)?.label ||
        next.version;
      const languageLabel =
        languageOptionsWithFallback.find((option) => option.value === next.language)?.label ||
        next.language;
      const description = `${versionLabel} • ${languageLabel}`;
      updateSessionItem(selectedItem.instanceId, {
        title: reference,
        reference,
        description,
        embedUrl: nextUrl,
        originalUrl: nextUrl,
      });
    },
    [
      selectedItem,
      updateSessionItem,
      versionOptionsWithFallback,
      languageOptionsWithFallback,
    ],
  );

  const presentationFileParts = useMemo(() => {
    if (!presentationRemoteSettings) return { topic: "", file: "" };
    const parts = splitPresentationFile(presentationRemoteSettings.file);
    if ((!parts.topic || !parts.file) && selectedItem?.type === "presentationRemote") {
      return {
        topic: parts.topic || selectedItem.presentTopic || "",
        file: parts.file || selectedItem.presentFile || "",
      };
    }
    return parts;
  }, [presentationRemoteSettings, selectedItem]);

  const selectedTopicForShare = useMemo(
    () => (presentationFileParts.topic || "").trim(),
    [presentationFileParts.topic],
  );
  const selectedTemplateForShare = useMemo(
    () => (loadedTemplateName ? slugify(loadedTemplateName) : ""),
    [loadedTemplateName],
  );

  const presentTopicOptions = useMemo(() => {
    const topics = new Set();
    presentCatalogItems.forEach((item) => {
      if (item.presentTopic) topics.add(item.presentTopic);
    });
    if (selectedItem?.presentTopic) {
      topics.add(selectedItem.presentTopic);
    }
    if (presentationFileParts.topic) {
      topics.add(presentationFileParts.topic);
    }
    return Array.from(topics).sort((a, b) => a.localeCompare(b));
  }, [presentCatalogItems, presentationFileParts.topic, selectedItem]);

  const presentFileOptionsByTopic = useMemo(() => {
    const map = new Map();
    presentCatalogItems.forEach((item) => {
      const topic = item.presentTopic || "";
      const file = item.presentFile || "";
      if (!file) return;
      if (!map.has(topic)) {
        map.set(topic, new Set());
      }
      map.get(topic).add(file);
    });
    if (selectedItem?.type === "presentationRemote") {
      const topic = selectedItem.presentTopic || presentationFileParts.topic || "";
      const file = selectedItem.presentFile || presentationFileParts.file || "";
      if (file) {
        if (!map.has(topic)) map.set(topic, new Set());
        map.get(topic).add(file);
      }
    }
    const result = new Map();
    map.forEach((files, topic) => {
      result.set(topic, Array.from(files).sort((a, b) => a.localeCompare(b)));
    });
    return result;
  }, [presentCatalogItems, presentationFileParts.file, presentationFileParts.topic, selectedItem]);

  const presentFilesForTopic = useMemo(() => {
    if (!presentationRemoteSettings) return [];
    const topicKey = presentationFileParts.topic || "";
    const existing = presentFileOptionsByTopic.get(topicKey) || [];
    const combined = new Set(existing);
    if (presentationFileParts.file) {
      combined.add(presentationFileParts.file);
    }
    return Array.from(combined).sort((a, b) => a.localeCompare(b));
  }, [presentationFileParts, presentFileOptionsByTopic, presentationRemoteSettings]);

  const getControllerLink = useCallback(() => {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    url.searchParams.delete("presenter");
    url.searchParams.delete("sync");
    url.searchParams.delete("session");
    url.searchParams.set("view", "presentation");
    if (selectedTopicForShare) {
      url.searchParams.set("topic", selectedTopicForShare);
    } else {
      url.searchParams.delete("topic");
    }
    if (selectedTemplateForShare) {
      url.searchParams.set("template", selectedTemplateForShare);
    } else {
      url.searchParams.delete("template");
    }
    if (!url.pathname || url.pathname === "/") {
      url.pathname = "/session";
    }
    return url.toString();
  }, [selectedTemplateForShare, selectedTopicForShare]);

  const buildPresenterLink = useCallback((token) => {
    if (typeof window === "undefined" || !token) return "";
    const presenterUrl = new URL(window.location.href);
    presenterUrl.searchParams.set("presenter", "1");
    presenterUrl.searchParams.set("sync", token);
    presenterUrl.searchParams.delete("session");
    presenterUrl.searchParams.set("view", "presentation");
    if (selectedTopicForShare) {
      presenterUrl.searchParams.set("topic", selectedTopicForShare);
    } else {
      presenterUrl.searchParams.delete("topic");
    }
    if (selectedTemplateForShare) {
      presenterUrl.searchParams.set("template", selectedTemplateForShare);
    } else {
      presenterUrl.searchParams.delete("template");
    }
    if (!presenterUrl.pathname || presenterUrl.pathname === "/") {
      presenterUrl.pathname = "/session";
    }
    return presenterUrl.toString();
  }, [selectedTemplateForShare, selectedTopicForShare]);

  const ensurePresenterToken = useCallback(() => {
    if (presenterToken) return presenterToken;
    const nextToken = presenterTokenId();
    setPresenterToken(nextToken);
    setPresenterConnected(false);
    return nextToken;
  }, [presenterToken]);

  const handlePresentationFilePartsChange = useCallback(
    (partial) => {
      if (!selectedItem || selectedItem.type !== "presentationRemote") return;
      const current = presentationFileParts;
      const nextTopic =
        partial.topic !== undefined ? partial.topic : current.topic;
      const nextFile = partial.file !== undefined ? partial.file : current.file;
      const combined = buildPresentationFile(nextTopic, nextFile);
      handlePresentationRemoteSettingsChange({
        file: combined,
        presentation: "",
      });
    },
    [handlePresentationRemoteSettingsChange, presentationFileParts, selectedItem],
  );

  const handleCopyControllerLink = useCallback(async () => {
    const link = getControllerLink();
    if (!link) return;
    const success = await copyTextToClipboard(link);
    showCopyStatus(success ? "" : "Unable to copy link");
  }, [getControllerLink, showCopyStatus]);

  const handleCopyPresenterLink = useCallback(async () => {
    if (!sessionItems.length) return;
    const token = ensurePresenterToken();
    const link = buildPresenterLink(token);
    if (!link) return;
    const success = await copyTextToClipboard(link);
    showCopyStatus(success ? "" : "Unable to copy link");
  }, [buildPresenterLink, ensurePresenterToken, sessionItems.length, showCopyStatus]);

  const playSlideTimerBeep = useCallback(() => {
    if (typeof window === "undefined") return;
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return;
    const context = new AudioCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.2, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.2);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.2);
    oscillator.onended = () => {
      context.close().catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!isPresenting || !slideTimerEnabled) {
      setSlideElapsedSeconds(0);
      slideTimerStartRef.current = null;
      slideTimerLastSegmentRef.current = 0;
      setTimerUseLeftPosition(false);
      setTimerPosition({ top: 20, left: 20 });
      return undefined;
    }
    slideTimerStartRef.current = Date.now();
    slideTimerLastSegmentRef.current = 0;
    setSlideElapsedSeconds(0);

    const intervalId = window.setInterval(() => {
      const start = slideTimerStartRef.current || Date.now();
      const elapsedSeconds = Math.max(0, Math.floor((Date.now() - start) / 1000));
      setSlideElapsedSeconds(elapsedSeconds);
      const segment = Math.floor(elapsedSeconds / 300);
      if (segment > slideTimerLastSegmentRef.current && segment > 0) {
        slideTimerLastSegmentRef.current = segment;
        playSlideTimerBeep();
      }
    }, 500);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isPresenting, slideTimerEnabled, currentIndex, playSlideTimerBeep]);

  useEffect(() => () => {
    const { move, up } = timerDragHandlersRef.current || {};
    if (move) {
      window.removeEventListener("pointermove", move);
    }
    if (up) {
      window.removeEventListener("pointerup", up);
    }
  }, []);

  const handleTimerPointerDown = useCallback(
    (event) => {
      if (!presentationTimerRef.current) return;
      event.preventDefault();
      const rect = presentationTimerRef.current.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;
      timerDragOffsetRef.current = { offsetX, offsetY, width: rect.width, height: rect.height };
      setTimerUseLeftPosition(true);
      if (timerDragHandlersRef.current.move) {
        window.removeEventListener("pointermove", timerDragHandlersRef.current.move);
      }
      if (timerDragHandlersRef.current.up) {
        window.removeEventListener("pointerup", timerDragHandlersRef.current.up);
      }

      const handlePointerMove = (moveEvent) => {
        const dragData = timerDragOffsetRef.current;
        if (!dragData) return;
        const { offsetX: dx, offsetY: dy, width, height } = dragData;
        const nextLeft = moveEvent.clientX - dx;
        const nextTop = moveEvent.clientY - dy;
        const clampLeft = Math.max(0, Math.min(nextLeft, window.innerWidth - width));
        const clampTop = Math.max(0, Math.min(nextTop, window.innerHeight - height));
        setTimerPosition({ top: clampTop, left: clampLeft });
      };

      const handlePointerUp = () => {
        timerDragOffsetRef.current = null;
        if (timerDragHandlersRef.current.move) {
          window.removeEventListener("pointermove", timerDragHandlersRef.current.move);
        }
        if (timerDragHandlersRef.current.up) {
          window.removeEventListener("pointerup", timerDragHandlersRef.current.up);
        }
        timerDragHandlersRef.current = { move: null, up: null };
      };

      timerDragHandlersRef.current = { move: handlePointerMove, up: handlePointerUp };
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [],
  );

  const startPresentation = useCallback(() => {
    if (!sessionItems.length) return;
    setIsPresenting(true);
    setCurrentIndex(0);
    setShowConfigOverlay(false);
    setAutoAdvanceEnabled(false);
  }, [sessionItems.length]);

  const openPresenterMode = useCallback(() => {
    if (!sessionItems.length) return;
    const token = ensurePresenterToken();
    setPresenterConnected(false);
    if (typeof window !== "undefined") {
      const presenterUrl = buildPresenterLink(token);
      if (presenterUrl) {
        window.open(presenterUrl, "_blank", "noopener=yes");
      }
    }
  }, [buildPresenterLink, ensurePresenterToken, sessionItems.length]);

  const handleExport = useCallback(() => {
    const blob = new Blob([JSON.stringify(sessionConfig, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${sessionTitle.replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}-session.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [sessionConfig, sessionTitle]);

  const buildFileBaseName = useCallback(() => {
    const raw = (sessionTitle || "Session Outline").trim();
    const sanitized = raw.replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "-");
    return sanitized || "session-outline";
  }, [sessionTitle]);

  const handleExportWord = useCallback(() => {
    const title = (sessionTitle || "Session Outline").trim() || "Session Outline";
    const fileBase = buildFileBaseName();
    const parts = [`<h1>${escapeHtml(title)}</h1>`];

    if (!sessionOutlineEntries.length) {
      parts.push("<p>No session items available.</p>");
    } else {
      const totalDuration = sessionOutlineEntries.reduce(
        (sum, entry) => sum + sanitizeDurationMinutes(entry.durationMinutes),
        0,
      );
      parts.push(
        `<p><strong>Total Agenda Items:</strong> ${sessionOutlineEntries.length}<br/><strong>Total Duration:</strong> ${totalDuration} min</p>`,
      );

      const rows = sessionOutlineEntries.map((entry) => {
        const duration = sanitizeDurationMinutes(entry.durationMinutes);
        const displayLine = `<strong>${escapeHtml(entry.heading)}</strong>`;
        return `<tr><td>${duration} min</td><td>${displayLine}</td></tr>`;
      });

      parts.push(
        `<table class="agenda-table"><thead><tr><th>Duration</th><th>Agenda Item</th></tr></thead><tbody>${rows.join(
          "",
        )}</tbody></table>`,
      );
    }

    const docHtml = `<!DOCTYPE html><html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset="utf-8"/><title>${escapeHtml(title)}</title><style>body{font-family:"Segoe UI",Arial,Helvetica,sans-serif;padding:48px;color:#0f172a;background:#f8fafc;}h1{font-size:30px;margin-bottom:24px;color:#1e293b;}p{font-size:14px;margin:6px 0 18px;}table{border-collapse:collapse;width:100%;margin-top:12px;font-size:14px;}thead tr{background:#1d4ed8;color:#f8fafc;}th,td{padding:10px 12px;border:1px solid #cbd5f5;text-align:left;}tbody tr:nth-child(even){background:#eef2ff;}tbody tr:nth-child(odd){background:#ffffff;}strong{font-weight:600;}</style></head><body>${parts.join("\n")}</body></html>`;
    const blob = new Blob(["\ufeff", docHtml], { type: "application/msword" });
    triggerDownload(blob, `${fileBase}.doc`);
  }, [buildFileBaseName, sessionOutlineEntries, sessionTitle]);

  const handleExportPdf = useCallback(() => {
    const title = (sessionTitle || "Session Outline").trim() || "Session Outline";
    const fileBase = buildFileBaseName();
    const pdfLines = [];

    if (!sessionOutlineEntries.length) {
      pdfLines.push("No session items available.");
    } else {
      let runningMinutes = 0;
      sessionOutlineEntries.forEach((entry) => {
        const duration = sanitizeDurationMinutes(entry.durationMinutes);
        const lineTitle = entry.heading.replace(/^\d+\.\\s*/, "");
        pdfLines.push(`${duration} min  ${lineTitle}`);
        runningMinutes += duration;
      });
      pdfLines.push("");
      pdfLines.push(`Total Duration: ${runningMinutes} min`);
    }

    const pdfBlob = buildSimplePdf(title, pdfLines);
    triggerDownload(pdfBlob, `${fileBase}.pdf`);
  }, [buildFileBaseName, sessionOutlineEntries, sessionTitle]);

  const handleJsonDraftChange = useCallback(
    (event) => {
      setJsonDraft(event.target.value);
      setJsonDraftPristine(false);
      if (importError) {
        setImportError("");
      }
    },
    [importError],
  );

  const handleEditModalOpen = useCallback(() => {
    setEditModalTab("current");
    setUrlImportInput("");
    setUrlImportError("");
    setImportError("");
    setTableError("");
    setTablePasteValue("");
    setTableRows(deriveTableRowsFromSession(sessionItems));
    setShowEditModal(true);
    setJsonDraftPristine(true);
    setJsonDraft(liveSessionJson);
  }, [liveSessionJson, sessionItems]);

  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setEditModalTab("current");
    setUrlImportError("");
    setImportError("");
    setTableError("");
  }, []);

  const handleUrlInputChange = useCallback(
    (event) => {
      setUrlImportInput(event.target.value);
      if (urlImportError) {
        setUrlImportError("");
      }
    },
    [urlImportError],
  );

  const handleUrlImport = useCallback(() => {
    const urls = splitPastedUrls(urlImportInput);
    if (!urls.length) {
      setUrlImportError("Paste at least one URL.");
      return;
    }
    try {
      const sequence = urls.map((entry, index) => buildSessionEntryFromUrl(entry, index));
      const title = (sessionTitle || "Session Outline").trim() || "Session Outline";
      importSession({
        title,
        autoTitle,
        sequence,
      });
      setUrlImportError("");
      setJsonDraftPristine(true);
    } catch (error) {
      setUrlImportError(error.message);
    }
  }, [autoTitle, importSession, sessionTitle, urlImportInput]);

  const handleSyncTableRows = useCallback(() => {
    setTableRows(deriveTableRowsFromSession(sessionItems));
    setTableError("");
  }, [sessionItems]);

  const handleAddTableRow = useCallback(() => {
    setTableRows((prev) => {
      const entry = createDefaultConfigEntry({ title: `Item ${prev.length + 1}` });
      return [...prev, createTableRowFromConfig(entry)];
    });
  }, []);

  const handleTableRowChange = useCallback(
    (index, field, value) => {
      setTableRows((prev) => {
        if (!prev[index]) return prev;
        const next = [...prev];
        const row = next[index];
        let config = row.config;
        let configDraft = row.configDraft;
        if (config) {
          if (field === "title") {
            config = { ...config, title: value };
          }
          if (field === "url") {
            config = {
              ...config,
              payload: {
                ...config.payload,
                originalUrl: value,
              },
            };
          }
          configDraft = JSON.stringify(config, null, 2);
        }
        next[index] = { ...row, [field]: value, config, configDraft };
        return next;
      });

      if (field === "title") {
        setSessionItems((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const next = [...prev];
          next[index] = { ...next[index], title: value };
          return next;
        });
      }
    },
    [setSessionItems],
  );

  const handleRemoveTableRow = useCallback((index) => {
    setTableRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
  }, []);

  const handleMoveTableRow = useCallback((index, direction) => {
    setTableRows((prev) => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const processTableText = useCallback((text) => {
    if (!text || !text.trim()) return false;
    let added = false;
    setTableRows((prev) => {
      const parsedLines = parseTableLines(text, prev.length);
      if (!parsedLines.length) {
        return prev;
      }
      const newRows = parsedLines.map((line, lineIndex) => {
        try {
          const entry = buildSessionEntryFromUrl(line.url, prev.length + lineIndex, {
            title: line.title,
          });
          return createTableRowFromConfig(entry);
        } catch (error) {
          return createTableRowFromConfig(
            createDefaultConfigEntry({
              title: line.title,
              payload: { originalUrl: line.url },
            }),
          );
        }
      });
      added = true;
      return [...prev, ...newRows];
    });
    if (added) {
      setTableError("");
    }
    return added;
  }, []);

  const handleTablePaste = useCallback(
    (event) => {
      const text = event.clipboardData?.getData("text") || "";
      if (!text.trim()) return;
      event.preventDefault();
      processTableText(text);
      setTablePasteValue("");
    },
    [processTableText],
  );

  const handleTablePasteInputChange = useCallback((event) => {
    setTablePasteValue(event.target.value);
  }, []);

  const handleTablePasteSubmit = useCallback(() => {
    if (!processTableText(tablePasteValue)) {
      setTableError("Paste at least one row with data.");
      return;
    }
    setTablePasteValue("");
  }, [processTableText, tablePasteValue]);

  const handleToggleRowExpanded = useCallback((index) => {
    setTableRows((prev) => {
      if (!prev[index]) return prev;
      const next = [...prev];
      next[index] = { ...prev[index], expanded: !prev[index].expanded };
      return next;
    });
  }, []);

  const handleRowConfigDraftChange = useCallback(
    (index, value) => {
      let parsed = null;
      let parseError = "";
      try {
        parsed = JSON.parse(value || "{}");
      } catch (error) {
        parseError = error.message;
      }

      const normalized = !parseError
        ? {
            ...parsed,
            id: parsed.id || tableRows[index]?.id || safeId("session-item"),
          }
        : null;

      setTableRows((prev) => {
        if (!prev[index]) return prev;
        const next = [...prev];
        const row = prev[index];
        if (parseError || !normalized) {
          next[index] = { ...row, configDraft: value, configError: parseError || "Invalid JSON" };
          return next;
        }
        const derivedTitle = normalized.title || row.title;
        const derivedUrl = deriveUrlFromConfig(normalized) || row.url;
        next[index] = {
          ...row,
          config: normalized,
          configDraft: value,
          configError: "",
          title: derivedTitle,
          url: derivedUrl,
        };
        return next;
      });

      if (!parseError && normalized) {
        setSessionItems((prev) => {
          if (index < 0 || index >= prev.length) return prev;
          const next = [...prev];
          next[index] = buildSessionItemFromConfigEntry({
            ...normalized,
            id: normalized.id || prev[index].instanceId || safeId("session-item"),
          });
          return next;
        });
      }
    },
    [setSessionItems, tableRows],
  );

  const handleApplyTableRows = useCallback(() => {
    if (!tableRows.length) {
      setTableError("Add at least one row to apply.");
      return;
    }
    try {
      const sequence = tableRows.map((row, index) => {
        let parsedConfig = row.config;
        if (!parsedConfig) {
          try {
            parsedConfig = JSON.parse(row.configDraft || "{}");
          } catch (error) {
            throw new Error(`Row ${index + 1}: ${error.message}`);
          }
        }
        const normalizedTitle = (row.title || parsedConfig.title || `Item ${index + 1}`).trim();
        const normalizedUrl = (row.url || deriveUrlFromConfig(parsedConfig)).trim();
        let finalConfig = {
          ...parsedConfig,
          id: parsedConfig.id || safeId("session-item"),
          title: normalizedTitle,
        };
        if (normalizedUrl) {
          try {
            const inferred = buildSessionEntryFromUrl(normalizedUrl, index, { title: normalizedTitle });
            finalConfig = {
              ...inferred,
              ...finalConfig,
              payload: {
                ...inferred.payload,
                ...(finalConfig.payload || {}),
                originalUrl: normalizedUrl,
              },
            };
          } catch (error) {
            finalConfig = {
              ...finalConfig,
              payload: {
                ...(finalConfig.payload || {}),
                originalUrl: normalizedUrl,
              },
            };
          }
        }
        return finalConfig;
      });
      const title = (sessionTitle || "Session Outline").trim() || "Session Outline";
      importSession({
        title,
        autoTitle,
        sequence,
      });
      setTableRows(sequence.map((entry) => createTableRowFromConfig(entry)));
      setTableError("");
      setJsonDraftPristine(true);
    } catch (error) {
      setTableError(error.message);
    }
  }, [autoTitle, importSession, sessionTitle, tableRows]);

  const handleSyncJsonDraft = useCallback(() => {
    setJsonDraft(liveSessionJson);
    setJsonDraftPristine(true);
    setImportError("");
  }, [liveSessionJson]);

  const handleImportText = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonDraft);
      importSession(parsed);
      setImportError("");
      setJsonDraftPristine(true);
    } catch (error) {
      setImportError(error.message);
    }
  }, [importSession, jsonDraft]);

  const handleFileImport = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      setJsonDraft(text);
      setJsonDraftPristine(true);
      setShowConfigOverlay(true);
      setImportError("");
      try {
        const parsed = JSON.parse(text || "{}");
        importSession(parsed);
      } catch (error) {
        setImportError(error.message);
      }
    };
    reader.readAsText(file);
  }, [importSession]);

  const handleTemplateSelect = useCallback((templateName) => {
    if (!templateName || !templates[templateName]) return;
    const templateData = templates[templateName];
    setJsonDraft(JSON.stringify(templateData, null, 2));
    setJsonDraftPristine(true);
    importSession(templateData, { preserveTemplateSelection: true });
    setLoadedTemplateName(templateName);
  }, [importSession, templates]);

  useEffect(() => {
    const templateName = resolveTemplateKey(searchParams.get("template"));
    if (templateName) {
      handleTemplateSelect(templateName);
    }

    const topicParam = searchParams.get("topic");
    if (topicParam && !topicParamAppliedRef.current) {
      const templateMatch = resolveTemplateKey(topicParam);
      if (templateMatch) {
        handleTemplateSelect(templateMatch);
        topicParamAppliedRef.current = true;
      }
    }

    const encodedSessionParam = searchParams.get("session");
    if (encodedSessionParam && !sessionParamLoadedRef.current) {
      const parsed = deserializeSessionParam(encodedSessionParam);
      if (parsed) {
        const expanded = expandShareSnapshot(parsed) || parsed;
        importSession(expanded);
        sessionParamLoadedRef.current = true;
      }
    }

    const view = searchParams.get("view");
    if (view === 'live' || view === 'presentation') {
      setStartFromUrl(true);
    }

    const syncParam = searchParams.get("sync");
    if (syncParam) {
      setPresenterToken(syncParam);
    }

    if (isSlideTimerParamEnabled(searchParams) && !slideTimerEnabled) {
      setSlideTimerEnabled(true);
    }

    if (searchParams.get("presenter") === "1") {
      setIsPresenting(true);
    }
  }, [searchParams, handleTemplateSelect, importSession, resolveTemplateKey, slideTimerEnabled]);

  useEffect(() => {
    if (startFromUrl && sessionItems.length > 0) {
      startPresentation();
      setStartFromUrl(false);
    }
  }, [startFromUrl, sessionItems.length, startPresentation]);

  const slideTimerColorCount = Math.max(1, SLIDE_TIMER_COLORS.length);
  const slideTimerColorIndex = Math.floor(slideElapsedSeconds / 300) % slideTimerColorCount;
  const slideTimerColor = SLIDE_TIMER_COLORS[slideTimerColorIndex] || "#fff";
  const slideTimerDisplay = formatSlideTimerDisplay(slideElapsedSeconds);
  const timerStyle = timerUseLeftPosition
    ? {
        top: `${timerPosition.top}px`,
        left: `${timerPosition.left}px`,
        right: "auto",
      }
    : {
        top: "1.25rem",
        right: "1.25rem",
      };

  const builderClasses = ["worship-builder", isPresenting ? "is-presenting" : ""].join(" ");

  return (
    <div className={builderClasses} ref={fullscreenRef}>
      <SEO
        title="Worship Builder"
        description="Build worship sessions by combining songs, readings, slides, and media."
      />
      {!isPresenting && (
        <div className="builder-toolbar">
          <div className="title-block">
            <div className="title-input-row">
              <input
                id="session-title"
                type="text"
                value={sessionTitle}
                onChange={(event) => setSessionTitle(event.target.value)}
                disabled={autoTitle}
                placeholder="Session title…"
                aria-label="Session title"
              />
              <button
                type="button"
                className={`auto-title ${autoTitle ? "active" : ""}`}
                onClick={() => setAutoTitle((prev) => !prev)}
                title="Toggle auto title"
              >
                <FaMagic />
                {autoTitle ? "Auto" : "Manual"}
              </button>
            </div>
          </div>
          <div className="toolbar-actions">
            <button
              type="button"
              className="toolbar-icon-button"
              onClick={handleToggleFullscreen}
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              aria-pressed={isFullscreen}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
            <CatalogSelector
              options={catalogSelectOptions}
              value={normalizedTemplateCatalogDomain}
              onChange={handleCatalogDomainChange}
            />
            <TemplateSelector templateNames={templateNames} onSelect={handleTemplateSelect} />
            <label className="import-button">
              <FaFileImport /> Import
              <input type="file" accept="application/json" onChange={handleFileImport} />
            </label>
            <button
              type="button"
              className={`toolbar-icon-button toolbar-icon-button--edit${showEditModal ? " is-active" : ""}`}
              onClick={handleEditModalOpen}
              title="Edit session JSON or paste URLs"
            >
              <FaEdit /> Edit
            </button>
            <div
              className={`export-menu${exportMenuOpen ? " is-open" : ""}`}
              ref={exportMenuRef}
            >
              <button
                type="button"
                className="export-button export-toggle"
                onClick={() => setExportMenuOpen((prev) => !prev)}
                aria-haspopup="true"
                aria-expanded={exportMenuOpen}
              >
                <FaDownload /> Export
              </button>
              {exportMenuOpen && (
                <div className="export-menu-panel" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      handleExport();
                      setExportMenuOpen(false);
                    }}
                  >
                    <FaDownload /> JSON Configuration
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      handleExportPdf();
                      setExportMenuOpen(false);
                    }}
                  >
                    <FaFilePdf /> Outline (PDF)
                  </button>

                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      handleExportWord();
                      setExportMenuOpen(false);
                    }}
                  >
                    <FaFileWord /> Outline (DOC)
                  </button>
              
                </div>
              )}
            </div>
            <div className="toolbar-action-pair">
              <button
                type="button"
                className="start-button"
                onClick={startPresentation}
                disabled={!sessionItems.length}
              >
                <FaPlay /> Start
              </button>
              <button
                type="button"
                className="toolbar-icon-button copy-link-button"
                onClick={handleCopyControllerLink}
                disabled={!sessionItems.length}
                title="Copy session controller link"
                aria-label="Copy session controller link"
              >
                <FaCopy />
              </button>
            </div>
            <div className="toolbar-action-pair">
              <button
                type="button"
                className="presenter-button"
                onClick={openPresenterMode}
                disabled={!sessionItems.length}
              >
                <FaExternalLinkAlt /> Present
              </button>
              <button
                type="button"
                className="toolbar-icon-button copy-link-button"
                onClick={handleCopyPresenterLink}
                disabled={!sessionItems.length}
                title="Copy presenter link"
                aria-label="Copy presenter link"
              >
                <FaCopy />
              </button>
            </div>
            <button
              type="button"
              className={`toolbar-icon-button toolbar-icon-button--timer${
                slideTimerEnabled ? " is-active" : ""
              }`}
              onClick={() => setSlideTimerEnabled((prev) => !prev)}
              aria-pressed={slideTimerEnabled}
              title="Toggle slide timer"
            >
              <FaClock />
            </button>
            {copyStatus && <span className="toolbar-copy-status">{copyStatus}</span>}
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="session-edit-modal" role="dialog" aria-modal="true" aria-label="Edit session configuration">
          <div
            className="session-edit-modal__backdrop"
            onClick={handleEditModalClose}
            role="presentation"
            aria-hidden="true"
          />
          <div className="session-edit-modal__panel" role="document">
            <header className="session-edit-modal__header">
              <h2>
                <FaEdit /> Session Editor
              </h2>
              <button type="button" onClick={handleEditModalClose} aria-label="Close session editor">
                <FaTimes />
              </button>
            </header>
            <div className="session-edit-modal__tabs" role="tablist">
              {EDIT_MODAL_TABS.map((tab) => {
                const isActive = editModalTab === tab.key;
                const tabId = `session-edit-tab-${tab.key}`;
                const panelId = `session-edit-panel-${tab.key}`;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    id={tabId}
                    aria-controls={panelId}
                    aria-selected={isActive}
                    className={isActive ? "is-active" : ""}
                    onClick={() => setEditModalTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className="session-edit-modal__body">
              {editModalTab === "current" && (
                <section
                  id="session-edit-panel-current"
                  role="tabpanel"
                  aria-labelledby="session-edit-tab-current"
                >
                  <p className="session-edit-modal__note">
                    Live JSON updates automatically as you modify the session.
                  </p>
                  <textarea value={liveSessionJson} readOnly spellCheck={false} />
                </section>
              )}
              {editModalTab === "json" && (
                <section
                  id="session-edit-panel-json"
                  role="tabpanel"
                  aria-labelledby="session-edit-tab-json"
                >
                  <p className="session-edit-modal__note">
                    Paste or edit JSON, then apply it to rebuild the session.
                  </p>
                  <textarea value={jsonDraft} onChange={handleJsonDraftChange} spellCheck={false} />
                  <div className="session-edit-modal__actions">
                    <button
                      type="button"
                      className="session-edit-modal__button--secondary"
                      onClick={handleSyncJsonDraft}
                    >
                      Sync with session
                    </button>
                    <button type="button" onClick={handleImportText}>
                      <FaUpload /> Apply JSON
                    </button>
                  </div>
                  {importError && <div className="session-edit-modal__error">{importError}</div>}
                </section>
              )}
              {editModalTab === "urls" && (
                <section
                  id="session-edit-panel-urls"
                  role="tabpanel"
                  aria-labelledby="session-edit-tab-urls"
                >
                  <p className="session-edit-modal__note">
                    Paste URLs separated by spaces, commas, or new lines. Each link becomes a session item with default settings.
                  </p>
                  <textarea
                    value={urlImportInput}
                    onChange={handleUrlInputChange}
                    spellCheck={false}
                    placeholder="https://youtu.be/... https://example.com/page"
                  />
                  <div className="session-edit-modal__actions">
                    <button type="button" onClick={handleUrlImport}>
                      <FaUpload /> Build Session
                    </button>
                  </div>
                  {urlImportError && <div className="session-edit-modal__error">{urlImportError}</div>}
                </section>
              )}
              {editModalTab === "table" && (
                <section
                  id="session-edit-panel-table"
                  role="tabpanel"
                  aria-labelledby="session-edit-tab-table"
                >
                  <p className="session-edit-modal__note">
                    Manage every session attribute in a spreadsheet-friendly grid. Paste rows from Google Sheets or Excel (title + URL) to append items, then use the JSON toggle per row to edit the full configuration payload before applying.
                  </p>
                  <div className="session-edit-table__toolbar">
                    <button type="button" onClick={handleAddTableRow}>
                      <FaPlus /> Add Row
                    </button>
                    <button type="button" onClick={handleSyncTableRows}>
                      <FaTable /> Sync From Session
                    </button>
                  </div>
                  <div className="session-edit-table" role="table">
                    <div className="session-edit-table__row session-edit-table__row--head" role="row">
                      <span>#</span>
                      <span>Title</span>
                      <span>URL</span>
                      <span>Actions</span>
                    </div>
                    {tableRows.length === 0 && (
                      <div className="session-edit-table__empty" role="row">
                        No rows yet. Add one or paste from your spreadsheet.
                      </div>
                    )}
                    {tableRows.map((row, index) => (
                      <div className="session-edit-table__row-wrapper" key={row.id || index}>
                        <div className="session-edit-table__row" role="row">
                          <span className="session-edit-table__sequence">{index + 1}</span>
                          <input
                            type="text"
                            value={row.title}
                            placeholder="Session item title"
                            aria-label={`Row ${index + 1} title`}
                            onChange={(event) => handleTableRowChange(index, "title", event.target.value)}
                          />
                          <input
                            type="text"
                            value={row.url}
                            placeholder="https://example.com"
                            aria-label={`Row ${index + 1} URL`}
                            onChange={(event) => handleTableRowChange(index, "url", event.target.value)}
                          />
                          <div className="session-edit-table__actions">
                            <button
                              type="button"
                              onClick={() => handleMoveTableRow(index, -1)}
                              disabled={index === 0}
                              aria-label={`Move row ${index + 1} up`}
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveTableRow(index, 1)}
                              disabled={index === tableRows.length - 1}
                              aria-label={`Move row ${index + 1} down`}
                            >
                              <FaArrowDown />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleRowExpanded(index)}
                              aria-label={`Toggle advanced settings for row ${index + 1}`}
                              aria-expanded={row.expanded}
                            >
                              <FaFileAlt />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveTableRow(index)}
                              aria-label={`Remove row ${index + 1}`}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                        {row.expanded && (
                          <div className="session-edit-table__row-details">
                            <label htmlFor={`session-row-config-${row.id}`}>
                              Row {index + 1} JSON
                            </label>
                            <textarea
                              id={`session-row-config-${row.id}`}
                              value={row.configDraft}
                              onChange={(event) => handleRowConfigDraftChange(index, event.target.value)}
                              spellCheck={false}
                            />
                            {row.configError && (
                              <div className="session-edit-modal__error">Row {index + 1}: {row.configError}</div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="session-edit-table__bulk">
                    <label htmlFor="session-table-paste">Paste rows (Title + URL)</label>
                    <textarea
                      id="session-table-paste"
                      value={tablePasteValue}
                      onChange={handleTablePasteInputChange}
                      onPaste={handleTablePaste}
                      spellCheck={false}
                      placeholder="Title\tURL"
                    />
                    <div className="session-edit-table__bulk-actions">
                      <button type="button" onClick={handleTablePasteSubmit}>
                        Add Rows
                      </button>
                      <button type="button" className="session-edit-modal__button--secondary" onClick={() => setTablePasteValue("")}>
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="session-edit-modal__actions">
                    <button type="button" onClick={handleApplyTableRows}>
                      <FaUpload /> Apply Table
                    </button>
                  </div>
                  {tableError && <div className="session-edit-modal__error">{tableError}</div>}
                </section>
              )}
            </div>
          </div>
        </div>
      )}

      {isPresenting ? (
        <div
          className={`presentation-stage ${showConfigOverlay ? "with-config" : ""}`}
          ref={presentationStageRef}
          tabIndex={-1}
        >
          {slideTimerEnabled && currentItem && (
            <div
              ref={presentationTimerRef}
              className="presentation-timer"
              onPointerDown={handleTimerPointerDown}
              style={{ ...timerStyle, color: slideTimerColor }}
            >
              {slideTimerDisplay}
            </div>
          )}
          <div className="presentation-body">
            {isPresenterView && !presenterConnected ? (
              <div className="presentation-empty">Waiting for session controller…</div>
            ) : currentItem ? (
              <>
                {/* <div className="presentation-content">
                  {renderItemPreview(currentItem, { stage: true, onEmbedRef: handlePresentationEmbedRef })}
                </div> */}
                <div className="presentation-content" ref={presentationContentRef}>
                  {renderItemPreview(currentItem, { stage: true, onEmbedRef: handlePresentationEmbedRef })}
                </div>
                {currentBackgroundMusicEmbed && (
                  <div className="background-music-player" aria-hidden="true">
                    <iframe
                      ref={presentationMusicRef}
                      src={currentBackgroundMusicEmbed}
                      title="Background music"
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                      onLoad={handleMusicIframeLoad}
                    />
                  </div>
                )}

                {presentationControlsVisible && (
                  <div
                    className="presentation-controls"
                    ref={presentationControlsRef}
                    style={presentationControlsStyle || undefined}
                  >
                    <button
                      type="button"
                      className="presentation-controls__handle"
                      onPointerDown={beginControlsDrag}
                      aria-label="Move controls"
                      title="Move controls"
                    >
                      <FaGripVertical />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowConfigOverlay((prev) => !prev)}
                      aria-label={showConfigOverlay ? "Hide session outline" : "Show session outline"}
                      title={showConfigOverlay ? "Hide session outline" : "Show session outline"}
                    >
                      {showConfigOverlay ? <FaTimes /> : <FaListUl />}
                    </button>
                    <button
                      type="button"
                      className="presentation-controls__shortcuts-toggle"
                      onClick={toggleShortcutsOverlay}
                      aria-pressed={shortcutsOverlayVisible}
                      aria-label="Show keyboard shortcuts"
                      title="Keyboard shortcuts"
                    >
                      <FaQuestionCircle />
                    </button>
                    {shortcutsOverlayVisible && (
                      <div
                        className="session-shortcuts-panel"
                        role="dialog"
                        aria-label="Session keyboard shortcuts"
                      >
                        <h3>Shortcuts</h3>
                        <ul>
                          {SESSION_SHORTCUTS.map((shortcut) => (
                            <li key={shortcut.keys}>
                              <span className="session-shortcut-keys">{shortcut.keys}</span>
                              <span>{shortcut.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={goToPrevious}
                      disabled={currentIndex === 0}
                      aria-label="Previous session item"
                      title="Previous session item"
                    >
                      <FaFastBackward />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigatePresentation("previous")}
                      disabled={currentIndex === 0}
                      aria-label="Previous item"
                      title="Previous item"
                    >
                      <FaStepBackward />
                    </button>
                    <button
                      type="button"
                      className="presentation-controls__play"
                      onClick={() => setAutoAdvanceEnabled((prev) => !prev)}
                      aria-pressed={autoAdvanceEnabled}
                      aria-label={autoAdvanceEnabled ? "Pause playback" : "Play presentation"}
                      title={autoAdvanceEnabled ? "Pause" : "Play"}
                    >
                      {autoAdvanceEnabled ? <FaPause /> : <FaPlay />}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigatePresentation("next")}
                      disabled={currentIndex === sessionItems.length - 1}
                      aria-label="Next item"
                      title="Next item"
                    >
                      <FaStepForward />
                    </button>
                    <button
                      type="button"
                      onClick={goToNext}
                      disabled={currentIndex === sessionItems.length - 1}
                      aria-label="Next session item"
                      title="Next session item"
                    >
                      <FaFastForward />
                    </button>
                    <button
                      type="button"
                      onClick={toggleBackgroundMusic}
                      aria-pressed={backgroundMusicEnabled}
                      aria-label={backgroundMusicToggleLabel}
                      title={backgroundMusicToggleLabel}
                      className={backgroundMusicEnabled ? "" : "is-muted"}
                      disabled={!isBackgroundMusicAvailable}
                    >
                      {backgroundMusicEnabled ? <FaMusic /> : <FaVolumeMute />}
                    </button>
                    <button
                      type="button"
                      onClick={stopPresentation}
                      aria-label="Cancel presentation"
                      title="Cancel presentation"
                    >
                      <FaTimesCircle />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="presentation-empty">No items in session.</div>
            )}
            <div id="presentation-watermark" className="global-watermark global-watermark--presentation" aria-hidden="true">
              {/* <span>{GLOBAL_WATERMARK_TEXT}</span> */}
            </div>
          </div>
          {showConfigOverlay && (
            <div className="config-overlay">
              <div className="overlay-header">
                <h2>Session Console</h2>
                <div className="overlay-header-actions">
                  <div className="overlay-actions">
                    <button type="button" onClick={handleImportText}>
                      <FaUpload /> Apply JSON
                    </button>
                    <button type="button" onClick={handleExport}>
                      <FaDownload /> Export
                    </button>
                  </div>
                  <button
                    type="button"
                    className="overlay-close"
                    onClick={() => setShowConfigOverlay(false)}
                    aria-label="Close session console"
                  >
                    <FaTimes /> Cancel
                  </button>
                </div>
              </div>
              <div className="overlay-body">
                <div className="overlay-column">
                  <h3>Sequence</h3>
                  <ul className="session-outline">
                    {sessionItems.map((item, index) => (
                      <li
                        key={item.instanceId}
                        className={`outline-item ${index === currentIndex ? "active" : ""}`}
                        draggable
                        onDragStart={(event) => handleDragStart(event, index)}
                        onDragOver={handleDragOver}
                        onDrop={(event) => handleDrop(event, index)}
                        onClick={() => setCurrentIndex(index)}
                      >
                        <span className="drag-icon">
                          <FaGripVertical />
                        </span>
                        <div>
                          <strong>{item.title}</strong>
                          <small>{formatTypeLabel(item.type)}</small>
                        </div>
                        <button
                          type="button"
                          className="remove-item"
                          onClick={(event) => {
                            event.stopPropagation();
                            removeSessionItem(item.instanceId);
                          }}
                        >
                          <FaTimes />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="overlay-column">
                  <h3>Config JSON</h3>
                  <textarea
                    value={jsonDraft}
                    onChange={handleJsonDraftChange}
                    spellCheck={false}
                  />
                  {importError && <div className="error-text">{importError}</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={builderClassName} ref={builderContentRef}>
          <div
            className={`panel-column panel-catalog${panelModes.catalog === "collapsed" ? " is-collapsed" : ""}`}
            style={getPanelStyle("catalog", 0)}
          >
            {panelModes.catalog === "collapsed" ? (
              renderCollapsedHandle("catalog")
            ) : (
              <>
                <section className="catalog-panel">
                  <header>
                    <div className="panel-heading">
                      <h2>
                        <FaPlus /> Content Library
                      </h2>
                      {renderPanelControls("catalog")}
                    </div>
                  </header>
                  <div className={`catalog-top-controls${catalogControlsExpanded ? "" : " is-collapsed"}`}>
                    <button
                      type="button"
                      className="catalog-top-toggle"
                      onClick={() => setCatalogControlsExpanded((prev) => !prev)}
                      aria-label={catalogControlsExpanded ? "Collapse content controls" : "Expand content controls"}
                      aria-expanded={catalogControlsExpanded}
                    >
                      {catalogControlsExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                    <div className="catalog-top-content">
                      <div className="catalog-group-selector">
                        <label htmlFor="catalog-source-select">
                          <span>Content Source</span>
                        </label>
                        <select
                          id="catalog-source-select"
                          value={activeGroup}
                          onChange={(event) => setActiveGroup(event.target.value)}
                        >
                          {sourceGroups.map((group) => (
                            <option key={group.key} value={group.key}>
                              {group.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="catalog-music-selector compact" title="Background music controls">
                        {backgroundMusicSelection.mode === "playlist" && backgroundPlaylistLoading && (
                          <span className="catalog-status">Loading…</span>
                        )}
                        <div className="catalog-music-grid">
                          <div className="compact-field">
                            <select
                              aria-label="Music mode"
                              value={backgroundMusicSelection.mode}
                              onChange={(event) => {
                          const nextMode = event.target.value;
                          setBackgroundMusicSelection((prev) => {
                            if (prev.mode === nextMode) {
                              return prev;
                            }
                            const base = { ...prev, mode: nextMode };
                            if (nextMode === "playlist") {
                              base.playlistId = prev.playlistId || backgroundPlaylistId;
                              base.playlistTitle =
                                getConfiguredPlaylistTitle(base.playlistId) ||
                                prev.playlistTitle;
                            }
                            if (nextMode !== "playlist") {
                              base.playlistVideoId = "";
                              base.playlistLabel = "";
                            }
                            if (nextMode !== "custom") {
                              base.customUrl = prev.customUrl;
                              base.customLabel = prev.customLabel;
                            }
                            return base;
                          });
                          setBackgroundMusicFormError("");
                        }}
                      >
                        <option value="none">No music</option>
                        <option value="playlist">Music playlist</option>
                        <option value="custom">Custom music URL</option>
                      </select>
                    </div>
                    {backgroundMusicSelection.mode === "playlist" && (
                      <>
                        {BACKGROUND_PLAYLISTS.length > 1 && (
                          <div className="compact-field">
                            <select
                              aria-label="Playlist source"
                              value={backgroundPlaylistId}
                              onChange={(event) =>
                                handleBackgroundPlaylistSourceChange(event.target.value)
                              }
                              disabled={backgroundPlaylistLoading}
                            >
                              {BACKGROUND_PLAYLISTS.map((playlist) => (
                                <option key={playlist.id} value={playlist.id}>
                                  {playlist.label || playlist.title}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="compact-field">
                          <select
                            aria-label="Playlist track"
                            value={backgroundMusicSelection.playlistVideoId}
                            onChange={(event) => {
                              const nextVideoId = event.target.value;
                              const selectedTrack = backgroundPlaylistItems.find(
                                (entry) => entry.videoId === nextVideoId,
                              );
                              setBackgroundMusicSelection((prev) => ({
                                ...prev,
                                playlistVideoId: nextVideoId,
                                playlistLabel: selectedTrack?.title || "",
                                playlistTitle:
                                  selectedTrack?.playlistTitle || prev.playlistTitle,
                              }));
                              setBackgroundMusicFormError("");
                            }}
                            disabled={backgroundPlaylistLoading || !backgroundPlaylistItems.length}
                          >
                            <option value="">
                              {backgroundPlaylistItems.length
                                ? "Playlist track…"
                                : backgroundPlaylistLoading
                                ? "Loading…"
                                : "No tracks found"}
                            </option>
                            {backgroundPlaylistItems.map((entry) => (
                              <option key={entry.videoId} value={entry.videoId}>
                                {entry.title || entry.videoId}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className={`compact-button${playlistPreviewActive ? " is-active" : ""}`}
                          onClick={handlePreviewBackgroundSelection}
                          disabled={!backgroundMusicSelection.playlistVideoId}
                          aria-pressed={playlistPreviewActive}
                          title={playlistPreviewActive ? "Stop preview" : "Preview track"}
                        >
                          {playlistPreviewActive ? <FaPause /> : <FaPlay />}
                        </button>
                        {backgroundMusicSelection.playlistVideoId && (
                          <div className="compact-field">
                            <input
                              type="text"
                              value={backgroundMusicSelection.playlistLabel}
                              onChange={(event) =>
                                setBackgroundMusicSelection((prev) => ({
                                  ...prev,
                                  playlistLabel: event.target.value,
                                }))
                              }
                              placeholder="Display label…"
                              aria-label="Playlist label"
                            />
                          </div>
                        )}
                      </>
                    )}
                    {backgroundMusicSelection.mode === "custom" && (
                      <>
                        <div className="compact-field">
                          <input
                            type="url"
                            value={backgroundMusicSelection.customUrl}
                            onChange={(event) => {
                              setBackgroundMusicSelection((prev) => ({
                                ...prev,
                                customUrl: event.target.value,
                              }));
                              setBackgroundMusicFormError("");
                            }}
                            placeholder="YouTube URL…"
                            aria-label="Background music URL"
                          />
                        </div>
                        <div className="compact-field">
                          <input
                            type="text"
                            value={backgroundMusicSelection.customLabel}
                            onChange={(event) => {
                              setBackgroundMusicSelection((prev) => ({
                                ...prev,
                                customLabel: event.target.value,
                              }));
                            }}
                            placeholder="Label (optional)…"
                            aria-label="Background music label"
                          />
                        </div>
                        <button
                          type="button"
                          className={`compact-button${customPreviewActive ? " is-active" : ""}`}
                          onClick={handlePreviewBackgroundSelection}
                          disabled={!backgroundMusicSelection.customUrl}
                          aria-pressed={customPreviewActive}
                          title={customPreviewActive ? "Stop preview" : "Preview custom music"}
                        >
                          {customPreviewActive ? <FaPause /> : <FaPlay />}
                        </button>
                      </>
                    )}
                  </div>
                  {backgroundMusicFormError && (
                    <div className="form-error">{backgroundMusicFormError}</div>
                  )}
                </div>
                {(catalogSearchEnabled ||
                  (activeCatalogKey === "youtube" && youtubeCatalogPlaylists.length > 0) ||
                  isCatalogLoading) && (
                  <div className="catalog-meta-controls">
                    {activeCatalogKey === "youtube" && youtubeCatalogPlaylists.length > 0 && (
                      <div className="catalog-youtube-filter">
                        <label htmlFor="catalog-youtube-playlist">
                          <span>Playlist</span>
                        </label>
                        <select
                          id="catalog-youtube-playlist"
                          value={youtubeCatalogPlaylistFilter}
                          onChange={(event) => setYoutubeCatalogPlaylistFilter(event.target.value)}
                        >
                          <option value="all">All playlists</option>
                          {youtubeCatalogPlaylists.map((playlist) => (
                            <option key={playlist.id} value={playlist.id}>
                              {playlist.title}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {catalogSearchEnabled && (
                      <div className="catalog-search">
                        <input
                          type="search"
                          value={activeCatalogSearch}
                          onChange={(event) =>
                            updateCatalogSearch(activeCatalogKey, event.target.value)
                          }
                          placeholder={`Search ${activeGroupData?.label || "library"}…`}
                          aria-label={`Search ${activeGroupData?.label || "library"}`}
                        />
                        {activeCatalogSearch && (
                          <button
                            type="button"
                            className="catalog-search-clear"
                            onClick={() => updateCatalogSearch(activeCatalogKey, "")}
                            aria-label="Clear search"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    )}
                    {activeGroup === "youtube" && youtubeCatalogPlaylists.length > 0 && (
                      <div className="catalog-filter-note">
                        Showing{" "}
                        {youtubeCatalogPlaylistFilter === "all"
                          ? "all playlists"
                          : youtubeCatalogPlaylists.find(
                              (playlist) => playlist.id === youtubeCatalogPlaylistFilter,
                            )?.title || "selected playlist"}
                      </div>
                    )}
                    {isCatalogLoading && <span className="catalog-status">Loading…</span>}
                  </div>
                )}
              </div>
            </div>

            {catalogPreview && (
              <div
                className="catalog-preview-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="Content preview"
                onClick={() => setCatalogPreview(null)}
              >
                <div className="catalog-preview" role="document" onClick={(event) => event.stopPropagation()}>
                  <div className="catalog-preview-header">
                    <span>{catalogPreview.title}</span>
                    <button type="button" onClick={() => setCatalogPreview(null)} aria-label="Stop preview">
                      <FaTimes />
                    </button>
                  </div>
                  <div
                    className={`catalog-preview-frame${
                      catalogPreview.embedUrl ? "" : catalogPreview.previewItem ? " catalog-preview-frame--content" : ""
                    }`}
                  >
                    {catalogPreview.embedUrl ? (
                      <iframe
                        src={catalogPreview.embedUrl}
                        title={catalogPreview.title}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                      />
                    ) : catalogPreview.previewItem ? (
                      <div className="catalog-preview-render">
                        {renderItemPreview(catalogPreview.previewItem, { stage: false })}
                      </div>
                    ) : (
                      <div className="catalog-preview-fallback">
                        {catalogPreview.fallbackContent || "Preview coming soon."}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="catalog-scroll">
              {activeGroupConfig.group?.description && activeGroup !== "present-catalog" && (
                <p className="catalog-description">{activeGroupConfig.group.description}</p>
              )}
              {activeGroup === "present-catalog" && (
                <PresentationLinkGenerator
                  addToSession={queueSessionItem}
                  onPreviewDeck={(item) => handleToggleCatalogPreview(item, item.embedUrl)}
                />
              )}
              {activeCatalogError && <div className="catalog-error">{activeCatalogError}</div>}
              {activeGroupConfig.group?.key === "custom-external" && (
                <div className="catalog-form">
                  <h3>Add External Media</h3>
                  <div className="catalog-form-grid">
                    <label>
                      Title
                      <input
                        type="text"
                        value={customMediaDraft.title}
                        onChange={(event) => {
                          setCustomMediaDraft((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }));
                          if (customMediaError) {
                            setCustomMediaError("");
                          }
                        }}
                        placeholder="e.g. Livestream or Teaching"
                      />
                    </label>
                    <label>
                      URL
                      <input
                        type="url"
                        value={customMediaDraft.url}
                        onChange={(event) => {
                          setCustomMediaDraft((prev) => ({
                            ...prev,
                            url: event.target.value,
                          }));
                          if (customMediaError) {
                            setCustomMediaError("");
                          }
                        }}
                        placeholder="https://example.com/stream"
                      />
                    </label>
                    <label>
                      Description (optional)
                      <textarea
                        value={customMediaDraft.description}
                        onChange={(event) => {
                          setCustomMediaDraft((prev) => ({
                            ...prev,
                            description: event.target.value,
                          }));
                          if (customMediaError) {
                            setCustomMediaError("");
                          }
                        }}
                        rows={2}
                      />
                    </label>
                    <label>
                      Embed Type
                      <select
                        value={customMediaDraft.mode}
                        onChange={(event) => {
                          setCustomMediaDraft((prev) => ({
                            ...prev,
                            mode: event.target.value,
                          }));
                          if (customMediaError) {
                            setCustomMediaError("");
                          }
                        }}
                      >
                        <option value="auto">Auto detect</option>
                        <option value="webpage">Web Page</option>
                        <option value="video">Video Player</option>
                      </select>
                    </label>
                  </div>
                  {customMediaError && <div className="form-error">{customMediaError}</div>}
                  <div className="catalog-form-actions">
                    <button type="button" onClick={handleCreateCustomMedia}>
                      <FaPlus /> Save to Library
                    </button>
                  </div>
                </div>
              )}

              {isBibleLibrary && (
                <div className="bible-quick-picker">
                  <div className="bible-quick-picker__header">
                    <div>
                      <h3>Quick Bible Builder</h3>
                      <p>Select a version, book, and passage to queue instantly.</p>
                    </div>
                    <div className="bible-picker-actions">
                      <button type="button" onClick={handleAddBibleSelection}>
                        <FaPlus /> Add
                      </button>
                      <button
                        type="button"
                        onClick={handlePreviewBibleSelection}
                        disabled={!currentBibleSelectionItem?.embedUrl}
                      >
                        <FaPlay /> Preview
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenBibleSelectionLink}
                        disabled={!currentBibleSelectionItem?.embedUrl}
                      >
                        <FaExternalLinkAlt /> Link
                      </button>
                    </div>
                  </div>
                  <div className="bible-quick-picker__group">
                    <label htmlFor="bible-version-select">
                      <span>Version</span>
                    </label>
                    <select
                      id="bible-version-select"
                      value={bibleSelection.version}
                      onChange={(event) => updateBibleSelection("version", event.target.value)}
                    >
                      {versionOptionsWithFallback.map((option) => (
                        <option key={`bible-version-${option.value}`} value={option.value}>
                          {option.label || option.value}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bible-quick-picker__group">
                    <label htmlFor="bible-book-select">
                      <span>Book</span>
                    </label>
                    <select
                      id="bible-book-select"
                      value={bibleSelection.book}
                      onChange={(event) => updateBibleSelection("book", event.target.value)}
                    >
                      {bibleBookOptions.map((option) => (
                        <option key={`bible-book-${option.value}`} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="bible-picker-row">
                    <label>
                      Chapter
                      <select
                        value={bibleSelection.chapter}
                        onChange={(event) => updateBibleSelection("chapter", event.target.value)}
                      >
                        {bibleChapterOptions.map((chapter) => (
                          <option key={`chapter-${chapter}`} value={chapter}>
                            {chapter}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Verse(s)
                      <input
                        type="text"
                        value={bibleSelection.verse}
                        onChange={(event) => updateBibleSelection("verse", event.target.value)}
                        placeholder="e.g. 1-5 or 3"
                      />
                    </label>
                    <label>
                      Language
                      <select
                        value={bibleSelection.language}
                        onChange={(event) => updateBibleSelection("language", event.target.value)}
                      >
                        {languageOptionsWithFallback.map((option) => (
                          <option key={`bible-language-${option.value}`} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="bible-picker-row">
                    <label>
                      Lines
                      <input
                        type="number"
                        min="1"
                        value={bibleSelection.lines}
                        onChange={(event) => updateBibleSelection("lines", event.target.value)}
                      />
                    </label>
                    <label>
                      Words / Min
                      <input
                        type="number"
                        min="60"
                        value={bibleSelection.wpm}
                        onChange={(event) => updateBibleSelection("wpm", event.target.value)}
                      />
                    </label>
                    <label>
                      Page
                      <input
                        type="number"
                        min="0"
                        value={bibleSelection.page}
                        onChange={(event) => updateBibleSelection("page", event.target.value)}
                      />
                    </label>
                  </div>
                </div>
              )}

              {currentCatalogItems.length > 0 ? (
                <div
                  className={`catalog-grid${isPresentLibrary ? " catalog-grid--present" : ""}${
                    isBibleLibrary ? " catalog-grid--bible" : ""
                  }`}
                >
                  {currentCatalogItems.map((item) => {
                    if (item.type === "bible-plan") {
                      return (
                        <article key={item.sourceId} className="catalog-plan">
                        <header>
                          <h3>{item.title}</h3>
                          {item.reference && <p className="reference">{item.reference}</p>}
                        </header>
                        <p>{item.description}</p>
                        <div className="plan-meta">
                          <select
                            value={bibleSelection.version}
                            onChange={(event) => updateBibleSelection("version", event.target.value)}
                          >
                            {item.versions.map((version) => (
                              <option key={version} value={version}>
                                {version}
                              </option>
                            ))}
                          </select>
                          <label>
                            Book
                            <select
                              value={bibleSelection.book}
                              onChange={(event) => updateBibleSelection("book", event.target.value)}
                            >
                              {item.books.map((book) => (
                                <option key={book} value={book}>
                                  {book}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Page
                            <input
                              type="number"
                              min="0"
                              value={bibleSelection.page}
                              onChange={(event) => updateBibleSelection("page", event.target.value)}
                            />
                          </label>
                          <label>
                            Lines
                            <input
                              type="number"
                              min="1"
                              value={bibleSelection.lines}
                              onChange={(event) => updateBibleSelection("lines", event.target.value)}
                            />
                          </label>
                          <label>
                            WPM
                            <input
                              type="number"
                              min="60"
                              value={bibleSelection.wpm}
                              onChange={(event) => updateBibleSelection("wpm", event.target.value)}
                            />
                          </label>
                        </div>
                        <button type="button" onClick={handleAddBibleSelection}>
                          <FaPlus /> Add Selection
                        </button>
                      </article>
                    );
                  }

                  const previewEmbedUrl = getCatalogPreviewEmbed(item);
                  const isPreviewingItem = catalogPreview?.sourceId === item.sourceId;
                  const isTemplateTile =
                    activeGroup === "sample-slides" || activeGroup === "layout-templates";

                  if (isTemplateTile) {
                    const previewTemplateItem = {
                      ...item,
                      instanceId: `preview-${item.sourceId}`,
                    };
                    const templateFallbackContent =
                      item.description ||
                      (Array.isArray(item.slides) && item.slides.length
                        ? `Slides:\n${item.slides.join("\n")}`
                        : "");
                    const showTemplatePreview =
                      Boolean(previewEmbedUrl) || Boolean(templateFallbackContent) || Boolean(previewTemplateItem);
                    const templateLink = item.originalUrl || item.embedUrl || previewEmbedUrl || "";
                    return (
                      <article key={item.sourceId} className="catalog-tile">
                        <div className="catalog-tile-body">
                          <h3>{item.title}</h3>
                        </div>
                        <div className="catalog-tile-actions">
                          <button
                            type="button"
                            className="tile-action"
                            onClick={() => queueSessionItem(item)}
                            aria-label="Add template to session"
                          >
                            <FaPlus />
                          </button>
                          {showTemplatePreview && (
                            <button
                              type="button"
                              className={`tile-action${isPreviewingItem ? " is-active" : ""}`}
                              onClick={() =>
                                handleToggleCatalogPreview(
                                  item,
                                  previewEmbedUrl,
                                  templateFallbackContent,
                                  previewTemplateItem,
                                )
                              }
                              aria-label={isPreviewingItem ? "Hide preview" : "Preview template"}
                              aria-pressed={isPreviewingItem}
                            >
                              <FaPlay />
                            </button>
                          )}
                          {templateLink && (
                            <a
                              href={templateLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tile-action tile-action-link"
                              aria-label="Open template source"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          )}
                        </div>
                      </article>
                    );
                  }

                  const safeExternalLink = item.originalUrl || item.embedUrl || previewEmbedUrl || "";
                  const showPreviewButton = Boolean(previewEmbedUrl);

                  if (isBibleLibrary) {
                    const snippet = item.content || item.description || "";
                    const displayReference = item.reference || "";
                    return (
                      <article key={item.sourceId} className="bible-tile">
                        <header>
                          <h3>{item.title}</h3>
                          {displayReference && <span>{displayReference}</span>}
                        </header>
                        {snippet && <p>{snippet}</p>}
                        <div className="bible-tile-actions">
                          <button
                            type="button"
                            className="tile-action"
                            onClick={() => queueSessionItem(item)}
                            aria-label="Add passage to session"
                          >
                            <FaPlus />
                          </button>
                          {showPreviewButton && (
                            <button
                              type="button"
                              className={`tile-action${isPreviewingItem ? " is-active" : ""}`}
                              onClick={() => handleToggleCatalogPreview(item, previewEmbedUrl)}
                              aria-label={isPreviewingItem ? "Hide preview" : "Preview passage"}
                              aria-pressed={isPreviewingItem}
                            >
                              <FaPlay />
                            </button>
                          )}
                          {safeExternalLink && (
                            <a
                              href={safeExternalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tile-action tile-action-link"
                              aria-label="Open Bible view"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          )}
                        </div>
                      </article>
                    );
                  }

                  if (isPresentLibrary) {
                    const topicLabel = describePresentTopic(item.presentTopic || "featured");
                    const deckSettings = extractPresentDeckSettings(item.embedUrl || "");
                    const presentFileLabel = item.presentFile
                      ? item.presentFile.replace(/^\//, "")
                      : "Default Deck";
                    return (
                      <article key={item.sourceId} className="present-tile">
                        <div className="present-tile-body">
                          <div className="present-tile-heading">
                            <div>
                              <h3>{item.title}</h3>
                              <p className="present-tile-path">{presentFileLabel}</p>
                            </div>
                            <span className="present-topic-badge">{topicLabel}</span>
                          </div>
                          {item.description && <p>{item.description}</p>}
                          <div className="present-meta-grid">
                            <div>
                              <span className="meta-label">Mode</span>
                              <span className="meta-value">{deckSettings.mode}</span>
                            </div>
                            <div>
                              <span className="meta-label">Tiles</span>
                              <span className="meta-value">{deckSettings.tiles}</span>
                            </div>
                            <div>
                              <span className="meta-label">Controls</span>
                              <span className="meta-value">{deckSettings.controls}</span>
                            </div>
                            <div>
                              <span className="meta-label">Descriptions</span>
                              <span className="meta-value">{deckSettings.descriptions}</span>
                            </div>
                          </div>
                        </div>
                        <div className="catalog-tile-actions">
                          <button
                            type="button"
                            className="tile-action"
                            onClick={() => queueSessionItem(item)}
                            aria-label="Add deck to session"
                          >
                            <FaPlus />
                          </button>
                          {showPreviewButton && (
                            <button
                              type="button"
                              className={`tile-action${isPreviewingItem ? " is-active" : ""}`}
                              onClick={() => handleToggleCatalogPreview(item, previewEmbedUrl)}
                              aria-label={isPreviewingItem ? "Hide preview" : "Preview deck"}
                              aria-pressed={isPreviewingItem}
                            >
                              <FaPlay />
                            </button>
                          )}
                          {safeExternalLink && (
                            <a
                              href={safeExternalLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tile-action tile-action-link"
                              aria-label="Open deck source"
                            >
                              <FaExternalLinkAlt />
                            </a>
                          )}
                        </div>
                      </article>
                    );
                  }

                  const hasDetails =
                    Boolean(item.reference) ||
                    (item.songs && item.songs.length) ||
                    (item.slides && item.slides.length) ||
                    (item.content && !item.embedUrl);
                  const showDetails = hasDetails && catalogControlsExpanded;
                  const detailChips = [];
                  if (showDetails && item.songs && item.songs.length) {
                    const previewSongs = item.songs.slice(0, 2).join(", ");
                    const remaining = item.songs.length - 2;
                    detailChips.push(
                      `Songs: ${previewSongs}${remaining > 0 ? ` +${remaining}` : ""}`,
                    );
                  }
                  if (showDetails && item.slides && item.slides.length) {
                    const previewSlides = item.slides.slice(0, 2).join(", ");
                    const remaining = item.slides.length - 2;
                    detailChips.push(
                      `Slides: ${previewSlides}${remaining > 0 ? ` +${remaining}` : ""}`,
                    );
                  }
                  if (showDetails && item.content && !item.embedUrl) {
                    detailChips.push("Notes available");
                  }

                  return (
                    <article key={item.sourceId} className="catalog-tile catalog-tile--content">
                      {item.thumbnail && (
                        <div className="catalog-tile-thumb">
                          <img src={item.thumbnail} alt={`${item.title} thumbnail`} />
                        </div>
                      )}
                      <div className="catalog-tile-body">
                        <div className="catalog-tile-title">
                          <h3>{item.title}</h3>
                          {item.reference && (
                            <span className="catalog-tile-reference">{item.reference}</span>
                          )}
                        </div>
                        {detailChips.length > 0 && (
                          <div className="catalog-tile-meta">
                            {detailChips.map((chip, index) => (
                              <span key={`${item.sourceId}-chip-${index}`} className="catalog-chip">
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="catalog-tile-actions">
                        <button
                          type="button"
                          className="tile-action"
                          onClick={() => queueSessionItem(item)}
                          aria-label="Add item to session"
                        >
                          <FaPlus />
                        </button>
                        {showPreviewButton && (
                          <button
                            type="button"
                            className={`tile-action${isPreviewingItem ? " is-active" : ""}`}
                            onClick={() => handleToggleCatalogPreview(item, previewEmbedUrl)}
                            aria-label={isPreviewingItem ? "Hide preview" : "Preview content"}
                            aria-pressed={isPreviewingItem}
                          >
                            <FaPlay />
                          </button>
                        )}
                        {safeExternalLink && (
                          <a
                            href={safeExternalLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tile-action tile-action-link"
                            aria-label="Open original source"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        )}
                        {activeGroupConfig.group?.key === "custom-external" && (
                          <button
                            type="button"
                            className="tile-action tile-action-danger"
                            onClick={() => removeCustomMediaItem(item.sourceId)}
                            aria-label="Remove saved item"
                          >
                            <FaTimes />
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
                </div>
              ) : (
                <div className="catalog-message">
                  {activeCatalogSearch
                    ? `No matches found for “${activeCatalogSearch}”.`
                    : activeGroupConfig.group?.key === "custom-external"
                    ? "Add a URL above to save it to your library."
                    : "No items available for this library yet."}
                </div>
              )}
            </div>
          </section>
                {isPanelVisible("catalog") && isPanelVisible("session") && (
                  <button
                    type="button"
                    className="panel-resizer"
                    aria-label="Resize Content Library and Session Outline panels"
                    onPointerDown={(event) => startResize(0, event)}
                  />
                )}
              </>
            )}
          </div>
        <div
          className={`panel-column panel-session${panelModes.session === "collapsed" ? " is-collapsed" : ""}`}
          style={getPanelStyle("session", 1)}
        >
          {panelModes.session === "collapsed" ? (
            renderCollapsedHandle("session")
          ) : (
            <>
              <section className="session-panel">
            <header>
              <div className="panel-heading">
                <h2>
                  <FaListUl /> Session Outline
                </h2>
                {renderPanelControls("session")}
              </div>
            </header>
            <div className="session-stack" ref={sessionStackRef}>
              <div
                className="session-scroll"
                style={{ flex: `${outlineRatio} 1 0%` }}
              >
                {sessionItems.length ? (
                  <ul className="session-outline">
                    {sessionItems.map((item, index) => (
                      <li
                        key={item.instanceId}
                        className={selectedItemId === item.instanceId ? "selected" : ""}
                        draggable
                        onDragStart={(event) => handleDragStart(event, index)}
                        onDragOver={handleDragOver}
                        onDrop={(event) => handleDrop(event, index)}
                        onClick={() => setSelectedItemId(item.instanceId)}
                      >
                        <span className="drag-icon">
                          <FaGripVertical />
                        </span>
                        <div className="outline-text">
                          <strong>{item.title}</strong>
                          <small>{formatTypeLabel(item.type)}</small>
                        </div>
                        <div className="outline-actions">
                          <button
                            type="button"
                            onClick={() => setCurrentIndex(index)}
                            aria-label="Preview item"
                            title="Preview item"
                          >
                            <FaPlay />
                          </button>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            className="outline-duration-input"
                            value={sanitizeDurationMinutes(item.durationMinutes)}
                            onClick={(event) => event.stopPropagation()}
                            onFocus={(event) => event.stopPropagation()}
                            onChange={(event) => handleDurationChange(item.instanceId, event.target.value)}
                            aria-label="Item duration (minutes)"
                            title="Item duration (minutes)"
                          />
                          <button
                            type="button"
                            onClick={() => removeSessionItem(item.instanceId)}
                            aria-label="Remove item"
                            title="Remove item"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="empty-outline">
                    <p>Add worship workshops, playlists, Bible passages, and slides to build your session.</p>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="outline-resizer"
                aria-label="Resize session outline editor space"
                onPointerDown={startOutlineResize}
              />
              <div
                className="session-editor-stack"
                style={{ flex: `${Math.max(0.3, 1 - outlineRatio)} 1 0%` }}
              >
                {selectedItem &&
                  [
                    "worship",
                    "slides",
                    "moment",
                    "presentationRemote",
                    "worshipRemote",
                    "externalPage",
                    "externalVideo",
                    "youtube",
                    "youtubeVideo",
                    "bibleRemote",
                  ].includes(selectedItem.type) && (
                    <div className="editor-panel">
                      <h3>
                        <FaEdit /> Customize Slide
                      </h3>
                      <label htmlFor="editor-title">Display Title</label>
                      <input
                        id="editor-title"
                        type="text"
                        value={selectedItem.title}
                        onChange={(event) =>
                          updateSessionItem(selectedItem.instanceId, {
                            title: event.target.value,
                          })
                        }
                      />
                      <div className="background-music-editor">
                        <div className="background-music-editor__header">
                          <span>
                            <FaMusic /> Background Music
                          </span>
                          <small>Optional track plays during live presentation</small>
                        </div>
                        <label>
                          <span>Mode</span>
                          <select
                            value={selectedItemBackgroundMode}
                            onChange={(event) =>
                              handleSelectedItemBackgroundModeChange(event.target.value)
                            }
                          >
                            <option value="none">None</option>
                            <option value="playlist">Playlist track</option>
                            <option value="custom">Custom YouTube URL</option>
                          </select>
                        </label>
                        {selectedItemBackgroundMode === "playlist" && (
                          <label>
                            <span>Playlist Track</span>
                            <select
                              value={selectedItemBackgroundMusic?.videoId || ""}
                              onChange={(event) =>
                                handleSelectedItemBackgroundPlaylistChange(event.target.value)
                              }
                              disabled={!backgroundPlaylistItems.length}
                            >
                              <option value="">
                                {backgroundPlaylistItems.length
                                  ? "Select a track…"
                                  : "No tracks available"}
                              </option>
                              {backgroundPlaylistItems.map((entry) => (
                                <option key={entry.videoId} value={entry.videoId}>
                                  {entry.title || entry.videoId}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        {selectedItemBackgroundMode === "custom" && (
                          <>
                            <label>
                              <span>YouTube URL</span>
                              <input
                                type="url"
                                value={
                                  selectedItemBackgroundMusic?.mode === "custom"
                                    ? selectedItemBackgroundMusic.originalUrl || ""
                                    : ""
                                }
                                onChange={(event) =>
                                  handleSelectedItemBackgroundCustomUrlChange(event.target.value)
                                }
                                placeholder="https://www.youtube.com/watch?v=…"
                              />
                            </label>
                            <label>
                              <span>Label (optional)</span>
                              <input
                                type="text"
                                value={
                                  selectedItemBackgroundMusic?.mode === "custom"
                                    ? selectedItemBackgroundMusic.title || ""
                                    : ""
                                }
                                onChange={(event) =>
                                  handleSelectedItemBackgroundCustomLabelChange(event.target.value)
                                }
                                placeholder="How should this track appear?"
                              />
                            </label>
                          </>
                        )}
                        {selectedItemBackgroundError && (
                          <div className="form-error">{selectedItemBackgroundError}</div>
                        )}
                        {selectedItemBackgroundMode !== "none" && (
                          <p className="editor-hint">
                            Music is hidden from the live slide but audible for the audience.
                          </p>
                        )}
                      </div>
                      {selectedItem.type === "worship" && (
                        <>
                          <label htmlFor="editor-songs">Songs (one per line)</label>
                          <textarea
                            id="editor-songs"
                            value={(selectedItem.songs || []).join("\n")}
                            onChange={(event) => {
                              const songs = event.target.value
                                .split("\n")
                                .map((entry) => entry.trim())
                                .filter(Boolean);
                              updateSessionItem(selectedItem.instanceId, {
                                songs,
                              });
                            }}
                          />
                          <p className="editor-hint">Update the workshop flow by adjusting the song order.</p>
                        </>
                      )}
                      {selectedItem.type === "slides" && (
                        <>
                          <label htmlFor="editor-slides">Slides (one per line)</label>
                          <textarea
                            id="editor-slides"
                            value={(selectedItem.slides || []).join("\n")}
                            onChange={(event) => {
                              const slides = event.target.value
                                .split("\n")
                                .map((entry) => entry.trim())
                                .filter(Boolean);
                              updateSessionItem(selectedItem.instanceId, {
                                slides,
                              });
                            }}
                          />
                          <p className="editor-hint">Each line will appear as a separate slide in the deck.</p>
                          <label htmlFor="editor-slide-layout">Layout</label>
                          <select
                            id="editor-slide-layout"
                            value={selectedItem.layout || DEFAULT_SLIDE_LAYOUT}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                layout: event.target.value,
                              })
                            }
                          >
                            {SLIDE_LAYOUT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-slide-theme">Theme</label>
                          <select
                            id="editor-slide-theme"
                            value={selectedItem.slideTheme || DEFAULT_SLIDE_THEME}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                slideTheme: event.target.value,
                              })
                            }
                          >
                            {SLIDE_THEME_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-slide-accent">Accent Color</label>
                          <select
                            id="editor-slide-accent"
                            value={selectedItem.slideAccent || DEFAULT_SLIDE_ACCENT}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                slideAccent: event.target.value,
                              })
                            }
                          >
                            {SLIDE_ACCENT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-slide-gradient">Gradient Overlay</label>
                          <select
                            id="editor-slide-gradient"
                            value={
                              typeof selectedItem.slideGradient === "string"
                                ? selectedItem.slideGradient
                                : DEFAULT_SLIDE_GRADIENT
                            }
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                slideGradient: event.target.value,
                              })
                            }
                          >
                            {SLIDE_GRADIENT_OPTIONS.map((option) => (
                              <option key={option.value || "none"} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="editor-hint">
                            Themes, accents, and gradients instantly restyle the live slide canvas.
                          </p>
                        </>
                      )}
                      {selectedItem.type === "presentationRemote" && presentationRemoteSettings && (
                        <>
                          <label htmlFor="editor-present-topic">Topic</label>
                          <input
                            id="editor-present-topic"
                            type="text"
                            list="present-topic-options"
                            value={presentationFileParts.topic}
                            onChange={(event) =>
                              handlePresentationFilePartsChange({
                                topic: event.target.value,
                              })
                            }
                            placeholder="e.g. bible"
                          />
                          <datalist id="present-topic-options">
                            {presentTopicOptions.map((topicOption) => (
                              <option key={topicOption} value={topicOption} />
                            ))}
                          </datalist>
                          <label htmlFor="editor-present-file">File</label>
                          <input
                            id="editor-present-file"
                            type="text"
                            list="present-file-options"
                            value={presentationFileParts.file}
                            onChange={(event) =>
                              handlePresentationFilePartsChange({
                                file: event.target.value,
                              })
                            }
                            placeholder="e.g. John"
                          />
                          <datalist id="present-file-options">
                            {presentFilesForTopic.map((fileOption) => (
                              <option key={fileOption} value={fileOption} />
                            ))}
                          </datalist>
                          <label htmlFor="editor-present-slides">Slide Range (optional)</label>
                          <input
                            id="editor-present-slides"
                            type="text"
                            value={presentationRemoteSettings.slideRange || ""}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                slideRange: event.target.value.trim(),
                              })
                            }
                            placeholder="e.g. 2-5 or 3"
                          />
                          <label
                            htmlFor="editor-present-disable-loop"
                            className="toggle-field"
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                          >
                            <input
                              id="editor-present-disable-loop"
                              type="checkbox"
                              checked={Boolean(presentationRemoteSettings.disableLoop)}
                              onChange={(event) =>
                                handlePresentationRemoteSettingsChange({
                                  disableLoop: event.target.checked,
                                })
                              }
                            />
                            Disable loop
                          </label>
                          <label htmlFor="editor-present-mode">Playback Mode</label>
                          <input
                            id="editor-present-mode"
                            type="text"
                            list="presentation-mode-options"
                            value={presentationRemoteSettings.mode}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                mode: event.target.value.trim(),
                              })
                            }
                          />
                          <datalist id="presentation-mode-options">
                            <option value="play" />
                            <option value="play-topic" />
                            <option value="presenter" />
                          </datalist>
                          <label htmlFor="editor-present-tiles">Show Tiles</label>
                          <select
                            id="editor-present-tiles"
                            value={presentationRemoteSettings.tiles === "1" ? "1" : "0"}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                tiles: event.target.value,
                              })
                            }
                          >
                            <option value="1">Show tiles</option>
                            <option value="0">Hide tiles</option>
                          </select>
                          <label htmlFor="editor-present-controls">Show Controls</label>
                          <select
                            id="editor-present-controls"
                            value={presentationRemoteSettings.controls === "1" ? "1" : "0"}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                controls: event.target.value,
                              })
                            }
                          >
                            <option value="1">Show controls</option>
                            <option value="0">Hide controls</option>
                          </select>
                          <label htmlFor="editor-present-descriptions">Descriptions</label>
                          <select
                            id="editor-present-descriptions"
                            value={presentationRemoteSettings.descriptions === "1" ? "1" : "0"}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                descriptions: event.target.value,
                              })
                            }
                          >
                            <option value="1">Show descriptions</option>
                            <option value="0">Hide descriptions</option>
                          </select>
                          <label htmlFor="editor-present-gif-delay">GIF Delay (ms)</label>
                          <input
                            id="editor-present-gif-delay"
                            type="number"
                            min="500"
                            step="100"
                            value={Number(presentationRemoteSettings.gifDelay) || 0}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                gifDelay: event.target.value,
                              })
                            }
                          />
                          <label htmlFor="editor-present-icon-size">Icon Size</label>
                          <input
                            id="editor-present-icon-size"
                            type="number"
                            min="12"
                            step="1"
                            value={Number(presentationRemoteSettings.iconSize) || 0}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                iconSize: event.target.value,
                              })
                            }
                          />
                          <label htmlFor="editor-present-master">Master Color</label>
                          <input
                            id="editor-present-master"
                            type="color"
                            value={presentationRemoteSettings.master}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                master: event.target.value,
                              })
                            }
                          />
                          <label htmlFor="editor-present-subview">Visual Type</label>
                          <input
                            id="editor-present-subview"
                            type="text"
                            value={presentationRemoteSettings.subView}
                            onChange={(event) =>
                              handlePresentationRemoteSettingsChange({
                                subView: event.target.value.trim(),
                              })
                            }
                          />
                          <p className="editor-hint">
                            Update playback, layout, and styling parameters without leaving the session
                            builder.
                          </p>
                        </>
                      )}
                      {selectedItem.type === "worshipRemote" && selectedWorshipRemoteSettings && (
                        <>
                          <label htmlFor="editor-worship-slug">Song Identifier</label>
                          <input
                            id="editor-worship-slug"
                            type="text"
                            value={selectedWorshipRemoteSettings.slug}
                            onChange={(event) =>
                              handleWorshipRemoteSettingsChange({
                                slug: event.target.value.trim(),
                              })
                            }
                            placeholder="worship-song-slug"
                          />
                          <label htmlFor="editor-worship-domain">Catalog Domain</label>
                          <input
                            id="editor-worship-domain"
                            type="text"
                            value={selectedWorshipRemoteSettings.domain}
                            onChange={(event) =>
                              handleWorshipRemoteSettingsChange({
                                domain: event.target.value.trim(),
                              })
                            }
                            placeholder="gcc-catalog.blogspot.com"
                          />
                          <label htmlFor="editor-worship-theme">Theme</label>
                          <input
                            id="editor-worship-theme"
                            type="text"
                            value={selectedWorshipRemoteSettings.theme}
                            onChange={(event) =>
                              handleWorshipRemoteSettingsChange({
                                theme: event.target.value.trim(),
                              })
                            }
                            placeholder="classic"
                          />
                          <label htmlFor="editor-worship-display">Display Mode</label>
                          <input
                            id="editor-worship-display"
                            type="text"
                            value={selectedWorshipRemoteSettings.display}
                            onChange={(event) =>
                              handleWorshipRemoteSettingsChange({
                                display: event.target.value.trim(),
                              })
                            }
                            placeholder="slide"
                          />
                          <label htmlFor="editor-worship-view">Button Layout</label>
                          <input
                            id="editor-worship-view"
                            type="text"
                            value={selectedWorshipRemoteSettings.songViewMode}
                            onChange={(event) =>
                              handleWorshipRemoteSettingsChange({
                                songViewMode: event.target.value.trim(),
                              })
                            }
                            placeholder="buttons"
                          />
                          <p className="editor-hint">
                            Adjust the worship embed parameters to load alternate catalogs or visual
                            styles.
                          </p>
                        </>
                      )}
                      {selectedItem.type === "bibleRemote" && selectedBibleRemoteSettings && (
                        <>
                          <label htmlFor="editor-bible-book">Book</label>
                          <select
                            id="editor-bible-book"
                            value={selectedBibleRemoteSettings.book}
                            onChange={(event) => {
                              const nextBook = event.target.value;
                              const bookIndex = parseInt(nextBook, 10);
                              const chapterLimit =
                                BIBLE_BOOKS[
                                  Number.isNaN(bookIndex)
                                    ? 0
                                    : Math.min(Math.max(bookIndex, 0), BIBLE_BOOKS.length - 1)
                                ]?.chapters || 1;
                              const currentChapter = parseInt(
                                selectedBibleRemoteSettings.chapter,
                                10,
                              );
                              const nextChapter = Number.isNaN(currentChapter)
                                ? "1"
                                : String(Math.min(currentChapter, chapterLimit));
                              handleBibleRemoteSettingsChange({
                                book: nextBook,
                                chapter: nextChapter,
                              });
                            }}
                          >
                            {bibleBookOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-bible-chapter">Chapter</label>
                          <select
                            id="editor-bible-chapter"
                            value={selectedBibleRemoteSettings.chapter}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                chapter: event.target.value,
                              })
                            }
                          >
                            {bibleRemoteChapterOptions.map((chapter) => (
                              <option key={chapter} value={chapter}>
                                {chapter}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-bible-verse">Verse (optional)</label>
                          <input
                            id="editor-bible-verse"
                            type="text"
                            value={selectedBibleRemoteSettings.verse}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                verse: event.target.value.trim(),
                              })
                            }
                            placeholder="e.g. 1 or 1-3"
                          />
                          <label htmlFor="editor-bible-version">Version</label>
                          <select
                            id="editor-bible-version"
                            value={selectedBibleRemoteSettings.version}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                version: event.target.value,
                              })
                            }
                          >
                            {versionOptionsWithFallback.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-bible-language">Language</label>
                          <select
                            id="editor-bible-language"
                            value={selectedBibleRemoteSettings.language}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                language: event.target.value,
                              })
                            }
                          >
                            {languageOptionsWithFallback.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <label htmlFor="editor-bible-page">Page</label>
                          <input
                            id="editor-bible-page"
                            type="number"
                            min="0"
                            value={selectedBibleRemoteSettings.page}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                page: event.target.value,
                              })
                            }
                          />
                          <label htmlFor="editor-bible-lines">Lines</label>
                          <input
                            id="editor-bible-lines"
                            type="number"
                            min="1"
                            value={selectedBibleRemoteSettings.lines}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                lines: event.target.value,
                              })
                            }
                          />
                          <label htmlFor="editor-bible-wpm">WPM</label>
                          <input
                            id="editor-bible-wpm"
                            type="number"
                            min="60"
                            value={selectedBibleRemoteSettings.wpm}
                            onChange={(event) =>
                              handleBibleRemoteSettingsChange({
                                wpm: event.target.value,
                              })
                            }
                          />
                          <p className="editor-hint">
                            Tune the remote Bible view just like the selector—book, chapter, version,
                            and reading pace are all in your control.
                          </p>
                        </>
                      )}
                      {["youtube", "youtubeVideo", "externalVideo"].includes(selectedItem.type) && (
                        <>
                          <label htmlFor="editor-video-embed">Embed URL</label>
                          <input
                            id="editor-video-embed"
                            type="text"
                            value={selectedItem.embedUrl || ""}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                embedUrl: event.target.value,
                              })
                            }
                            placeholder="https://www.youtube.com/embed/…"
                          />
                        </>
                      )}
                      {(selectedItem.type === "externalPage" ||
                        selectedItem.type === "externalVideo") && (
                        <>
                          <label htmlFor="editor-external-type">Embed Type</label>
                          <select
                            id="editor-external-type"
                            value={selectedItem.type}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                type: event.target.value,
                              })
                            }
                          >
                            <option value="externalPage">Web Page</option>
                            <option value="externalVideo">Video</option>
                          </select>
                        </>
                      )}
                      {typeof selectedItem.reference === "string" && (
                        <>
                          <label htmlFor="editor-reference">Reference</label>
                          <input
                            id="editor-reference"
                            type="text"
                            value={selectedItem.reference}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                reference: event.target.value,
                              })
                            }
                          />
                        </>
                      )}
                      {selectedItem.originalUrl &&
                        !["externalPage", "presentationRemote", "bibleRemote"].includes(
                          selectedItem.type,
                        ) && (
                          <>
                            <label htmlFor="editor-original-url">Source URL</label>
                            <input
                              id="editor-original-url"
                              type="text"
                              value={selectedItem.originalUrl}
                              onChange={(event) =>
                                updateSessionItem(selectedItem.instanceId, {
                                  originalUrl: event.target.value,
                                })
                              }
                              placeholder="https://example.com/source"
                            />
                          </>
                        )}
                      {selectedItem.type === "externalPage" && (
                        <>
                          <label htmlFor="editor-website-url">Page URL</label>
                          <input
                            id="editor-website-url"
                            type="url"
                            value={selectedWebsiteValues.baseUrl}
                            onChange={(event) => {
                              const nextBase = event.target.value;
                              const combined = buildWebsiteUrlFromParts(
                                nextBase,
                                selectedWebsiteValues.params
                              );
                              const fallbackUrl = normalizeWebsiteUrl(nextBase);
                              updateSessionItem(selectedItem.instanceId, {
                                embedUrl: combined,
                                originalUrl: combined || fallbackUrl,
                              });
                            }}
                            placeholder="https://example.com/page"
                          />
                          <label htmlFor="editor-website-params">URL Parameters (optional)</label>
                          <input
                            id="editor-website-params"
                            type="text"
                            value={selectedWebsiteValues.params}
                            onChange={(event) => {
                              const nextParams = event.target.value;
                              const combined = buildWebsiteUrlFromParts(
                                selectedWebsiteValues.baseUrl,
                                nextParams
                              );
                              const fallbackUrl = selectedWebsiteValues.baseUrl
                                ? normalizeWebsiteUrl(selectedWebsiteValues.baseUrl)
                                : "";
                              updateSessionItem(selectedItem.instanceId, {
                                embedUrl: combined,
                                originalUrl: combined || fallbackUrl,
                              });
                            }}
                            placeholder="e.g. lang=ta&page=resources"
                          />
                          <p className="editor-hint">
                            Add query parameters to jump directly to specific pages or localized
                            content.
                          </p>
                        </>
                      )}
                      {[
                        "moment",
                        "presentationRemote",
                        "externalPage",
                        "externalVideo",
                        "youtube",
                        "youtubeVideo",
                        "worshipRemote",
                        "bibleRemote",
                      ].includes(selectedItem.type) && (
                        <>
                          <label htmlFor="editor-description">Description</label>
                          <textarea
                            id="editor-description"
                            value={selectedItem.description || ""}
                            onChange={(event) =>
                              updateSessionItem(selectedItem.instanceId, {
                                description: event.target.value,
                              })
                            }
                          />
                          <p className="editor-hint">
                            Fine-tune the supporting copy shown in the outline and presenter view.
                          </p>
                        </>
                      )}
                      {selectedItem.type === "worshipRemote" && (
                        <p className="editor-hint">
                          This catalog song will open the linked worship slide in the presentation view.
                        </p>
                      )}
                      {(selectedItem.type === "externalPage" || selectedItem.type === "externalVideo") && (
                        <p className="editor-hint">
                          Update the title or description to match how you will introduce this external media.
                        </p>
                      )}
                    </div>
                  )}
                {selectedItemId && (
                  <div className="notes-panel">
                    <h3>
                      <FaFileAlt /> Item Notes
                    </h3>
                    <textarea
                      value={selectedItem?.notes || ""}
                      onChange={(event) => updateSessionItemNotes(selectedItemId, event.target.value)}
                      placeholder="Add speaking cues, transitions, or reminders."
                    />
                  </div>
                )}
                {!selectedItem && !selectedItemId && (
                  <div className="editor-placeholder">
                    <p>Select any session item to customize its slides or jot down presenter notes.</p>
                  </div>
                )}
              </div>
            </div>
              </section>
              {isPanelVisible("session") && isPanelVisible("preview") && (
                <button
                  type="button"
                  className="panel-resizer"
                  aria-label="Resize Session Outline and Live Preview panels"
                  onPointerDown={(event) => startResize(1, event)}
                />
              )}
            </>
          )}
        </div>
      <div
        className={`panel-column panel-preview${panelModes.preview === "collapsed" ? " is-collapsed" : ""}`}
        style={getPanelStyle("preview", 2)}
      >
        {panelModes.preview === "collapsed" ? (
          renderCollapsedHandle("preview")
        ) : (
          <>
            <section className="preview-panel">
            <header>
              <div className="panel-heading">
                <h2>
                  <FaPlay /> Preview
                </h2>
                {renderPanelControls("preview")}
              </div>
              <div className="preview-controls">
                <button type="button" onClick={goToPrevious} disabled={currentIndex === 0}>
                  <FaStepBackward />
                </button>
                <span className="preview-counter">
                  {sessionItems.length ? `${currentIndex + 1} / ${sessionItems.length}` : "0 / 0"}
                </span>
                <button
                  type="button"
                  onClick={goToNext}
                  disabled={currentIndex >= sessionItems.length - 1}
                >
                  <FaStepForward />
                </button>
                <button
                  type="button"
                  className={`preview-auto-advance-toggle${autoAdvanceEnabled ? " is-active" : ""}`}
                  onClick={() => setAutoAdvanceEnabled((prev) => !prev)}
                  title={autoAdvanceEnabled ? "Pause auto-advance" : "Start auto-advance"}
                  aria-pressed={autoAdvanceEnabled}
                >
                  {autoAdvanceEnabled ? <FaPause /> : <FaPlay />}
                </button>
                <input
                  type="number"
                  min="5"
                  className="preview-auto-advance-seconds"
                  value={autoAdvanceSeconds}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    setAutoAdvanceSeconds(Number.isFinite(nextValue) && nextValue > 0 ? nextValue : 3);
                  }}
                  aria-label="Auto-advance seconds"
                  title="Auto-advance interval (seconds)"
                />
                <button
                  type="button"
                  className={`preview-audio-toggle${previewAudioMuted ? " is-muted" : ""}`}
                  onClick={() => setPreviewAudioMuted((prev) => !prev)}
                  title={previewAudioMuted ? "Unmute preview audio" : "Mute preview audio"}
                  aria-pressed={previewAudioMuted}
                >
                  {previewAudioMuted ? <FaVolumeMute /> : <FaVolumeUp />}
                </button>
              </div>
            </header>
            <div className="preview-stage" ref={previewStageRef}>
              {shouldMirrorPreviewClicks && (
                <>
                  <div
                    className="preview-interaction-overlay"
                    onPointerDown={handlePreviewPointerDown}
                    onPointerUp={handlePreviewPointerUp}
                    onClick={handlePreviewClick}
                    onDoubleClick={handlePreviewDoubleClick}
                    onWheel={handlePreviewWheel}
                  />
                  <div className="preview-interaction-hint">
                    Connected to presenter — clicks control live output
                  </div>
                </>
              )}
              {sessionItems.length && previewItem ? (
                renderItemPreview(previewItem, { onEmbedRef: handlePreviewEmbedRef })
              ) : (
                <div className="preview-empty">Select content from the library to see it here.</div>
              )}
              {previewItem?.notes && (
                <div className="speaker-notes">
                  <h4>Speaker Notes</h4>
                  <p>{renderMultilineText(previewItem.notes)}</p>
                </div>
              )}
              {previewBackgroundMusicEmbed && (
                <div className="background-music-player" aria-hidden="true">
                  <iframe
                    ref={previewMusicRef}
                    src={previewBackgroundMusicEmbed}
                    title="Preview background music"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    onLoad={handleMusicIframeLoad}
                  />
                </div>
              )}
              <div id="preview-watermark" className="global-watermark global-watermark--session" aria-hidden="true">
                <span>{GLOBAL_WATERMARK_TEXT}</span>
              </div>
            </div>
            </section>
          </>
        )}
      </div>
    </div>
  )}
  </div>
  );
}

export default WorshipBuilder;
